import { useState, useMemo, useEffect, useRef, type ReactNode } from 'react';
import {
  Zap, Droplets, Thermometer, Gauge, Wind, CheckCircle2, AlertCircle,
  Info, RotateCcw, FlaskConical, Settings, Beaker, Activity,
  ShieldCheck, Flame, Layers, FileText,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';

/* =========================================================================
   ELECTROCHLORINATION PLANT - SPECIFICATION & SIZING TOOL
   On-site sodium hypochlorite generation from seawater via electrolysis.
   Typical applications: seawater intake biofouling control (power stations,
   desalination plants, offshore platforms), ballast-water disinfection,
   cooling-tower make-up disinfection.

   Engineering basis (public-domain):
     - Faraday's law of electrolysis for Cl2 yield
     - Bommaraju & Orosco "Brine Electrolysis", ECS
     - Bennett (1980) "Electrodes for the generation of Cl2 / O2 from seawater"
     - De Nora / Evoqua / Severn Trent commercial envelopes
     - IEC 60079 / DSEAR for H2 vent safety (LEL 4 % v/v in air)
   ========================================================================= */

// ---------------------------------------------------------------------------
// Standard seawater property helpers
// ---------------------------------------------------------------------------

/** Seawater density (kg/m^3) - simplified El-Dessouky & Ettouny correlation. */
function swDensity(salinity_ppt: number, T_C: number): number {
  const dT = T_C - 20;
  return 1000 + 0.808 * salinity_ppt - 0.0708 * dT - 0.003 * dT * dT + 0.0005 * salinity_ppt * dT;
}

/** Seawater electrical conductivity (mS/cm) - UNESCO PSS-78 simplified. */
function swConductivity(salinity_ppt: number, T_C: number): number {
  const Cref = 42.914 * (salinity_ppt / 35);
  const tempFactor = 1 + 0.0205 * (T_C - 15) - 5.5e-5 * (T_C - 15) * (T_C - 15);
  return Cref * tempFactor;
}

/** Theoretical Cl2 yield per Faraday at 100 % efficiency (g/Ah). */
const CL2_PER_AH = (71 / (2 * 96485)) * 3600; // = 1.3245 g/Ah

// ---------------------------------------------------------------------------
// Param types
// ---------------------------------------------------------------------------

type CellTech = 'monopolar' | 'bipolar';
type AcidType = 'HCl_15pc' | 'HCl_30pc' | 'sulphamic_5pc';
type DutyMode = 'continuous' | 'shock' | 'cyclic';

/**
 * Standard pipe sizes for electrochlorination cell brine connections.
 * Inside diameters are Sch 80 PVDF (BS EN ISO 15493 / ASTM F441) typical for
 * NaOCl / seawater service to BS EN 1092-1 / ASME B16.5 flange faces.
 * Manufacturers (De Nora, Severn Trent, Evoqua, Hidrostal) supply cells with
 * these nominal connection sizes - choose the smallest DN that satisfies the
 * brine-velocity envelope (0.4 - 1.5 m/s typical for cell nozzles).
 */
const STD_PIPES: { dn: number; id_mm: number; max_flow_m3h: number; label: string }[] = [
  { dn: 25,  id_mm: 26.6,  max_flow_m3h:  2.5, label: 'DN25 / 1"' },
  { dn: 32,  id_mm: 35.0,  max_flow_m3h:  4.5, label: 'DN32 / 1-1/4"' },
  { dn: 40,  id_mm: 41.0,  max_flow_m3h:  6.5, label: 'DN40 / 1-1/2"' },
  { dn: 50,  id_mm: 52.5,  max_flow_m3h: 10.5, label: 'DN50 / 2"' },
  { dn: 65,  id_mm: 68.8,  max_flow_m3h: 18.0, label: 'DN65 / 2-1/2"' },
  { dn: 80,  id_mm: 81.7,  max_flow_m3h: 25.5, label: 'DN80 / 3"' },
  { dn: 100, id_mm: 107.1, max_flow_m3h: 44.0, label: 'DN100 / 4"' },
  { dn: 125, id_mm: 131.7, max_flow_m3h: 66.0, label: 'DN125 / 5"' },
  { dn: 150, id_mm: 159.3, max_flow_m3h: 95.0, label: 'DN150 / 6"' },
  { dn: 200, id_mm: 213.7, max_flow_m3h: 170,  label: 'DN200 / 8"' },
  { dn: 250, id_mm: 263.0, max_flow_m3h: 260,  label: 'DN250 / 10"' },
  { dn: 300, id_mm: 312.7, max_flow_m3h: 365,  label: 'DN300 / 12"' },
];

/** Returns the smallest DN whose max flow envelope >= demand. */
function recommendDN(flow_m3h: number, vel_target_ms = 1.0): number {
  // velocity-driven: A = Q / v; D = sqrt(4 A / pi)
  const Q_m3s = flow_m3h / 3600;
  const D_req_mm = Math.sqrt(4 * Q_m3s / Math.max(0.1, vel_target_ms) / Math.PI) * 1000;
  // Pick the smallest standard ID >= required
  const hit = STD_PIPES.find(s => s.id_mm >= D_req_mm) ?? STD_PIPES[STD_PIPES.length - 1];
  return hit.dn;
}

/** Lookup the standard pipe by DN (or fallback to first). */
function lookupDN(dn: number) {
  return STD_PIPES.find(s => s.dn === dn) ?? STD_PIPES[0];
}

/** Snap to nearest standard DN >= the recommended size, with sane lower bound. */
function snapDN(rec: number): number {
  return STD_PIPES.find(s => s.dn >= rec)?.dn ?? STD_PIPES[STD_PIPES.length - 1].dn;
}

/* ============================================================
   Recommendation engines - all sized from seawater flow rate.
   Each returns a partial-state patch to merge with setP().
   ============================================================ */

/** Sizing recipe for the electrolyser stack. */
function recommendStack(p: ECParams): Partial<ECParams> {
  // Cl2 demand kg/h
  const cl2_kgh = p.hypochloriteOutput_kgh;
  // Pick module count so each delivers <= 5 kg Cl2/h (industry mid-size cell)
  const modules = Math.max(1, Math.ceil(cl2_kgh / 5));
  // Total electrolyser inlet ~ 1 m3/h brine per 1 kg Cl2/h product
  const elecInlet = Math.max(2, Math.min(200, cl2_kgh * 1.0));
  const brineFlow = elecInlet / modules;
  return {
    electrolyserInlet_m3h: parseFloat(elecInlet.toFixed(2)),
    seawaterFlow_PDoff_m3h: parseFloat((elecInlet * 1.5).toFixed(2)),  // booster header (1.5× inlet)
    modules_installed: modules,
    modules_standby:   1,
    cells_per_module:  20,
    currentDensity_kAm2: 3.0,
    cellVoltage_V:     4.0,
    currentEfficiency_pc: 90,
    brineVelocity_ms:  0.6,
    // Per-series (T/R) DC current = total mass-equivalent current ÷ cells in series,
    // with a 15 % margin, rounded to 50 A. (Bipolar stacks share one DC current.)
    cell_ratedDC_current_A:   Math.ceil(((cl2_kgh / modules * 1000) / (CL2_PER_AH * 0.9) / 20) * 1.15 / 50) * 50,
    cell_ratedDC_voltage_V:   85,
    cell_ratedCapacity_kgCl2h: parseFloat((cl2_kgh / modules * 1.15).toFixed(2)),
    cell_operatingPressure_barg: 1.5,
    cell_inlet_DN:  snapDN(recommendDN(brineFlow, 1.2)),
    cell_outlet_DN: snapDN(recommendDN(brineFlow, 1.0)),
  };
}

/** Sizing recipe for the NaOCl dosing skid (once-through: dose the process flow). */
function recommendDosing(p: ECParams): Partial<ECParams> {
  // NaOCl solution flow = process flow × ppm ÷ delivered concentration.
  const dose_mass_kgh = (p.dosing_process_flow_m3h * p.dosing_target_ppm) / 1000;
  const flow_total_Lh = p.dosing_delivered_conc_mgL > 0
    ? (dose_mass_kgh * 1e6) / p.dosing_delivered_conc_mgL
    : 0;
  // Choose duty pump count by flow band
  const duty = flow_total_Lh < 200 ? 1 : flow_total_Lh < 1000 ? 2 : 3;
  const per_pump = duty > 0 ? flow_total_Lh / duty : flow_total_Lh;
  return {
    dosingPumps_duty:    duty,
    dosingPumps_standby: 1,
    dosingPump_capacity_Lh_each: parseFloat((per_pump * 1.1).toFixed(0)), // 10 % margin
    dosingPumpHead_bar:  2.0,
    dosingPump_staticPressure_mbar: 280,
    dosingPump_inlet_DN:  snapDN(recommendDN(per_pump / 1000, 0.5)),
    dosingPump_outlet_DN: snapDN(recommendDN(per_pump / 1000, 1.0)),
    storageTank_h:       2,
  };
}

/** Sizing recipe for the H2 dilution blowers. */
function recommendBlower(p: ECParams): Partial<ECParams> {
  const cl2_kgh = p.hypochloriteOutput_kgh;
  const H2 = cl2_kgh * 0.316;
  const air = H2 * 40; // dilution factor 40
  // 1 duty + 1 standby for systems up to 500 Nm3/h air; else 2+1
  const duty = air < 500 ? 1 : 2;
  const per_blower = air * 1.25 / duty;
  return {
    vent_dilution_factor: 40,
    blower_safety_factor: 1.25,
    blower_duty:         duty,
    blower_standby:      1,
    blower_static_mbar:  60,
    blower_purge_min:    15,
    blower_inlet_DN:  snapDN(recommendDN(per_blower, 12)),
    blower_outlet_DN: snapDN(recommendDN(per_blower, 10)),
  };
}

/** Sizing recipe for the acid wash CIP system. */
function recommendAcid(p: ECParams): Partial<ECParams> {
  // Hardness-based interval
  const totalHardness = p.calciumHardness_mgL + p.magnesiumHardness_mgL;
  const interval = Math.max(7, Math.min(120, 30 * (6500 / Math.max(100, totalHardness))));
  // Tank ~ 0.5 m3 per module (allow for CIP loop + spent acid drain)
  const modules = Math.max(1, p.modules_installed);
  const tank_m3 = Math.max(0.5, Math.min(15, modules * 0.5));
  const duration_min = 90;
  const flow_total_Lh = (tank_m3 * 1000) / (duration_min / 60);
  return {
    acidType:               'HCl_15pc' as AcidType,
    acidWashEvery_d:        Math.round(interval),
    acidWashDuration_min:   duration_min,
    acidWashConc_target_pc: 5,
    acidWashTank_m3:        parseFloat(tank_m3.toFixed(1)),
    acidPumpHead_bar:       3,
    acidPump_staticPressure_mbar: 400,
    acidPumps_duty:         1,
    acidPumps_standby:      1,
    acidPump_inlet_DN:  snapDN(recommendDN(flow_total_Lh / 1000, 0.5)),
    acidPump_outlet_DN: snapDN(recommendDN(flow_total_Lh / 1000, 1.0)),
  };
}

/** All-recommendations bundle. */
function recommendAll(p: ECParams): Partial<ECParams> {
  const a = recommendStack(p);
  const merged = { ...p, ...a };
  const b = recommendDosing(merged);
  const merged2 = { ...merged, ...b };
  const c = recommendBlower(merged2);
  const merged3 = { ...merged2, ...c };
  const d = recommendAcid(merged3);
  return { ...a, ...b, ...c, ...d };
}

interface ECParams {
  // ---- Plant flow architecture ----
  seawaterFlow_PDoff_m3h: number;   // max treated-water flow (PD = pressure-drop OFF)
  electrolyserInlet_m3h:  number;   // brine flow into the electrolyser stack
  hypochloriteOutput_kgh: number;   // product Cl2 mass output from the electrolyser
  // ---- Seawater chemistry (standard) ----
  salinity_ppt:     number;
  temperature_C:    number;
  pH_feed:          number;
  TSS_mgL:          number;
  turbidity_NTU:    number;
  // ---- Detailed seawater chemistry (extended) ----
  TDS_mgL:            number;  // total dissolved solids
  chloride_mgL:       number;  // Cl- - 19 000 at S=35
  bromide_mgL:        number;  // Br- - ~ 65 at S=35
  sulphate_mgL:       number;  // SO4^2-
  calciumHardness_mgL: number; // mg/L as CaCO3
  magnesiumHardness_mgL: number; // mg/L as CaCO3
  totalAlkalinity_mgL: number;  // mg/L as CaCO3
  freeCO2_mgL:        number;
  silica_mgL:         number;
  ironMn_mgL:         number;  // total Fe + Mn (electrode-fouling risk)
  organic_TOC_mgL:    number;  // total organic carbon
  // ---- Cell technology ----
  cellTech:          CellTech;
  currentDensity_kAm2: number;
  cellVoltage_V:     number;
  currentEfficiency_pc: number;
  cells_per_module:  number;
  modules_installed: number;
  modules_standby:   number;
  // ---- Brine side ----
  brineVelocity_ms:         number;
  // ---- Cell rated operating conditions (nameplate) ----
  cell_ratedDC_current_A:   number;   // rated DC current per module
  cell_ratedDC_voltage_V:   number;   // rated DC voltage per module
  cell_ratedCapacity_kgCl2h: number;  // nameplate Cl2 production per module
  cell_operatingPressure_barg: number; // brine-side gauge pressure
  cell_inlet_DN:            number;   // standard DN of inlet flange
  cell_outlet_DN:           number;   // standard DN of outlet flange
  // ---- Hypochlorite dosing (once-through: dose the process flow at a target ppm) ----
  dosing_process_flow_m3h: number;   // process water being dosed (e.g. cooling-water main)
  dosing_target_ppm:       number;   // residual chlorine target at the dosing point
  dosing_delivered_conc_mgL: number; // available-Cl concentration the pump delivers
  dosingPumps_duty:    number;
  dosingPumps_standby: number;
  dosingPumpHead_bar:  number;
  dosingPump_staticPressure_mbar: number;
  dosingPump_capacity_Lh_each: number;   // user-set capacity per duty pump (L/h) - 0 = derived
  dosingPump_inlet_DN: number;            // standard pipe (suction)
  dosingPump_outlet_DN: number;           // standard pipe (discharge)
  storageTank_h:       number;
  // ---- Hydrogen dilution / vent (blowers) ----
  vent_dilution_factor: number;
  blower_safety_factor: number;     // 1.1 - 1.5 sizing margin over computed
  blower_purge_min:    number;      // pre-energise + post-shutdown purge minutes
  blower_static_mbar:  number;      // total static pressure at blower discharge (mbar)
  blower_duty:         number;
  blower_standby:      number;
  blower_inlet_DN:     number;            // standard pipe (suction / air intake)
  blower_outlet_DN:    number;            // standard pipe (vent discharge)
  // ---- H2 tank-headspace target (drives blower auto-sizing) ----
  h2_target_pct_vv:    number;      // max equilibrium % H2 v/v in tank headspace (1% = 25% LEL)
  h2_tank_volume_m3:   number;      // tank/enclosure headspace volume to ventilate
  // ---- Acid wash ----
  acidType:           AcidType;
  acidWashEvery_d:    number;
  acidWashDuration_min: number;
  acidWashConc_target_pc: number;
  acidWashTank_m3:    number;
  acidPumpHead_bar:   number;
  acidPump_staticPressure_mbar: number;
  acidPumps_duty:     number;
  acidPumps_standby:  number;
  acidPump_inlet_DN:  number;
  acidPump_outlet_DN: number;
  // ---- Working conditions (apply to ALL devices) ----
  workingTemp_C:      number;       // ambient / electrical-room temperature for blowers, pumps, panels
  workingPressure_mbar_atm: number; // local atmospheric pressure (sea-level default ~1013)
  // ---- Seawater pre-filter (sized from PD-off inflow) ----
  filter_micron_um:        number;  // 80 - 100 typical for electrochlorination
  filter_designDP_bar:     number;  // clean-to-dirty DP trigger
  filter_rate_m3_m2_h:     number;  // design filtration rate
  filter_inlet_DN:         number;  // standard pipe
  filter_outlet_DN:        number;  // standard pipe
  // ---- Transformer / rectifier (datasheet) ----
  txr_AC_primary_V:  number;        // AC supply (typ 400 / 690 V)
  txr_efficiency_pc: number;        // overall transformer + rectifier efficiency
  txr_redundancy:    'N' | 'N+1';   // installed redundancy
  // ---- NaOCl storage + H2 degassing tank ----
  storage_tank_volume_m3: number;
  storage_degassing_residence_min: number; // residence time for H2 disengagement
  storage_material:  'GRP' | 'HDPE' | 'PP' | 'PVDF';
  storage_freeboard_pc: number;     // % above design liquid level for vent gas-space
  // ---- Degassing tank design + breathing (API 2000) ----
  tank_HD_ratio:           number;  // height : diameter ratio (legacy / fallback)
  tank_design_pressure_mbar: number; // tank design overpressure
  tank_design_vacuum_mbar:   number; // tank design vacuum (positive number = mbar of vacuum)
  vent_velocity_target_ms:   number; // target gas velocity in the vent line
  // ---- Vendor sizing-report method (QatarEnergy T30X2, doc 4494-M2UT-6-12-0001) ----
  tank_diameter_mm:    number;      // vessel diameter (report assumes 1750)
  tank_max_height_mm:  number;      // site head-room limit - tank must fit under this
  antifoam_flow_m3h:   number;      // B — seawater antifoam spray into tank
  degas_min_min:       number;      // F — minimum safe H2 degassing residence (5)
  freeboard_top_mm:    number;      // P — HHLL → tank top (500 = 0.5 m)
  hhll_margin_pc:      number;      // O — margin on stack height to set HHLL (20 %)
  // PD operating scenarios
  case1_on_min:  number; case1_off_min: number; case1_dose_m3h: number; // Case-1 20/10, 22.5
  case2_on_min:  number; case2_off_min: number; case2_dose_m3h: number; // Case-2 15/15, 30
  // nozzle / dead-band heights (mm)
  nozzle_bottom_I_mm: number;       // I  tank bottom → outlet nozzle
  nozzle_dia_J_mm:    number;       // J  outlet nozzle diameter
  nozzle_brim_K_mm:   number;       // K  nozzle brim → low-low level
  deadband_L_mm:      number;       // L  low-low → low (PD OFF) level
  // ---- Eductor (NaOCl dosing motive) ----
  eductor_motive_m3h:    number;    // seawater motive flow
  eductor_suction_Lh:    number;    // NaOCl suction flow
  eductor_motive_DN:     number;
  eductor_suction_DN:    number;
  eductor_discharge_DN:  number;
  eductor_motive_pressure_bar: number;
  // ---- Duty cycle ----
  dutyMode:          DutyMode;
  shockHoursPerDay:  number;
  // Cyclic mode — repeating ON/OFF pattern (e.g. 30 min ON / 30 min OFF, or 20/20)
  cycleOnMin:        number;
  cycleOffMin:       number;
}

// Defaults reproduce the QKQ2831 / Mesaieed (Perocon) sizing workbook so the page
// matches the issued P&ID on load. See the build-out in the Datasheet-verification
// panel. Key vendor figures: 15 kg/h NaOCl, 1500 A/m², 0.75 Faraday eff, 20 cells in
// series → 756 A DC × 85 V → 64 kW DC, 4.31 kWh/kg, H2 5.43 Nm³/h, dilution air 543.
const DEFAULTS: ECParams = {
  // headline plant flows
  seawaterFlow_PDoff_m3h: 22.5,
  electrolyserInlet_m3h:  15.0,
  hypochloriteOutput_kgh: 15.0,
  salinity_ppt:     44.4,   // vendor PSS salinity (chloride 24.57 g/L)
  temperature_C:    16,     // design / worst-case (min temp → highest cell voltage)
  pH_feed:          8.2,
  TSS_mgL:          100,
  turbidity_NTU:    3,
  // detailed chemistry (QKQ2831 seawater composition, Mesaieed)
  TDS_mgL:               47465,
  chloride_mgL:          24570,
  bromide_mgL:           80,
  sulphate_mgL:          3508,
  calciumHardness_mgL:   1102,
  magnesiumHardness_mgL: 4148,
  totalAlkalinity_mgL:   50,
  freeCO2_mgL:           1.5,
  silica_mgL:            2.0,
  ironMn_mgL:            0.05,
  organic_TOC_mgL:       1.0,
  cellTech:           'bipolar',
  currentDensity_kAm2: 1.5,    // 1500 A/m² (vendor)
  cellVoltage_V:       4.27,   // per cell → 20 × 4.27 = 85.4 VDC stack (vendor practical V)
  currentEfficiency_pc: 75,    // Faraday (current) efficiency 0.75 (vendor)
  cells_per_module:    20,     // 2 ECU × 10 cells in series per stream
  modules_installed:   1,
  modules_standby:     1,
  brineVelocity_ms:         0.6,
  cell_ratedDC_current_A:    756,   // T/R rated DC current per series (vendor)
  cell_ratedDC_voltage_V:    85,    // T/R rated DC voltage (vendor)
  cell_ratedCapacity_kgCl2h: 15,    // stream nameplate NaOCl capacity
  cell_operatingPressure_barg: 2.0, // operating pressure 2.0 bar(g) (datasheet)
  cell_inlet_DN:             65,    // 2½" / DN65 supply header (vendor line sizing)
  cell_outlet_DN:            65,
  // Dosing on the once-through (vendor QKQ2831) basis: 4652 m³/h process @ 1.5 ppm,
  // delivered at ~300 mg/L available Cl → ≈23.3 m³/h dosing duty.
  dosing_process_flow_m3h: 4652,
  dosing_target_ppm:       1.5,
  dosing_delivered_conc_mgL: 1000,  // = NaOCl product strength (production/inflow); pump moves the 1000 mg/L product
  dosingPumps_duty:    1,
  dosingPumps_standby: 1,
  dosingPumpHead_bar:  2.0,            // vendor SD pump head ≈ 2.28 bar (with static below)
  dosingPump_staticPressure_mbar: 280,
  dosingPump_capacity_Lh_each: 0,   // 0 = derived from demand
  dosingPump_inlet_DN: 100,         // sized for the once-through dosing flow (~23 m³/h → 0.7 m/s)
  dosingPump_outlet_DN: 80,         // ~1.2 m/s discharge (vendor uses DN50 @ 3.3 m/s for intermittent shock)
  storageTank_h:       2,
  vent_dilution_factor: 100,  // physics: air:H2 ≈ 100:1 → H2 ≈ 0.99% v/v (< 25% of the 4% LEL)
  blower_safety_factor: 1.0,  // vendor blower duty = dilution air (no extra margin)
  blower_purge_min:     15,
  blower_static_mbar:   50,   // 50 mbar(g) discharge (datasheet)
  blower_duty:          1,
  blower_standby:       1,
  blower_inlet_DN:      150,  // DN150 air-scour duct (vendor)
  blower_outlet_DN:     150,
  h2_target_pct_vv:     1.0,   // 25 % LEL (industry standard for enclosed brine/H2 spaces)
  h2_tank_volume_m3:    5.0,
  acidType:               'HCl_15pc',
  acidWashEvery_d:        21,
  acidWashDuration_min:   30,
  acidWashConc_target_pc: 6,
  acidWashTank_m3:        2.0,
  acidPumpHead_bar:       2,
  acidPump_staticPressure_mbar: 400,
  acidPumps_duty:         1,
  acidPumps_standby:      1,
  acidPump_inlet_DN:      50,   // sized for ~4 m3/h recirc at ~0.5 m/s (DN25 gave 1.8 m/s)
  acidPump_outlet_DN:     40,   // sized for ~4 m3/h at ~1.0 m/s
  workingTemp_C:           35,
  workingPressure_mbar_atm: 1013,
  // seawater pre-filter (everything else derived from PD-off seawater flow)
  filter_micron_um:    100,
  filter_designDP_bar: 0.5,
  filter_rate_m3_m2_h: 100,
  filter_inlet_DN:     65,
  filter_outlet_DN:    65,
  // transformer / rectifier (datasheet defaults - 415 V primary, 95 % rectifier x 98 % transformer)
  txr_AC_primary_V:  415,
  txr_efficiency_pc: 93,
  txr_redundancy:    'N+1',
  // Degassing hypochlorite storage tank (datasheet A30XX-TX2, GRVE + ECTFE)
  storage_tank_volume_m3: 5.13,
  storage_degassing_residence_min: 20,
  storage_material:  'GRP',
  storage_freeboard_pc: 20,
  tank_HD_ratio:            2.14,   // datasheet 1750 Ø × 3750 T-T
  tank_design_pressure_mbar: 75,    // tank design overpressure (datasheet design ATM; 75 = relief margin)
  tank_design_vacuum_mbar:   5,     // vacuum-relief setpoint
  vent_velocity_target_ms:   15,    // physics: standard gas-line velocity → ~114 mm bore (DN125), ΔP ~3 mbar
  tank_diameter_mm:    1900,        // widened from 1750 to fit a 3.4 m head-room limit
  tank_max_height_mm:  3400,        // site head-room limit
  antifoam_flow_m3h:   0.5,         // B
  degas_min_min:       5,           // F — 5-min minimum safe degassing
  freeboard_top_mm:    500,         // P — 0.5 m HHLL → top
  hhll_margin_pc:      20,          // O — 20 % margin to set HHLL
  case1_on_min:  20, case1_off_min: 10, case1_dose_m3h: 22.5,
  case2_on_min:  15, case2_off_min: 15, case2_dose_m3h: 30,
  nozzle_bottom_I_mm: 150,          // I
  nozzle_dia_J_mm:    80,           // J
  nozzle_brim_K_mm:   150,          // K
  deadband_L_mm:      200,          // L
  // eductor (NaOCl dosing motive)
  eductor_motive_m3h:    7.5,   // motive seawater split from PD-off side
  eductor_suction_Lh:    1500,
  eductor_motive_DN:     50,
  eductor_suction_DN:    25,
  eductor_discharge_DN:  65,
  eductor_motive_pressure_bar: 3.5,
  dutyMode:         'continuous',
  shockHoursPerDay: 4,
  cycleOnMin:       30,
  cycleOffMin:      30,
};

const ACID_PROPS: Record<AcidType, { name: string; concPct: number; sg: number; activeFrac: number; useCase: string }> = {
  HCl_15pc:      { name: 'Hydrochloric acid 15 %', concPct: 15, sg: 1075, activeFrac: 0.15, useCase: 'Standard CIP for electrode descaling (CaCO3 / Mg(OH)2)' },
  HCl_30pc:      { name: 'Hydrochloric acid 30 %', concPct: 30, sg: 1149, activeFrac: 0.30, useCase: 'Heavy fouling / extended interval cleaning' },
  sulphamic_5pc: { name: 'Sulphamic acid 5 %',     concPct:  5, sg: 1020, activeFrac: 0.05, useCase: 'Low-corrosivity option for stainless components' },
};

// ---------------------------------------------------------------------------
// Project datasheet replica — QatarEnergy NGL Complex, Mesaieed
// Transcribed from issued Rev.0 process datasheets (P&ID 4737-M2UT-6-51-0001-002/003/004):
//   Electrochlorination Cells, H2 Dilution Air Blowers, Degassing Hypochlorite
//   Storage Tank, Acid Wash Pumps. Values marked VTA/HOLD are vendor-to-advise.
// ---------------------------------------------------------------------------
/** Equipment tag numbers as on the issued P&ID (4737-M2UT-6-51-0001) — overlaid on
 *  the diagram. The cells / rectifier / blowers sit inside the vendor "Pulse Dosing
 *  Electrochlorination Package" box on the P&ID, so they keep the datasheet placeholder
 *  tags; the balance-of-plant items use the drawing's S-X1 / T-X5 / P-X5 / SM / W30 / T30 tags. */
const MESAIEED_TAGS = {
  cells:    'A30XX-EYXX1A/B',     // inside the vendor package box on the P&ID
  blowers:  'A30XX-KX1A/B',
  airFilter:'A30XX-SX1A/B',
  filters:  'S-X1A/B',            // seawater self-cleaning filters (2×100%)
  mixerIn:  'SM-X1A/B',           // in-line static mixers (per filter stream)
  degasTank:'T-X5',               // degassing hypochlorite storage tank
  dosingPumps:'P-X5A/B',          // NaOCl dosing pumps (+ PM-X5A/B motors)
  mixerOut: 'W30-X1',             // in-line static mixer on the dosing line
  drainPit: 'T30-X3',            // drain pit (2 m³)
  drainPump:'P-30X4A/B',          // drain pit pump
  pcv:      'PCV-X01',            // seawater inlet pressure control valve
  acidPumps:'A30XX-PX2A/B',
  acidTank: 'A30XX-TX1',
} as const;

/** PRESET A — Vendor sizing (Perocon "System Design Calculations" QKQ2831 Rev.2).
 *  The manufacturer's design calc; the page's default basis. */
const PRESET_VENDOR: Partial<ECParams> = {
  // Plant flows (15 m³/h electrolyser stream; NaOCl 15 / 22.5 m³/h tank/booster)
  seawaterFlow_PDoff_m3h: 22.5,
  electrolyserInlet_m3h:  15.0,
  hypochloriteOutput_kgh: 15.0,           // NaOCl production capacity (max)
  // Seawater chemistry (QKQ2831 composition: chloride 24.57 g/L, TDS 47 465 mg/L)
  salinity_ppt:     44.4,
  temperature_C:    16,                    // design / worst-case (15–35 operating)
  pH_feed:          8.2,
  TSS_mgL:          100,
  TDS_mgL:          47465,
  chloride_mgL:     24570,
  sulphate_mgL:     3508,
  totalAlkalinity_mgL: 50,
  calciumHardness_mgL:   1102,
  magnesiumHardness_mgL: 4148,
  // Electrolyser electricals (bipolar, 2 ECU × 10 cells = 20 in series; 1500 A/m²; 0.75 Faraday eff)
  cellTech:           'bipolar',
  cells_per_module:    20,
  modules_installed:   1,
  modules_standby:     1,
  currentDensity_kAm2: 1.5,
  cellVoltage_V:       4.27,                // → 85.4 VDC stack
  currentEfficiency_pc: 75,
  cell_ratedDC_current_A: 756,             // T/R rated DC current per series
  cell_ratedDC_voltage_V: 85,              // T/R rated DC voltage
  cell_ratedCapacity_kgCl2h: 15,
  cell_operatingPressure_barg: 2.0,        // operating pressure 2.0 bar(g)
  cell_inlet_DN:  65,                      // DN65 supply header (vendor line sizing)
  cell_outlet_DN: 65,
  // H2 dilution air blowers (2×100 %, 543 Nm³/h, 50 mbar discharge)
  vent_dilution_factor: 100,               // air:H2 = 100:1 → 1% H2 in air
  blower_safety_factor: 1.0,
  blower_duty:    1,
  blower_standby: 1,
  blower_static_mbar: 50,
  blower_inlet_DN:  150,                    // DN150 air-scour duct
  blower_outlet_DN: 150,
  h2_target_pct_vv: 1.0,                    // dilute H2 to <1 % in air
  // Degassing tank — vendor sizes a large vessel (degas + 16 h shock storage of the 4652 m³/h process)
  storage_tank_volume_m3: 1061,
  storage_material: 'GRP',
  h2_tank_volume_m3: 5.0,
  // Hypochlorite dosing (once-through: 4652 m³/h process @ 1.5 ppm, 300 mg/L delivered → ≈23.3 m³/h)
  dosing_process_flow_m3h: 4652,
  dosing_target_ppm: 1.5,
  dosing_delivered_conc_mgL: 1000,  // = NaOCl product strength (production/inflow); pump moves the 1000 mg/L product
  dosingPumps_duty: 1,
  dosingPumps_standby: 1,
  dosingPumpHead_bar: 2.0,
  dosingPump_staticPressure_mbar: 280,
  // Acid wash CIP (5–7 % HCl, 5 m³/h, every 2–4 weeks, ~30 min)
  acidType: 'HCl_15pc',
  acidWashConc_target_pc: 6,
  acidWashEvery_d: 21,
  acidWashDuration_min: 30,
  acidPumpHead_bar: 2.0,
  acidPumps_duty: 1,
  acidPumps_standby: 1,
  // Working conditions (Mesaieed: max ambient 52 °C)
  workingTemp_C: 35,
  workingPressure_mbar_atm: 1013,
};

/** PRESET B — Issued datasheet (QatarEnergy process datasheets, P&ID 4737-M2UT-6-51-0001).
 *  The client-issued nameplate: higher T/R rating (1100 A / 61 V), 24 cells (2 modules × 12),
 *  the small 5.13 m³ degassing vessel A30XX-TX2 and the 600 Nm³/h blower. Same seawater,
 *  capacity, dosing and acid basis as the vendor preset. */
const PRESET_DATASHEET: Partial<ECParams> = {
  ...PRESET_VENDOR,
  // Cells: 12 cells/module in series at ~5.08 V → 61 VDC; CE ~86 % → ≈1100 A/series
  cells_per_module:    12,
  currentDensity_kAm2: 2.0,
  cellVoltage_V:       5.08,                // → 61 VDC stack
  currentEfficiency_pc: 86,                 // → ≈1100 A per series
  cell_ratedDC_current_A: 1100,             // datasheet rated DC current
  cell_ratedDC_voltage_V: 61,               // datasheet rated DC voltage
  cell_inlet_DN:  80,                       // 3" inlet/outlet (datasheet)
  cell_outlet_DN: 80,
  // Blower 600 Nm³/h (556 × ~8 % margin), 50 mbar discharge
  blower_safety_factor: 1.1,
  blower_static_mbar: 50,
  blower_inlet_DN:  250,                    // 10" tank nozzles N3/N4
  blower_outlet_DN: 250,
  // Degassing hypochlorite storage tank A30XX-TX2 — 5.13 m³ working, GRVE + ECTFE
  storage_tank_volume_m3: 5.13,
};

/** Two source documents for the SAME Mesaieed plant, compared side-by-side.
 *  - Vendor sizing  = Perocon "System Design Calculations" QKQ2831 Rev.2 (manufacturer's calc; page default).
 *  - Issued datasheet = QatarEnergy process datasheets, P&ID 4737-M2UT-6-51-0001 (client-issued).
 *  diff:true marks a genuine engineering difference (not just rounding). */
interface DocRow { param: string; vendor: string; datasheet: string; note: string; diff?: boolean; }
const VENDOR_VS_DATASHEET: DocRow[] = [
  { param: 'NaOCl capacity',         vendor: '15 kg/h',            datasheet: '15 kg/h',            note: 'Same duty.' },
  { param: 'Product concentration',  vendor: '1000 mg/L (310 demand)', datasheet: '1000 mg/L',      note: 'Same max; vendor also tracks the 310 mg/L delivered dose.' },
  { param: 'Cells in series',        vendor: '20  (2 ECU × 10)',   datasheet: '24  (2 mod × 12)',   note: 'Different cell count / module split.', diff: true },
  { param: 'Rated DC current',       vendor: '756 A / series',     datasheet: '1100 A',             note: 'Datasheet carries T/R headroom; sizing is the calculated duty.', diff: true },
  { param: 'Rated DC voltage',       vendor: '85 VDC',             datasheet: '61 VDC',             note: 'Follows the current/cell-count split.', diff: true },
  { param: 'DC power',               vendor: '≈ 64 kW',            datasheet: '≈ 67 kW (1100×61)',  note: 'Similar power, different I×V split.' },
  { param: 'Current density',        vendor: '1500 A/m²',          datasheet: 'not stated',         note: 'Sizing basis only.' },
  { param: 'Faraday efficiency',     vendor: '0.75 (design)',      datasheet: 'not stated',         note: 'Conservative sizing derate.' },
  { param: 'H₂ basis',               vendor: '0.362 Nm³/kg NaOCl', datasheet: '0.30 Nm³/kg act-Cl', note: 'Different reference basis → slightly different H₂.', diff: true },
  { param: 'H₂ generation',          vendor: '5.43 Nm³/h',         datasheet: '5.56 Nm³/h',         note: 'Within 2 %.' },
  { param: 'Dilution air',           vendor: '543 Nm³/h',          datasheet: '600 Nm³/h',          note: 'Datasheet adds ~5–8 % blower margin (556→600).', diff: true },
  { param: 'Blower discharge',       vendor: '150 mmwg (≈15 mbar)', datasheet: '50 mbar',           note: 'Different head basis.', diff: true },
  { param: 'Degassing tank',         vendor: '1061 m³',            datasheet: '5.13 m³ (A30XX-TX2)', note: 'DIFFERENT VESSELS: vendor = degas + 16 h shock storage of the 4652 m³/h process; datasheet = the degassing vessel only.', diff: true },
  { param: 'NaOCl dosing pump',      vendor: '23.25 m³/h @ 2.28 bar', datasheet: 'min-flow return only', note: 'Vendor sizes the dosing duty; datasheet shows the tank nozzle, not a pump rating.', diff: true },
  { param: 'Acid wash',              vendor: '5 m³/h, 2–4 wk',     datasheet: '5–7 % HCl, 2–4 wk',  note: 'Consistent.' },
  { param: 'Operating pressure',     vendor: '6 barg (design)',    datasheet: '2 barg op / 10 design', note: 'Datasheet separates operating vs design.' },
];

/** Datasheet design values for the verification panel — compared against the live calc. */
interface RefRow { group: string; label: string; datasheet: string; live: (p: ECParams, r: ReturnType<typeof calcResults>) => string; check?: (p: ECParams, r: ReturnType<typeof calcResults>) => boolean; }
const MESAIEED_REF: RefRow[] = [
  { group: 'Cells', label: 'Inlet seawater flowrate', datasheet: '15 m³/h',  live: p => `${p.electrolyserInlet_m3h.toFixed(1)} m³/h`, check: p => Math.abs(p.electrolyserInlet_m3h - 15) <= 0.6 },
  { group: 'Cells', label: 'NaOCl production (max)',   datasheet: '15 kg/h',  live: p => `${p.hypochloriteOutput_kgh.toFixed(1)} kg/h`, check: p => Math.abs(p.hypochloriteOutput_kgh - 15) <= 1 },
  { group: 'Cells', label: 'NaOCl concentration', datasheet: '1000 mg/L',
    // Datasheet basis: product mass ÷ liquid throughput (15 kg/h ÷ 15 m³/h = 1000 mg/L).
    live: p => `${(p.electrolyserInlet_m3h > 0 ? (p.hypochloriteOutput_kgh / p.electrolyserInlet_m3h) * 1000 : 0).toFixed(0)} mg/L`,
    check: p => { const c = p.electrolyserInlet_m3h > 0 ? (p.hypochloriteOutput_kgh / p.electrolyserInlet_m3h) * 1000 : 0; return c >= 700 && c <= 1400; } },
  { group: 'Cells', label: 'Cells in series per stream', datasheet: '20',     live: p => `${p.cells_per_module}`, check: p => p.cells_per_module === 20 },
  { group: 'Cells', label: 'Actual current (Faraday)', datasheet: '15 119 A', live: (_p, r) => `${r.I_per_module_A.toFixed(0)} A`, check: (_p, r) => Math.abs(r.I_per_module_A - 15119) <= 800 },
  { group: 'Cells', label: 'DC current per series (T/R)', datasheet: '756 A',  live: (_p, r) => `${r.I_cell_A.toFixed(0)} A`, check: (_p, r) => Math.abs(r.I_cell_A - 756) <= 40 },
  { group: 'Cells', label: 'Stack voltage',            datasheet: '85 VDC',    live: (_p, r) => `${r.stackVoltage_V.toFixed(0)} V`, check: (_p, r) => Math.abs(r.stackVoltage_V - 85) <= 5 },
  { group: 'Cells', label: 'DC power per stream',      datasheet: '64 kW',     live: (_p, r) => `${r.power_total_DC_kW.toFixed(1)} kW`, check: (_p, r) => Math.abs(r.power_total_DC_kW - 64) <= 8 },
  { group: 'Cells', label: 'Specific energy (DC ≈4.31)', datasheet: '4.31 kWh/kg', live: (_p, r) => `${r.specificEnergy_kWh_per_kgCl2.toFixed(2)} kWh/kg`, check: (_p, r) => r.specificEnergy_kWh_per_kgCl2 <= 6 },
  { group: 'Cells', label: 'Operating pressure',       datasheet: '2.0 bar(g)', live: p => `${p.cell_operatingPressure_barg.toFixed(1)} bar(g)`, check: p => Math.abs(p.cell_operatingPressure_barg - 2) <= 0.5 },
  { group: 'H₂', label: 'H₂ generation rate',          datasheet: '5.43 Nm³/h', live: (_p, r) => `${r.H2_Nm3h.toFixed(2)} Nm³/h`, check: (_p, r) => Math.abs(r.H2_Nm3h - 5.43) <= 0.6 },
  { group: 'H₂', label: 'Dilution air requirement',    datasheet: '543 Nm³/h',  live: (_p, r) => `${r.dilutionAir_Nm3h.toFixed(0)} Nm³/h`, check: (_p, r) => Math.abs(r.dilutionAir_Nm3h - 543) <= 80 },
  { group: 'H₂', label: 'Vent H₂ fraction',            datasheet: '< 1 % v/v',  live: (_p, r) => `${r.vent_H2_pc_vv.toFixed(2)} % v/v`, check: (_p, r) => r.vent_safe },
  { group: 'H₂', label: 'Blower discharge pressure',   datasheet: '50 mbar(g)', live: p => `${p.blower_static_mbar} mbar`, check: p => Math.abs(p.blower_static_mbar - 50) <= 15 },
  { group: 'Tank', label: 'Degassing tank volume',     datasheet: '5.13 or 1061 m³', live: p => `${p.storage_tank_volume_m3.toFixed(2)} m³`, check: p => Math.abs(p.storage_tank_volume_m3 - 5.13) <= 0.6 || Math.abs(p.storage_tank_volume_m3 - 1061) <= 60 },
  { group: 'Dosing', label: 'Shock dosing pump flow',  datasheet: '23.25 m³/h', live: (_p, r) => `${(r.dosingPumpFlow_total_demand_Lh/1000).toFixed(1)} m³/h`, check: (_p, r) => Math.abs(r.dosingPumpFlow_total_demand_Lh/1000 - 23.25) <= 3 },
  { group: 'Dosing', label: 'Dosing pump power',       datasheet: '2.25 kW',    live: (_p, r) => `${(r.dosingPump_kW_each*Math.max(1,1)).toFixed(2)} kW`, check: (_p, r) => Math.abs(r.dosingPump_kW_each - 2.25) <= 0.6 },
  { group: 'Acid', label: 'Acid wash concentration',   datasheet: '5–7 % HCl',  live: p => `${p.acidWashConc_target_pc.toFixed(0)} %`, check: p => p.acidWashConc_target_pc >= 5 && p.acidWashConc_target_pc <= 7 },
  { group: 'Acid', label: 'Acid wash interval',        datasheet: '2–4 weeks',  live: p => `${p.acidWashEvery_d} d`, check: p => p.acidWashEvery_d >= 14 && p.acidWashEvery_d <= 28 },
];

// ---------------------------------------------------------------------------
// Equipment catalogue & duty-split (Equipment Selection tab)
// Each piece of plant has a set of standard unit ratings; the page picks the
// smallest size that meets the live duty in ≤ maxDuty units, then adds standby.
// ---------------------------------------------------------------------------
interface EqType { key: string; label: string; unit: string; sizes: number[]; note: string; }
const EQUIPMENT_CATALOGUE: EqType[] = [
  { key: 'stream',   label: 'Electrochlorination streams', unit: 'kg/h NaOCl', sizes: [5, 10, 15, 20, 30, 40], note: 'Bipolar seawater stream (≈15 kg/h class = 2 ECU × 10 cells, 756 A).' },
  { key: 'txr',      label: 'Transformer-rectifiers',      unit: 'kW DC',      sizes: [16, 32, 64, 100, 160, 250], note: 'One T/R per electrolyser stream (+ standby).' },
  { key: 'dosing',   label: 'NaOCl dosing pumps',          unit: 'm³/h',       sizes: [2, 8, 16, 25, 40, 60, 100], note: 'Diaphragm metering < 16 m³/h; centrifugal ≥ 25 m³/h (PVDF/PTFE wetted).' },
  { key: 'blower',   label: 'H₂ dilution blowers',         unit: 'Nm³/h',      sizes: [300, 600, 1000, 1500, 2500], note: 'Centrifugal / side-channel; sized to keep H₂ < 1 % v/v.' },
  { key: 'acid',     label: 'Acid wash (CIP) pumps',       unit: 'm³/h',       sizes: [5, 10], note: 'Intermittent CIP; 5 m³/h for < 60 kg/h capacity.' },
  { key: 'strainer', label: 'Seawater strainers',          unit: 'm³/h',       sizes: [25, 50, 100, 200, 400, 800], note: 'Auto / manual self-cleaning, 80–150 µm.' },
];
/** Pick a unit rating and duty count for a live total. override>0 forces the rating.
 *  Auto prefers a single duty unit (smallest catalogue size that covers the whole duty,
 *  → 1 + 1); only when the duty exceeds the largest available size does it split across
 *  multiple of the largest unit. Mirrors how the vendor selects 1×23 m³/h dosing etc. */
function splitUnits(total: number, sizes: number[], override?: number) {
  const rating = override && override > 0
    ? override
    : (sizes.find(s => s >= total) ?? sizes[sizes.length - 1]);
  const duty = Math.max(1, Math.ceil(total / Math.max(1e-6, rating)));
  return { rating, duty };
}

// ---------------------------------------------------------------------------
// Engineering calculations
// ---------------------------------------------------------------------------
function calcResults(p: ECParams) {
  const rho  = swDensity(p.salinity_ppt, p.temperature_C);
  const cond = swConductivity(p.salinity_ppt, p.temperature_C);

  // ---- Detailed chemistry corrections ----
  // 1. Chloride-availability correction on current efficiency:
  //    Reference: ~ 19 000 mg/L at S=35 ppt -> nominal CE.
  //    Below 8 000 mg/L Cl-, CE drops linearly (less Cl2 vs O2 competition).
  const Cl_ref = 19000;
  const Cl_factor = Math.max(0.55, Math.min(1.02, p.chloride_mgL / Cl_ref));
  const adjusted_CE_pc = Math.max(50, Math.min(98, p.currentEfficiency_pc * Cl_factor));

  // 2. Total hardness drives the recommended wash interval (CaCO3 + Mg(OH)2 scaling).
  const totalHardness_mgL = p.calciumHardness_mgL + p.magnesiumHardness_mgL;
  // Empirical: at 6 500 mg/L (open ocean) wash ~ 30 d; doubles when hardness halved.
  const recommendedWashInterval_d = Math.max(7, Math.min(120, 30 * (6500 / Math.max(100, totalHardness_mgL))));

  // 3. Bromate formation risk (Br- > 0.4 mg/L is the WHO guideline floor; seawater is far above).
  const bromate_risk = p.bromide_mgL > 50 ? 'High'
    : p.bromide_mgL > 10 ? 'Moderate'
    : p.bromide_mgL > 0.4 ? 'Watch'
    : 'Low';

  // 4. Bicarbonate / alkalinity sets neutralisation duty for spent acid stream
  const neutralisation_NaOH_kg_per_event_basis = p.totalAlkalinity_mgL; // for display only

  // Daily ON hours under each duty mode:
  //   continuous → 24 h
  //   shock      → user-set shock hours per day
  //   cyclic     → 24 h × ON / (ON+OFF) with sub-minute clamp
  const cycleTotalMin = Math.max(0.5, p.cycleOnMin + p.cycleOffMin);
  const cycleDutyFrac = Math.max(0, Math.min(1, p.cycleOnMin / cycleTotalMin));
  const dailyDuty_h =
    p.dutyMode === 'continuous' ? 24
    : p.dutyMode === 'cyclic'    ? 24 * cycleDutyFrac
    : Math.max(1, Math.min(24, p.shockHoursPerDay));
  // ----------- Headline plant flows -----------
  // The user sets the hypochlorite product output directly (kg Cl2 / h)
  // and the derived free-Cl2 dose into the treated water is computed against
  // the PD-off seawater flow.
  const cl2_demand_kgh_inst = p.hypochloriteOutput_kgh;
  const cl2_demand_kgd = cl2_demand_kgh_inst * dailyDuty_h;
  const cl2_demand_kgy = cl2_demand_kgd * 365;
  // Free-Cl2 residual delivered to the PROTECTED process water (the cooling/seawater
  // main being dosed), not the booster. = product mass ÷ process flow. Changes live
  // with both the Cl2 production AND the process-flow input.
  const freeCl2_dose_mgL = p.dosing_process_flow_m3h > 0
    ? (p.hypochloriteOutput_kgh * 1000) / p.dosing_process_flow_m3h
    : 0;
  // Derived per-module brine flow from total electrolyser inlet
  const brineFlow_per_module_m3h = Math.max(1, p.modules_installed) > 0
    ? p.electrolyserInlet_m3h / Math.max(1, p.modules_installed)
    : p.electrolyserInlet_m3h;

  const modules_running = Math.max(1, p.modules_installed);
  const cl2_per_module_gh = (cl2_demand_kgh_inst * 1000) / modules_running;
  // Use chloride-corrected effective current efficiency derived from detailed chemistry
  const I_per_module_A    = cl2_per_module_gh / (CL2_PER_AH * (adjusted_CE_pc / 100));
  const totalCurrent_A    = I_per_module_A * modules_running;

  const cellArea_m2_per_cell = I_per_module_A / (p.currentDensity_kAm2 * 1000) / Math.max(1, p.cells_per_module);
  const cellArea_total_m2    = cellArea_m2_per_cell * p.cells_per_module * modules_running;

  const stackVoltage_V      = p.cellVoltage_V * p.cells_per_module;
  // Bipolar (series) stack: the SAME DC current flows through every cell and the
  // cell voltages add. I_per_module_A is the production-equivalent TOTAL current
  // (Faraday, ∝ mass); the actual transformer-rectifier DC current is that divided
  // by the number of cells in series. Power = V_stack × I_cell  (vendor QKQ2831:
  // 15119 A / 20 cells = 756 A per series × 85 VDC = 64 kW). The previous form
  // multiplied V_stack by the total current and over-counted by N_cells.
  const I_cell_A            = I_per_module_A / Math.max(1, p.cells_per_module);
  const power_per_module_kW = (stackVoltage_V * I_cell_A) / 1000;
  const power_total_DC_kW   = power_per_module_kW * modules_running;
  // Use user-set transformer + rectifier overall efficiency (replaces old 0.95 x 0.98 product)
  const txr_eff = Math.max(0.7, Math.min(0.99, p.txr_efficiency_pc / 100));
  const power_total_AC_kW   = power_total_DC_kW / txr_eff;
  const specificEnergy_kWh_per_kgCl2 = cl2_demand_kgh_inst > 0
    ? power_total_AC_kW / cl2_demand_kgh_inst
    : 0;

  const totalBrineFlow_m3h = brineFlow_per_module_m3h * modules_running;
  // Product concentration = NaOCl mass produced ÷ liquid throughput.
  //   15 kg/h ÷ 15 m³/h = 1 kg/m³ = 1000 mg/L  (matches the P&ID / datasheet).
  const naocl_mgL = totalBrineFlow_m3h > 0
    ? (cl2_demand_kgh_inst / totalBrineFlow_m3h) * 1000
    : 0;
  // …expressed as % w/w (mass solute ÷ mass solution): 1000 mg/L ≈ 0.10 % w/w.
  const naocl_pc_w = rho > 0 ? naocl_mgL / (rho * 10) : 0;

  // Stoichiometry: 1 mol Cl2 -> 1 mol H2. The vendor sizing workbook (QKQ2831)
  // uses 0.362 Nm³ H2 per kg of product at the design temperature (vs the 0.316
  // theoretical at NTP) — 15 kg/h × 0.362 = 5.43 Nm³/h, matching the P&ID.
  // INSTANTANEOUS H2 generation = present whenever electrolysis is energised.
  const H2_RATIO_Nm3_per_kg = 0.362;
  const H2_Nm3h = cl2_demand_kgh_inst * H2_RATIO_Nm3_per_kg;
  const dilutionAir_Nm3h = H2_Nm3h * p.vent_dilution_factor;
  const vent_H2_pc_vv = (H2_Nm3h + dilutionAir_Nm3h) > 0
    ? (H2_Nm3h / (H2_Nm3h + dilutionAir_Nm3h)) * 100
    : 0;

  // ---- BLOWER SIZING (H2 dilution) ----
  // Recommended Nm3/h = dilution air x safety factor
  const blowerFlow_Nm3h_each_duty = p.blower_duty > 0
    ? (dilutionAir_Nm3h * p.blower_safety_factor) / p.blower_duty
    : dilutionAir_Nm3h * p.blower_safety_factor;
  // Actual (working condition) m3/h: density correction Nm3 -> m3 at working T and P
  // V_actual = V_normal x (T_work_K / 273.15) x (1013 / P_work_mbar)
  const Tk = p.workingTemp_C + 273.15;
  const densityCorr = (Tk / 273.15) * (1013 / Math.max(500, p.workingPressure_mbar_atm));
  const blowerFlow_actual_m3h_each = blowerFlow_Nm3h_each_duty * densityCorr;
  const blowerFlow_total_actual_m3h = blowerFlow_actual_m3h_each * p.blower_duty;
  // Blower shaft power (adiabatic approximation, assumed cold-air blower):
  // P (kW) = (Q_actual_m3s) * dP_Pa / eta_blower  ; eta ~ 0.55 for low-pressure side-channel blowers
  const Q_m3s_each = blowerFlow_actual_m3h_each / 3600;
  const dP_Pa = p.blower_static_mbar * 100; // mbar -> Pa
  const blower_motor_kW_each = (Q_m3s_each * dP_Pa) / (0.55 * 1000);

  // ---- BLOWER TIMING ----
  // H2 is generated whenever the rectifier is energised.  Blower run schedule:
  //   continuous duty  : 24 h/d minus purge already covered
  //   shock duty       : (shockHoursPerDay + 2 x purge) hours per day
  const blower_run_hours_per_day =
    p.dutyMode === 'continuous'
      ? 24
      : p.dutyMode === 'cyclic'
        // One purge per ON-segment; cycles/day = 1440 / total cycle minutes
        ? Math.min(24, dailyDuty_h + (1440 / cycleTotalMin) * (2 * p.blower_purge_min / 60))
        : Math.min(24, p.shockHoursPerDay + 2 * (p.blower_purge_min / 60));
  // Pre-energise and post-shutdown purge for safety - re-stated as a separate value
  const blower_purge_total_min = 2 * p.blower_purge_min;

  // ---- DOSING DUTY (once-through, vendor QKQ2831 basis) ----
  // Mass of available chlorine to dose into the process water, then the NaOCl
  // solution volume needed at the delivered (available-Cl) concentration:
  //   mass = Q_process × ppm           (g/h, since 1 ppm ≈ 1 g/m³)
  //   Q_sol = mass / conc_delivered     (m³/h, conc in g/m³ = mg/L)
  // e.g. 4652 m³/h × 1.5 ppm ÷ 300 mg/L = 23.3 m³/h.  (Product max is 1000 mg/L;
  // the delivered/diluted basis sets the pump duty — see the dosing inputs.)
  const dose_mass_kgh = (p.dosing_process_flow_m3h * p.dosing_target_ppm) / 1000;
  const dosingPumpFlow_total_demand_Lh = p.dosing_delivered_conc_mgL > 0
    ? (dose_mass_kgh * 1e6) / p.dosing_delivered_conc_mgL   // kg/h→mg/h ÷ (mg/L) = L/h
    : 0;
  // User-set per-duty capacity (L/h) overrides the derived demand if > 0
  const dosingPumpFlow_per_duty_Lh = p.dosingPump_capacity_Lh_each > 0
    ? p.dosingPump_capacity_Lh_each
    : (p.dosingPumps_duty > 0 ? dosingPumpFlow_total_demand_Lh / p.dosingPumps_duty : dosingPumpFlow_total_demand_Lh);
  // Installed total = per-pump capacity x duty count
  const dosingPumpFlow_total_Lh = dosingPumpFlow_per_duty_Lh * p.dosingPumps_duty;
  // Margin vs demand
  const dosing_margin_pc = dosingPumpFlow_total_demand_Lh > 0
    ? ((dosingPumpFlow_total_Lh / dosingPumpFlow_total_demand_Lh) - 1) * 100
    : 0;
  // Pump pressure = discharge head (bar -> Pa) + system static pressure (mbar -> Pa)
  // Pump (hydraulic→shaft) efficiency 0.67 per the vendor pump-selection sheets.
  const dosing_dP_Pa = p.dosingPumpHead_bar * 1e5 + p.dosingPump_staticPressure_mbar * 100;
  const dosingPump_kW_each = (dosingPumpFlow_per_duty_Lh / 1000 / 3600) * dosing_dP_Pa / 0.67 / 1000;
  const storageTank_m3 = (dosingPumpFlow_total_Lh / 1000) * p.storageTank_h;

  const acid = ACID_PROPS[p.acidType];
  const wash_volume_per_event_m3 = Math.max(0.5, p.acidWashTank_m3);
  const active_acid_kg_per_event = wash_volume_per_event_m3 * (p.acidWashConc_target_pc / 100) * 1000;
  const concentrate_m3_per_event = active_acid_kg_per_event / (acid.activeFrac * acid.sg);
  const events_per_year = 365 / Math.max(1, p.acidWashEvery_d);
  const concentrate_m3_per_year = concentrate_m3_per_event * events_per_year;
  const concentrate_kg_per_year = concentrate_m3_per_year * acid.sg;
  const acidPump_total_flow_Lh = (wash_volume_per_event_m3 * 1000) / (Math.max(1, p.acidWashDuration_min) / 60);
  const acidPump_per_duty_Lh = p.acidPumps_duty > 0
    ? acidPump_total_flow_Lh / p.acidPumps_duty
    : acidPump_total_flow_Lh;
  const acid_dP_Pa = p.acidPumpHead_bar * 1e5 + p.acidPump_staticPressure_mbar * 100;
  const acidPump_kW_each = (acidPump_per_duty_Lh / 1000 / 3600) * acid_dP_Pa / 0.55 / 1000;

  const cell_x_section_m2 = p.cells_per_module > 0
    ? (brineFlow_per_module_m3h / 3600) / Math.max(0.01, p.brineVelocity_ms)
    : 0;

  // ---- Cell-nameplate utilisation ----
  // Current / voltage utilisation = computed / rated
  // Utilisation compares the ACTUAL per-series DC current (I_cell_A) to the
  // transformer-rectifier nameplate (e.g. 756 A), not the mass-equivalent total.
  const cell_current_util_pc = p.cell_ratedDC_current_A > 0
    ? (I_cell_A / p.cell_ratedDC_current_A) * 100
    : 0;
  const cell_voltage_util_pc = p.cell_ratedDC_voltage_V > 0
    ? (stackVoltage_V / p.cell_ratedDC_voltage_V) * 100
    : 0;
  const cell_capacity_util_pc = p.cell_ratedCapacity_kgCl2h > 0
    ? ((cl2_demand_kgh_inst / modules_running) / p.cell_ratedCapacity_kgCl2h) * 100
    : 0;

  // ---- Inlet / outlet pipe velocity check ----
  const inletPipe  = lookupDN(p.cell_inlet_DN);
  const outletPipe = lookupDN(p.cell_outlet_DN);
  const inletArea_m2  = Math.PI / 4 * (inletPipe.id_mm  / 1000) ** 2;
  const outletArea_m2 = Math.PI / 4 * (outletPipe.id_mm / 1000) ** 2;
  const Q_per_module_m3s = brineFlow_per_module_m3h / 3600;
  const inlet_vel_ms  = inletArea_m2  > 0 ? Q_per_module_m3s / inletArea_m2  : 0;
  const outlet_vel_ms = outletArea_m2 > 0 ? Q_per_module_m3s / outletArea_m2 : 0;
  const recommended_inlet_DN  = recommendDN(brineFlow_per_module_m3h, 1.5);
  const recommended_outlet_DN = recommendDN(brineFlow_per_module_m3h, 1.2);

  // ---- Operating pressure band ----
  const pressure_in_band = p.cell_operatingPressure_barg >= 0.5 && p.cell_operatingPressure_barg <= 3.0;

  // ---- Calculated rated DC current (Faraday) ----
  // I_rated = (Cl2 per module x 1000) / (1.3245 g/Ah x CE) x 1.15 design margin
  // Rounded to nearest 50 A for nameplate-friendly value.
  const cl2_per_module_design_gh = (p.hypochloriteOutput_kgh * 1000) / Math.max(1, p.modules_installed);
  const rated_current_calc_A = Math.round(((cl2_per_module_design_gh / (CL2_PER_AH * (adjusted_CE_pc / 100))) * 1.15) / 50) * 50;
  // Calculated rated capacity at the user's chosen nameplate current
  const rated_capacity_calc_kgCl2h = (CL2_PER_AH * (adjusted_CE_pc / 100) * p.cell_ratedDC_current_A) / 1000;

  // ---- Dosing pump connection velocities ----
  const dosingPipeIn  = lookupDN(p.dosingPump_inlet_DN);
  const dosingPipeOut = lookupDN(p.dosingPump_outlet_DN);
  const dosing_Q_per_duty_m3s = dosingPumpFlow_per_duty_Lh / 1000 / 3600;
  const dosing_in_vel_ms  = dosing_Q_per_duty_m3s / (Math.PI / 4 * (dosingPipeIn.id_mm  / 1000) ** 2);
  const dosing_out_vel_ms = dosing_Q_per_duty_m3s / (Math.PI / 4 * (dosingPipeOut.id_mm / 1000) ** 2);
  const recommended_dosing_in_DN  = recommendDN(dosingPumpFlow_per_duty_Lh / 1000, 0.5);
  const recommended_dosing_out_DN = recommendDN(dosingPumpFlow_per_duty_Lh / 1000, 1.0);

  // ---- Blower connection velocities (uses ACTUAL m3/h at working condition) ----
  const blowerPipeIn  = lookupDN(p.blower_inlet_DN);
  const blowerPipeOut = lookupDN(p.blower_outlet_DN);
  const blower_Q_each_m3s = blowerFlow_actual_m3h_each / 3600;
  const blower_in_vel_ms  = blower_Q_each_m3s / (Math.PI / 4 * (blowerPipeIn.id_mm  / 1000) ** 2);
  const blower_out_vel_ms = blower_Q_each_m3s / (Math.PI / 4 * (blowerPipeOut.id_mm / 1000) ** 2);
  const recommended_blower_in_DN  = recommendDN(blowerFlow_actual_m3h_each, 12);   // air intake 8 - 15 m/s
  const recommended_blower_out_DN = recommendDN(blowerFlow_actual_m3h_each, 10);   // discharge 8 - 12 m/s

  // ---- Acid pump connection velocities ----
  const acidPipeIn  = lookupDN(p.acidPump_inlet_DN);
  const acidPipeOut = lookupDN(p.acidPump_outlet_DN);
  const acid_Q_per_duty_m3s = acidPump_per_duty_Lh / 1000 / 3600;
  const acid_in_vel_ms  = acid_Q_per_duty_m3s / (Math.PI / 4 * (acidPipeIn.id_mm  / 1000) ** 2);
  const acid_out_vel_ms = acid_Q_per_duty_m3s / (Math.PI / 4 * (acidPipeOut.id_mm / 1000) ** 2);
  const recommended_acid_in_DN  = recommendDN(acidPump_per_duty_Lh / 1000, 0.5);
  const recommended_acid_out_DN = recommendDN(acidPump_per_duty_Lh / 1000, 1.0);

  // ---- Seawater pre-filter sizing (everything derived from PD-off inflow) ----
  const filter_flow_m3h = p.seawaterFlow_PDoff_m3h; // sized to MAX plant flow
  const filter_area_m2  = filter_flow_m3h / Math.max(1, p.filter_rate_m3_m2_h);
  // Velocity at inlet / outlet on chosen DN
  const filterPipeIn  = lookupDN(p.filter_inlet_DN);
  const filterPipeOut = lookupDN(p.filter_outlet_DN);
  const filter_in_vel  = (filter_flow_m3h / 3600) / (Math.PI / 4 * (filterPipeIn.id_mm  / 1000) ** 2);
  const filter_out_vel = (filter_flow_m3h / 3600) / (Math.PI / 4 * (filterPipeOut.id_mm / 1000) ** 2);
  // Recommended pipe DN at 2 m/s seawater design velocity
  const recommended_filter_in_DN  = recommendDN(filter_flow_m3h, 2.0);
  const recommended_filter_out_DN = recommendDN(filter_flow_m3h, 2.0);
  // Backwash flow envelope: typically 2 - 5 % of forward flow per cycle
  const filter_backwash_m3h = filter_flow_m3h * 0.04;
  // Number of filters: 1 duty + 1 standby (matches preset)
  const filter_units_duty    = 1;
  const filter_units_standby = 1;

  // ---- Eductor connection velocities (standard motive/suction/discharge) ----
  const eductorMotivePipe    = lookupDN(p.eductor_motive_DN);
  const eductorSuctionPipe   = lookupDN(p.eductor_suction_DN);
  const eductorDischargePipe = lookupDN(p.eductor_discharge_DN);
  const eductor_motive_vel    = (p.eductor_motive_m3h / 3600)        / (Math.PI / 4 * (eductorMotivePipe.id_mm    / 1000) ** 2);
  const eductor_suction_vel   = (p.eductor_suction_Lh / 1000 / 3600) / (Math.PI / 4 * (eductorSuctionPipe.id_mm   / 1000) ** 2);
  const eductor_discharge_m3h = p.eductor_motive_m3h + p.eductor_suction_Lh / 1000;
  const eductor_discharge_vel = (eductor_discharge_m3h / 3600)       / (Math.PI / 4 * (eductorDischargePipe.id_mm / 1000) ** 2);
  // Motive : suction ratio - typical eductor envelope 4 : 1 to 8 : 1
  const eductor_ratio = p.eductor_suction_Lh > 0
    ? p.eductor_motive_m3h / (p.eductor_suction_Lh / 1000)
    : 0;
  const eductor_ratio_ok = eductor_ratio >= 3 && eductor_ratio <= 10;

  // ---- Tank gas-space (degassing residence + freeboard) ----
  const storage_gas_volume_m3 = p.storage_tank_volume_m3 * (p.storage_freeboard_pc / 100);
  // Degassing-tank H2 disengagement: actual residence available vs required
  const storage_actual_residence_min = (p.storage_tank_volume_m3 / Math.max(0.01, p.electrolyserInlet_m3h)) * 60;
  const storage_degassing_ok = storage_actual_residence_min >= p.storage_degassing_residence_min;

  // Engineering bands widened to cover both common design points: modern high-CD cells
  // AND the conservative vendor (QKQ2831) basis used here (1.5 kA/m², 0.75 design Faraday
  // eff, Arabian-Gulf seawater ~44 ppt). Values outside still flag; valid vendor designs do not.
  const ce_in_band = p.currentEfficiency_pc >= 70 && p.currentEfficiency_pc <= 96;
  const cd_in_band = p.currentDensity_kAm2 >= 1.0 && p.currentDensity_kAm2 <= 4.0;
  const v_in_band  = p.brineVelocity_ms >= 0.4 && p.brineVelocity_ms <= 0.8;
  const vent_safe  = vent_H2_pc_vv <= 1.0;
  const dilution_ok = p.vent_dilution_factor >= 30;
  const temp_in_band = p.temperature_C >= 10 && p.temperature_C <= 35;
  const sal_in_band  = p.salinity_ppt  >= 28 && p.salinity_ppt  <= 50;   // open ocean ~35; Arabian Gulf ~44–47

  return {
    rho, cond,
    freeCl2_dose_mgL, brineFlow_per_module_m3h,
    adjusted_CE_pc, Cl_factor, totalHardness_mgL, recommendedWashInterval_d,
    bromate_risk, neutralisation_NaOH_kg_per_event_basis,
    cl2_demand_kgh: cl2_demand_kgh_inst, cl2_demand_kgd, cl2_demand_kgy,
    I_per_module_A, I_cell_A, totalCurrent_A,
    cellArea_m2_per_cell, cellArea_total_m2,
    stackVoltage_V, power_per_module_kW, power_total_DC_kW, power_total_AC_kW,
    specificEnergy_kWh_per_kgCl2,
    naocl_pc_w, naocl_mgL, totalBrineFlow_m3h,
    H2_Nm3h, dilutionAir_Nm3h, vent_H2_pc_vv,
    blowerFlow_Nm3h_each_duty, blowerFlow_actual_m3h_each, blowerFlow_total_actual_m3h,
    blower_motor_kW_each, blower_run_hours_per_day, blower_purge_total_min,
    densityCorr,
    dosingPumpFlow_total_Lh, dosingPumpFlow_per_duty_Lh, dosingPump_kW_each,
    storageTank_m3,
    wash_volume_per_event_m3, active_acid_kg_per_event,
    concentrate_m3_per_event, concentrate_m3_per_year, concentrate_kg_per_year,
    events_per_year,
    acidPump_total_flow_Lh, acidPump_per_duty_Lh, acidPump_kW_each,
    cell_x_section_m2,
    cell_current_util_pc, cell_voltage_util_pc, cell_capacity_util_pc,
    rated_current_calc_A, rated_capacity_calc_kgCl2h,
    inletPipe, outletPipe, inlet_vel_ms, outlet_vel_ms,
    recommended_inlet_DN, recommended_outlet_DN, pressure_in_band,
    dosingPumpFlow_total_demand_Lh, dosing_margin_pc,
    dosing_in_vel_ms, dosing_out_vel_ms, recommended_dosing_in_DN, recommended_dosing_out_DN,
    blower_in_vel_ms, blower_out_vel_ms, recommended_blower_in_DN, recommended_blower_out_DN,
    acid_in_vel_ms, acid_out_vel_ms, recommended_acid_in_DN, recommended_acid_out_DN,
    filter_flow_m3h, filter_area_m2, filter_in_vel, filter_out_vel,
    recommended_filter_in_DN, recommended_filter_out_DN,
    filter_backwash_m3h, filter_units_duty, filter_units_standby,
    eductor_motive_vel, eductor_suction_vel, eductor_discharge_vel,
    eductor_discharge_m3h, eductor_ratio, eductor_ratio_ok,
    storage_gas_volume_m3, storage_actual_residence_min, storage_degassing_ok,
    txr_eff,
    ce_in_band, cd_in_band, v_in_band, vent_safe, dilution_ok,
    temp_in_band, sal_in_band, dailyDuty_h,
  };
}

// ---------------------------------------------------------------------------
// Degassing hypochlorite storage tank — vessel design + API 2000 breathing
// ---------------------------------------------------------------------------
// Physics:
//  • Vessel = vertical cylinder; working volume from degassing residence (H2
//    disengagement) with a freeboard gas space; H = ratio × D.
//  • H2 is evolved with the NaOCl and continuously diluted by forced blower air
//    to < 1 % v/v (= 25 % of the 4 % LEL).
//  • Tank "breathing" per API 2000: the vent must pass the OUT-breathing
//    (forced dilution air + H2 + liquid pump-in displacement + thermal expansion)
//    and admit the IN-breathing (liquid pump-out + thermal contraction, e.g. on a
//    blower trip) without exceeding the tank's design pressure / vacuum.
function degassingDesign(p: ECParams, r: ReturnType<typeof calcResults>) {
  // ─────────────────────────────────────────────────────────────────────────
  // Vendor sizing method — QatarEnergy report 4494-M2UT-6-12-0001 Rev.3.
  // Tank fills during PD-OFF (only electrolyser + antifoam in) and empties
  // during PD-ON (dosing out). Working volume = worst liquid swing + the 5-min
  // degassing reserve. Tank height is built from the nozzle/level stack + 20 %
  // HHLL margin + 0.5 m freeboard. Reproduces the issued T30X2 datasheet.
  // ─────────────────────────────────────────────────────────────────────────
  const A = Math.max(0.1, p.electrolyserInlet_m3h);                       // electrolyser inflow (m³/h)
  const B = Math.max(0, p.antifoam_flow_m3h);                             // antifoam inflow (m³/h)
  const F = Math.max(0, p.degas_min_min);                                 // H2 degassing residence (min)
  const G = (A * F) / 60;                                                 // degassing minimum volume (m³)

  // Working volume per sub-state: ON → H = E−C+G (draw-down); OFF → H = C−E+G (fill).
  const sub = (onMin: number, offMin: number, dose: number) => {
    const Con = (A + B) * (onMin / 60);                                   // accumulating during ON
    const Eon = dose * (onMin / 60);                                      // out during ON
    const Hon = Eon - Con + G;                                            // working vol (ON)
    const Coff = (A + B) * (offMin / 60);                                 // accumulating during OFF
    const Hoff = Coff + G;                                                // working vol (OFF, E=0)
    return { Con, Eon, Hon, Coff, Hoff };
  };
  const c1 = sub(p.case1_on_min, p.case1_off_min, p.case1_dose_m3h);
  const c2 = sub(p.case2_on_min, p.case2_off_min, p.case2_dose_m3h);
  const cases = [
    { id: 'Case-1 ON',  H: c1.Hon },  { id: 'Case-1 OFF', H: c1.Hoff },
    { id: 'Case-2 ON',  H: c2.Hon },  { id: 'Case-2 OFF', H: c2.Hoff },
  ];
  const govern = cases.reduce((m, x) => (x.H > m.H ? x : m), cases[0]);   // worst-case working volume
  const Vwork  = govern.H;
  const flowIn  = A;
  const flowOut = Math.max(p.case1_dose_m3h, p.case2_dose_m3h);           // worst PD-ON outlet draw

  // ── Geometry (level stack, m) ──
  const D    = p.tank_diameter_mm / 1000;                                 // m
  const area = Math.PI / 4 * D * D;                                       // m²
  const M    = Vwork / area;                                             // Low→High swing height (m)
  const I = p.nozzle_bottom_I_mm / 1000, J = p.nozzle_dia_J_mm / 1000;
  const K = p.nozzle_brim_K_mm / 1000,  L = p.deadband_L_mm / 1000;
  const N = I + J + K + L + M;                                            // bottom → High level
  const O = N * (1 + p.hhll_margin_pc / 100);                            // High-High level (with margin)
  const Ptop = p.freeboard_top_mm / 1000;
  const H = O + Ptop;                                                     // total tank height (T/L)
  const Vtotal = area * H;
  // Derived level setpoints (mm) — reproduce the datasheet "Level Settings" table
  const lvl_LLLL = (I + J + K) * 1000;
  const lvl_LLL  = lvl_LLLL + L * 1000;
  const lvl_HLL  = lvl_LLL + M * 1000;
  const lvl_HHLL = O * 1000;
  const topMM    = H * 1000;
  const Vheel  = area * lvl_LLL / 1000;                                   // liquid heel below Low
  const Vvapor = area * Ptop;                                             // gas space above HHLL
  const HD     = H / Math.max(0.01, D);
  const degassResidenceActual = (Vwork / A) * 60;                        // min (working / inflow)
  const Vdegas5 = G;                                                     // 5-min degassing volume
  const Vdegass15 = A * 15 / 60;                                         // reference 15-min
  const Vdegass20 = A * 20 / 60;                                         // reference 20-min
  const riseVel_mmps = (A / 3600) / area * 1000;

  // ── H2 generation & dilution ──
  const H2  = r.H2_Nm3h;                                                  // Nm³/h
  const air = r.dilutionAir_Nm3h;                                         // Nm³/h forced in by blower
  const ventH2pct = (H2 + air) > 0 ? (H2 / (H2 + air)) * 100 : 0;         // % v/v in the vent
  const lelPct = ventH2pct / 4 * 100;                                     // % of the 4 % LEL
  const h2ToLEL_min = H2 > 0 ? (Vvapor * 0.04) / (H2 / 60) : Infinity;    // time to 4% in headspace if blower trips

  // ── API 2000 normal venting (thermal) — small at this size ──
  const Vtherm_out = 0.32 * Math.pow(Vtotal, 0.9);                        // Nm³/h air out (heating)
  const Vtherm_in  = 0.25 * Math.pow(Vtotal, 0.7);                        // Nm³/h air in (cooling)

  // ── Vent duty (phase-dependent) ──
  // The cells run CONTINUOUSLY (vendor table: inflow A = 15 m³/h during both PD ON and
  // OFF), so H2 and the blower dilution air are constant — the forced sweep is the same
  // in ON and OFF. The pulse-dosing cycle only moves the LIQUID LEVEL, adding a small
  // gas displacement: during PD OFF the tank FILLS (dosing off), the rising liquid pushes
  // extra gas out → vent PEAKS. During PD ON the tank empties and the vent is lower.
  const forcedVent   = air + H2;                                         // continuous forced gas sweep (Nm³/h)
  const fillRate     = flowIn + p.antifoam_flow_m3h;                     // liquid rise during PD OFF (dosing off) = A+B
  const ventFill     = forcedVent + fillRate + Vtherm_out;              // PD-OFF (filling) = WORST case
  const ventDose     = forcedVent - Math.max(0, flowOut - fillRate);    // PD-ON (emptying) = lower
  const normalBreath = fillRate + Vtherm_out;                           // API 2000 normal out-breathing (the displacement part)
  const outBreath    = ventFill;                                         // size the vent for the PD-OFF peak
  // ── IN-breathing (vacuum, e.g. blower trip while pumping out): pump-out + thermal in ──
  const inBreath  = flowOut + Vtherm_in;                                  // Nm³/h

  // ── Vent line sizing (governing = out-breathing) ──
  const ventQ_m3s = outBreath / 3600;
  const ventArea  = ventQ_m3s / Math.max(1, p.vent_velocity_target_ms);
  const ventD_req_mm = Math.sqrt(4 * ventArea / Math.PI) * 1000;
  const ventDN   = STD_PIPES.find(s => s.id_mm >= ventD_req_mm)?.dn ?? STD_PIPES[STD_PIPES.length - 1].dn;
  const ventPipe = lookupDN(ventDN);
  const ventVel  = ventQ_m3s / (Math.PI / 4 * (ventPipe.id_mm / 1000) ** 2);
  const dP_vent_mbar = (4 * 0.5 * 1.2 * ventVel * ventVel) / 100;         // K≈4 (gooseneck+bends), ρ_air 1.2

  // ── PRESSURE / VACUUM EVENTS ──────────────────────────────────────────────
  const kRho = 4 * 0.5 * 1.2;                                            // K·½·ρ for air loss
  // (a) Normal operating pressure = the vent ΔP while the blower sweeps (positive).
  const opPressure_mbar = dP_vent_mbar;
  // (b) Worst PRESSURE: vent blocks while the blower runs → it deadheads into the tank.
  const blowerDeadhead_mbar = 500;                                      // blower 0.5 barg (datasheet)
  // (c) Worst VACUUM (credible): blower trips while dosing keeps pumping out, vent OPEN —
  //     air is drawn in through the open vent; the suction is tiny.
  const vacOpenVent_mbar = (kRho * Math.pow((inBreath / 3600) / (Math.PI / 4 * (ventPipe.id_mm / 1000) ** 2), 2)) / 100;
  // (d) Worst VACUUM (fault-on-fault): blower trip + vent ALSO blocked + pump-out →
  //     pressure falls toward seawater vapour pressure ≈ collapse, unless a breaker opens.
  const collapseVac_mbar = 32 - 1013;                                   // ~ -981 mbar gauge
  // Vacuum breaker sized to admit the in-breathing at the allowable design vacuum.
  const vacBrkVel = Math.sqrt(Math.max(1, p.tank_design_vacuum_mbar) * 100 / kRho);
  const vacBrkD_mm = Math.sqrt(4 * ((inBreath / 3600) / Math.max(0.1, vacBrkVel)) / Math.PI) * 1000;
  const vacDN = STD_PIPES.find(s => s.id_mm >= vacBrkD_mm)?.dn ?? STD_PIPES[STD_PIPES.length - 1].dn;
  const pressureReliefNeeded = blowerDeadhead_mbar > p.tank_design_pressure_mbar;

  // ── Verdicts ──
  const ventOk   = dP_vent_mbar <= p.tank_design_pressure_mbar;
  const h2Ok     = ventH2pct < 1.0;
  const degassOk = degassResidenceActual >= p.degas_min_min;             // working residence ≥ 5-min minimum
  const heightOk = topMM <= p.tank_max_height_mm;                        // fits under the head-room limit
  // diameter needed to just meet the height limit (widen the tank, same working volume)
  const dForHeight_mm = (() => {
    const Mmax = (p.tank_max_height_mm / 1000 - Ptop) / (1 + p.hhll_margin_pc / 100) - (I + J + K + L);
    if (Mmax <= 0) return NaN;
    const areaMin = Vwork / Mmax;
    return Math.sqrt(4 * areaMin / Math.PI) * 1000;
  })();

  return { flowIn, flowOut, Vwork, Vtotal, Vvapor, Vheel, Vdegas5, Vdegass15, Vdegass20,
    degassResidenceActual, D, H, HD, area, topMM, riseVel_mmps, heightOk, dForHeight_mm,
    A, B, F, G, c1, c2, govern, M, N, O, Ptop, I, J, K, L,
    lvl_LLLL, lvl_LLL, lvl_HLL, lvl_HHLL,
    H2, air, ventH2pct, lelPct, h2ToLEL_min, Vtherm_out, Vtherm_in,
    forcedVent, fillRate, ventFill, ventDose, normalBreath, outBreath, inBreath,
    ventD_req_mm, ventDN, ventVel, dP_vent_mbar, vacDN, ventOk, h2Ok, degassOk,
    opPressure_mbar, blowerDeadhead_mbar, vacOpenVent_mbar, collapseVac_mbar, vacBrkD_mm, pressureReliefNeeded };
}

type Crit = { label: string; value: string; limit: string; pass: boolean; critical?: boolean };

function validate(p: ECParams, r: ReturnType<typeof calcResults>): Crit[] {
  return [
    { label: 'Seawater salinity',          value: `${p.salinity_ppt.toFixed(1)} ppt`,            limit: '28 - 50 ppt',           pass: r.sal_in_band, critical: true },
    { label: 'Seawater temperature',       value: `${p.temperature_C.toFixed(1)} C`,             limit: '10 - 35 C',             pass: r.temp_in_band, critical: true },
    { label: 'Current density',            value: `${p.currentDensity_kAm2.toFixed(2)} kA/m^2`,  limit: '1.0 - 4.0',             pass: r.cd_in_band, critical: true },
    { label: 'Cell voltage',               value: `${p.cellVoltage_V.toFixed(2)} V`,             limit: '3.5 - 4.5 V',           pass: p.cellVoltage_V >= 3.5 && p.cellVoltage_V <= 4.5 },
    { label: 'Current efficiency',         value: `${p.currentEfficiency_pc.toFixed(0)} %`,      limit: '70 - 96 % (design)',    pass: r.ce_in_band, critical: true },
    { label: 'Brine velocity',             value: `${p.brineVelocity_ms.toFixed(2)} m/s`,        limit: '0.4 - 0.8 m/s',         pass: r.v_in_band, critical: true },
    { label: 'NaOCl product concentration', value: `${r.naocl_mgL.toFixed(0)} mg/L`,              limit: '≈ 1000 mg/L (P&ID)',    pass: r.naocl_mgL >= 500 && r.naocl_mgL <= 2000 },
    { label: 'Vent H2 fraction',           value: `${r.vent_H2_pc_vv.toFixed(2)} % v/v`,         limit: '< 1.0 % (25 % of LEL)', pass: r.vent_safe, critical: true },
    { label: 'H2 dilution ratio',          value: `${p.vent_dilution_factor.toFixed(0)} : 1`,    limit: '>= 30 : 1',             pass: r.dilution_ok, critical: true },
    { label: 'Modules redundancy',         value: `${p.modules_installed} + ${p.modules_standby}`, limit: '>= 1 standby',        pass: p.modules_standby >= 1 },
    { label: 'Dosing pumps redundancy',    value: `${p.dosingPumps_duty} + ${p.dosingPumps_standby}`, limit: '>= 1 standby',     pass: p.dosingPumps_standby >= 1 },
    { label: 'Acid pumps redundancy',      value: `${p.acidPumps_duty} + ${p.acidPumps_standby}`, limit: '>= 1 standby',         pass: p.acidPumps_standby >= 1 },
    { label: 'H2 dilution blowers redundancy', value: `${p.blower_duty} + ${p.blower_standby}`,  limit: '>= 1 standby',          pass: p.blower_standby >= 1, critical: true },
    { label: 'Blower safety factor',       value: `x ${p.blower_safety_factor.toFixed(2)}`,      limit: '1.0 - 1.5',             pass: p.blower_safety_factor >= 1.0 && p.blower_safety_factor <= 1.5 },
    { label: 'Working temperature',        value: `${p.workingTemp_C.toFixed(1)} C`,             limit: '5 - 45 C',              pass: p.workingTemp_C >= 5 && p.workingTemp_C <= 45 },
    { label: 'Blower static pressure',     value: `${p.blower_static_mbar.toFixed(0)} mbar`,     limit: '20 - 200 mbar',         pass: p.blower_static_mbar >= 20 && p.blower_static_mbar <= 200 },
    { label: 'Cell current utilisation',   value: `${r.cell_current_util_pc.toFixed(0)} %`,      limit: '60 - 100 % of rated',   pass: r.cell_current_util_pc >= 60 && r.cell_current_util_pc <= 105, critical: true },
    { label: 'Cell voltage utilisation',   value: `${r.cell_voltage_util_pc.toFixed(0)} %`,      limit: '70 - 105 % of rated',   pass: r.cell_voltage_util_pc >= 70 && r.cell_voltage_util_pc <= 105 },
    { label: 'Cell capacity utilisation',  value: `${r.cell_capacity_util_pc.toFixed(0)} %`,     limit: '50 - 100 % of rated',   pass: r.cell_capacity_util_pc >= 50 && r.cell_capacity_util_pc <= 105, critical: true },
    { label: 'Operating pressure',         value: `${p.cell_operatingPressure_barg.toFixed(2)} barg`, limit: '0.5 - 3.0 barg',   pass: r.pressure_in_band, critical: true },
    { label: 'Inlet pipe velocity',        value: `${r.inlet_vel_ms.toFixed(2)} m/s`,            limit: '0.4 - 1.5 m/s',         pass: r.inlet_vel_ms >= 0.4 && r.inlet_vel_ms <= 1.5 },
    { label: 'Outlet pipe velocity',       value: `${r.outlet_vel_ms.toFixed(2)} m/s`,           limit: '0.3 - 1.2 m/s',         pass: r.outlet_vel_ms >= 0.3 && r.outlet_vel_ms <= 1.2 },
    { label: 'NaOCl storage residence',    value: `${p.storageTank_h.toFixed(1)} h`,             limit: '1 - 4 h',               pass: p.storageTank_h >= 1 && p.storageTank_h <= 4 },
    { label: 'Acid wash interval',         value: `every ${p.acidWashEvery_d} d`,                limit: '14 - 60 d',             pass: p.acidWashEvery_d >= 14 && p.acidWashEvery_d <= 60 },
    { label: 'Acid wash concentration',    value: `${p.acidWashConc_target_pc.toFixed(1)} % w/w`,limit: '3 - 10 %',              pass: p.acidWashConc_target_pc >= 3 && p.acidWashConc_target_pc <= 10 },
  ];
}

// ---------------------------------------------------------------------------
// Small UI helpers
// ---------------------------------------------------------------------------
function NumField(props: { label: string; value: number; step?: number; min?: number; max?: number; suffix?: string; onChange: (v: number) => void; help?: string; }) {
  const hasRange = props.min !== undefined && props.max !== undefined;
  const v = Number.isFinite(props.value) ? props.value : 0;
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-300">{props.label}</span>
        <span className="text-[10px] text-slate-500">
          {props.suffix && <span>{props.suffix} </span>}
          {hasRange && <span className="text-slate-600">[{props.min}-{props.max}]</span>}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {hasRange && (
          <input
            type="range"
            value={v}
            step={props.step ?? 0.1}
            min={props.min}
            max={props.max}
            onChange={e => props.onChange(parseFloat(e.target.value) || 0)}
            className="flex-1 accent-cyan-500 h-1.5"
          />
        )}
        <input
          type="number"
          value={v}
          step={props.step ?? 0.1}
          min={props.min}
          max={props.max}
          onChange={e => props.onChange(parseFloat(e.target.value) || 0)}
          className={`${hasRange ? 'w-20' : 'w-full'} px-2 py-1.5 rounded-md bg-slate-900/60 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500`}
        />
      </div>
      {props.help && <p className="text-[10px] text-slate-500 mt-0.5">{props.help}</p>}
    </label>
  );
}

function SelField<T extends string>(props: { label: string; value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-300 mb-1 block">{props.label}</span>
      <select
        value={props.value}
        onChange={e => props.onChange(e.target.value as T)}
        className="w-full px-3 py-1.5 rounded-md bg-slate-900/60 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500"
      >
        {props.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Section({ title, icon, children, action }: { title: string; icon: React.ReactNode; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <section className="rounded-xl bg-slate-900/40 border border-slate-700/60 p-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-cyan-300">{icon}{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function RecommendBtn({ onClick, label = 'Recommend' }: { onClick: () => void; label?: string }) {
  return (
    <button onClick={onClick}
      className="px-2 py-1 rounded-md text-[10.5px] font-semibold bg-cyan-600/20 border border-cyan-500/60 text-cyan-200 hover:bg-cyan-600/30 transition-colors inline-flex items-center gap-1">
      <Activity className="w-3 h-3" /> {label}
    </button>
  );
}

function KPI({ label, value, unit, tone = 'neutral' }: { label: string; value: string | number; unit?: string; tone?: 'neutral' | 'good' | 'warn' | 'bad'; }) {
  const toneCls = tone === 'good' ? 'border-emerald-500/40 text-emerald-300'
    : tone === 'warn' ? 'border-amber-500/40 text-amber-300'
    : tone === 'bad' ? 'border-rose-500/40 text-rose-300'
    : 'border-slate-600 text-slate-200';
  return (
    <div className={`rounded-lg bg-slate-900/50 border ${toneCls} p-3`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">{label}</div>
      <div className="text-lg font-semibold">{value}{unit && <span className="text-xs text-slate-400 ml-1">{unit}</span>}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Process flow diagram (SVG, live values)
// ---------------------------------------------------------------------------
function ProcessDiagram({ p, r }: { p: ECParams; r: ReturnType<typeof calcResults> }) {
  // Stream values
  const ventTone = r.vent_safe ? '#10b981' : '#f43f5e';
  const energyTone = r.specificEnergy_kWh_per_kgCl2 < 5.5 ? '#10b981' : r.specificEnergy_kWh_per_kgCl2 < 7 ? '#f59e0b' : '#f43f5e';

  // Helper for boxes — larger title, larger body, more vertical spacing per line.
  // `tag` overlays the P&ID equipment tag number in the header (Mesaieed replica).
  const Box = ({ x, y, w, h, title, lines, fill = '#0f172a', stroke = '#475569', tag }: {
    x: number; y: number; w: number; h: number; title: string; lines: string[]; fill?: string; stroke?: string; tag?: string;
  }) => (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={8} fill={fill} stroke={stroke} strokeWidth={1.8} />
      <rect x={x} y={y} width={w} height={20} rx={8} fill={stroke} fillOpacity={0.18} />
      <text x={x + w / 2} y={y + 14} fill={stroke} fontSize={13} fontWeight={700} textAnchor="middle" letterSpacing={0.4}>{title}</text>
      {tag && (
        <text x={x + w - 6} y={y + h - 5} fill="#fbbf24" fontSize={8.5} fontWeight={700} textAnchor="end" letterSpacing={0.2}>{tag}</text>
      )}
      {lines.map((l, i) => (
        <text key={i} x={x + w / 2} y={y + 36 + i * 14} fill="#e2e8f0" fontSize={12} textAnchor="middle">{l}</text>
      ))}
    </g>
  );

  const Arrow = ({ x1, y1, x2, y2, label, colour = '#64748b', dashed = false }: {
    x1: number; y1: number; x2: number; y2: number; label?: string; colour?: string; dashed?: boolean;
  }) => {
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;
    return (
      <g>
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={colour} strokeWidth={2.2}
          markerEnd="url(#arrow)" strokeDasharray={dashed ? '6 4' : undefined} />
        {label && (
          <text x={midX} y={midY - 5} fill={colour} fontSize={12} fontWeight={700} textAnchor="middle">{label}</text>
        )}
      </g>
    );
  };

  // ── P&ID symbol + callout helpers ──────────────────────────────────────
  // Live data sits in a callout BELOW each unit, joined by a leader line, so it
  // never overlaps the equipment symbol.
  const Tag = ({ x, y, t }: { x: number; y: number; t: string }) => (
    <text x={x} y={y} fill="#fbbf24" fontSize={9} fontWeight={700} textAnchor="middle" letterSpacing={0.2}>{t}</text>
  );
  const Callout = ({ cx, top, w, title, rows, stroke = '#3b4a5e' }: { cx: number; top: number; w: number; title: string; rows: string[]; stroke?: string }) => {
    const h = 21 + rows.length * 15;
    const x = cx - w / 2;
    return (
      <g>
        <line x1={cx} y1={top - 14} x2={cx} y2={top} stroke="#475569" strokeWidth={1} strokeDasharray="2 3" />
        <rect x={x} y={top} width={w} height={h} rx={6} fill="#0b1220" stroke={stroke} strokeWidth={1.1} />
        <text x={cx} y={top + 14} fill="#94a3b8" fontSize={10} fontWeight={700} textAnchor="middle">{title}</text>
        {rows.map((t, i) => (
          <text key={i} x={x + 9} y={top + 29 + i * 15} fill="#dbe4ee" fontSize={10.5}>{t}</text>
        ))}
      </g>
    );
  };
  const Pump = ({ cx, cy, stroke = '#10b981' }: { cx: number; cy: number; stroke?: string }) => (
    <g>
      <circle cx={cx} cy={cy} r={14} fill="#0f172a" stroke={stroke} strokeWidth={2} />
      <path d={`M ${cx - 5} ${cy - 7} L ${cx + 9} ${cy} L ${cx - 5} ${cy + 7} Z`} fill={stroke} fillOpacity={0.85} />
    </g>
  );
  // Live pipe-velocity badge sitting on a line — green inside the design band, amber outside.
  // Reacts to the DN dropdowns (velocity = flow ÷ selected pipe area).
  // Pipe badge — flow rate + chosen DN + resulting velocity for one equipment-to-
  // equipment line. Velocity = flow ÷ area(DN); the box is green inside 0.3–3 m/s.
  const PipeBadge = ({ x, y, flow, unit, dn, v, label, count, band = [0.3, 3.0] }: { x: number; y: number; flow: number; unit: string; dn: number | string; v: number; label?: string; count?: number; band?: [number, number] }) => {
    const ok = v >= band[0] && v <= band[1];
    const c = ok ? '#34d399' : '#fbbf24';
    const flowStr = flow < 100 ? flow.toFixed(2) : flow.toFixed(0);
    return (
      <g>
        {label && <text x={x} y={y - 19} fill="#8696ab" fontSize={9} fontWeight={700} textAnchor="middle">{label}{count && count > 1 ? ` ×${count}` : ''}</text>}
        <rect x={x - 47} y={y - 13} width={94} height={28} rx={5} fill="#0b1220" stroke={c} strokeWidth={1} />
        <text x={x} y={y - 1} fill="#dbe4ee" fontSize={10} fontWeight={700} textAnchor="middle">{flowStr} {unit}</text>
        <text x={x} y={y + 11} fill={c} fontSize={9.5} fontWeight={700} textAnchor="middle">DN{dn} · {v.toFixed(2)} m/s</text>
      </g>
    );
  };

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox="0 0 1560 540" className="w-full h-auto" style={{ minWidth: 1100 }}
           preserveAspectRatio="xMidYMid meet">
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" />
          </marker>
          <linearGradient id="brineGrad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#0ea5e9" />
            <stop offset="100%" stopColor="#22d3ee" />
          </linearGradient>
        </defs>

        {/* Title strip */}
        <text x={780} y={24} fill="#cbd5e1" fontSize={16} fontWeight={700} textAnchor="middle" letterSpacing={1.2}>
          SEAWATER ELECTROCHLORINATION — P&amp;ID (Mesaieed, 4737-M2UT-6-51-0001)
        </text>
        <text x={780} y={43} fill="#64748b" fontSize={11} textAnchor="middle" letterSpacing={0.5}>
          Equipment laid out as the issued P&amp;ID · live data in the callouts below each unit
        </text>
        {/* ===== process header (main line, cy = 230) ===== */}
        <line x1={175} y1={230} x2={1500} y2={230} stroke="#1f2d3d" strokeWidth={11} strokeLinecap="round" />

        {/* 1. SEAWATER FEED (cx 130) */}
        <g>
          <rect x={85} y={198} width={90} height={66} rx={4} fill="#082f49" stroke="#0ea5e9" strokeWidth={2} />
          <path d="M 94 218 q 10 -7 20 0 t 20 0 t 20 0" fill="none" stroke="#38bdf8" strokeWidth={1.6} />
          <path d="M 94 231 q 10 -7 20 0 t 20 0 t 20 0" fill="none" stroke="#38bdf8" strokeWidth={1.6} />
          <path d="M 94 244 q 10 -7 20 0 t 20 0 t 20 0" fill="none" stroke="#38bdf8" strokeWidth={1.6} />
          <text x={130} y={188} fill="#7dd3fc" fontSize={12} fontWeight={700} textAnchor="middle">Seawater feed</text>
        </g>

        {/* 2. STRAINER (cx 380) */}
        <g>
          <rect x={335} y={200} width={90} height={60} rx={4} fill="#0f172a" stroke="#38bdf8" strokeWidth={2} />
          <line x1={341} y1={260} x2={419} y2={200} stroke="#38bdf8" strokeWidth={1.2} />
          <line x1={355} y1={260} x2={419} y2={214} stroke="#38bdf8" strokeWidth={1.2} />
          <line x1={369} y1={260} x2={419} y2={228} stroke="#38bdf8" strokeWidth={1.2} />
          <text x={380} y={190} fill="#7dd3fc" fontSize={12} fontWeight={700} textAnchor="middle">Seawater filters</text>
          <Tag x={380} y={276} t={MESAIEED_TAGS.filters} />
        </g>

        {/* Seawater inlet PCV (pressure control valve) */}
        <g>
          <path d="M 200 224 L 214 230 L 200 236 Z M 228 224 L 214 230 L 228 236 Z" fill="none" stroke="#94a3b8" strokeWidth={1.6} />
          <line x1={214} y1={224} x2={214} y2={216} stroke="#94a3b8" strokeWidth={1.4} />
          <Tag x={214} y={250} t={MESAIEED_TAGS.pcv} />
        </g>

        {/* In-line static mixer SM-X1A/B (seawater → cells) */}
        <g>
          <rect x={573} y={221} width={36} height={18} rx={3} fill="#0f172a" stroke="#7dd3fc" strokeWidth={1.6} />
          <path d="M 577 230 l 6 -5 l 6 10 l 6 -10 l 6 10 l 4 -5" fill="none" stroke="#7dd3fc" strokeWidth={1.3} />
          <Tag x={585} y={274} t={MESAIEED_TAGS.mixerIn} />
        </g>

        {/* 3. ELECTROCHLORINATION CELLS A/B (cx 680) */}
        <g>
          {[0, 1].map(i => (
            <g key={i}>
              <rect x={615} y={198 + i * 34} width={130} height={30} rx={3} fill="#082f49" stroke="#22d3ee" strokeWidth={2} />
              {[0, 1, 2, 3, 4, 5].map(k => (
                <line key={k} x1={628 + k * 20} y1={202 + i * 34} x2={628 + k * 20} y2={224 + i * 34} stroke="#22d3ee" strokeWidth={1} strokeOpacity={0.55} />
              ))}
              <text x={609} y={218 + i * 34} fill="#67e8f9" fontSize={9} fontWeight={700} textAnchor="end">{i === 0 ? 'A.W' : 'B.S'}</text>
            </g>
          ))}
          <text x={680} y={186} fill="#67e8f9" fontSize={12} fontWeight={700} textAnchor="middle">Electrochlorination cells</text>
          <Tag x={680} y={284} t={MESAIEED_TAGS.cells} />
        </g>

        {/* DC rectifier (above cells) */}
        <g>
          <rect x={620} y={108} width={120} height={42} rx={5} fill="#1f1d3a" stroke="#a78bfa" strokeWidth={2} />
          <text x={680} y={125} fill="#c4b5fd" fontSize={10.5} fontWeight={700} textAnchor="middle">Rectifier (T/R)</text>
          <text x={680} y={140} fill="#ddd6fe" fontSize={10} textAnchor="middle">{r.power_total_DC_kW.toFixed(0)} kW . {p.txr_AC_primary_V} VAC</text>
          <line x1={680} y1={150} x2={680} y2={198} stroke="#a78bfa" strokeWidth={2.2} markerEnd="url(#arrow)" strokeDasharray="5 3" />
          <text x={692} y={178} fill="#a78bfa" fontSize={9} fontWeight={700}>DC</text>
        </g>

        {/* 4. DEGASSING HYPOCHLORITE TANK (cx 1010) */}
        <g>
          <rect x={965} y={200} width={90} height={66} rx={3} fill="#062c1e" stroke="#10b981" strokeWidth={2} />
          <ellipse cx={1010} cy={200} rx={45} ry={8} fill="#062c1e" stroke="#10b981" strokeWidth={2} />
          <path d="M 1010 193 L 1010 168 Q 1010 158 1022 158 L 1034 158" fill="none" stroke={ventTone} strokeWidth={2} markerEnd="url(#arrow)" />
          <text x={1040} y={154} fill={ventTone} fontSize={9} fontWeight={700}>H2 vent {r.vent_H2_pc_vv.toFixed(2)}%</text>
          <text x={1010} y={186} fill="#6ee7b7" fontSize={12} fontWeight={700} textAnchor="middle">Degassing tank</text>
          <Tag x={1010} y={286} t={MESAIEED_TAGS.degasTank} />
        </g>

        {/* H2 dilution blowers + air filter (above tank) */}
        <g>
          <rect x={915} y={84} width={190} height={44} rx={5} fill="#3a0f1a" stroke={ventTone} strokeWidth={2} />
          <text x={1010} y={101} fill="#fca5a5" fontSize={10.5} fontWeight={700} textAnchor="middle">H2 dilution blowers + air filter</text>
          <text x={1010} y={117} fill="#fecaca" fontSize={10} textAnchor="middle">{r.dilutionAir_Nm3h.toFixed(0)} Nm3/h . {p.blower_duty}+{p.blower_standby}</text>
          <line x1={1000} y1={128} x2={995} y2={200} stroke={ventTone} strokeWidth={2.2} markerEnd="url(#arrow)" />
          <Tag x={1010} y={80} t={`${MESAIEED_TAGS.blowers} . ${MESAIEED_TAGS.airFilter}`} />
        </g>

        {/* 5. NaOCl DOSING PUMPS (cx 1320) */}
        <g>
          <Pump cx={1303} cy={230} stroke="#10b981" />
          <Pump cx={1338} cy={230} stroke="#34d399" />
          <text x={1320} y={190} fill="#6ee7b7" fontSize={12} fontWeight={700} textAnchor="middle">NaOCl dosing pumps</text>
          <text x={1320} y={262} fill="#94a3b8" fontSize={9} textAnchor="middle">{p.dosingPumps_duty}+{p.dosingPumps_standby} duty/standby</text>
          <Tag x={1320} y={278} t={MESAIEED_TAGS.dosingPumps} />
        </g>

        {/* In-line static mixer W30-X1 (dosing line) */}
        <g>
          <rect x={1390} y={221} width={36} height={18} rx={3} fill="#0f172a" stroke="#6ee7b7" strokeWidth={1.6} />
          <path d="M 1394 230 l 6 -5 l 6 10 l 6 -10 l 6 10 l 4 -5" fill="none" stroke="#6ee7b7" strokeWidth={1.3} />
          <Tag x={1408} y={215} t={MESAIEED_TAGS.mixerOut} />
        </g>

        {/* Drain pit T30-X3 + drain pump P-30X4A/B (collects filter / tank / supply drains) */}
        <g>
          <rect x={1432} y={300} width={120} height={50} rx={3} fill="#0f172a" stroke="#64748b" strokeWidth={1.8} />
          <text x={1492} y={317} fill="#94a3b8" fontSize={10} fontWeight={700} textAnchor="middle">Drain pit + pump</text>
          <text x={1492} y={331} fill="#cbd5e1" fontSize={9}  textAnchor="middle">2 m³ · 4.4 m³/h</text>
          <text x={1492} y={345} fill="#fbbf24" fontSize={8.5} fontWeight={700} textAnchor="middle">{MESAIEED_TAGS.drainPit} / {MESAIEED_TAGS.drainPump}</text>
          <text x={1492} y={290} fill="#64748b" fontSize={8} textAnchor="middle">drains: filters · tank · supply</text>
        </g>

        {/* 6. ACID WASH CIP (below cells) */}
        <g>
          <rect x={615} y={314} width={130} height={56} rx={4} fill="#451a03" stroke="#f59e0b" strokeWidth={2} />
          <text x={680} y={331} fill="#fcd34d" fontSize={10.5} fontWeight={700} textAnchor="middle">Acid wash / CIP</text>
          <text x={680} y={346} fill="#fde68a" fontSize={9.5} textAnchor="middle">{p.acidWashConc_target_pc.toFixed(0)}% HCl . every {p.acidWashEvery_d} d</text>
          <text x={680} y={360} fill="#fde68a" fontSize={9.5} textAnchor="middle">{r.acidPump_per_duty_Lh.toFixed(0)} L/h @ {p.acidPumpHead_bar.toFixed(1)} bar</text>
          <Tag x={680} y={385} t={`${MESAIEED_TAGS.acidPumps} . ${MESAIEED_TAGS.acidTank}`} />
          <line x1={632} y1={314} x2={632} y2={264} stroke="#f59e0b" strokeWidth={1.8} markerEnd="url(#arrow)" strokeDasharray="5 3" />
          <line x1={728} y1={264} x2={728} y2={314} stroke="#f59e0b" strokeWidth={1.8} markerEnd="url(#arrow)" strokeDasharray="5 3" />
        </g>

        {/* ===== flow arrows + LIVE flow rate · DN · velocity per pipe ===== */}
        <line x1={175} y1={230} x2={335} y2={230} stroke="#0ea5e9" strokeWidth={2.6} markerEnd="url(#arrow)" />
        <PipeBadge x={255} y={208} flow={r.filter_flow_m3h} unit="m³/h" dn={p.filter_inlet_DN} v={r.filter_in_vel} label="seawater" />
        <line x1={425} y1={230} x2={615} y2={230} stroke="#0ea5e9" strokeWidth={2.6} markerEnd="url(#arrow)" />
        <PipeBadge x={520} y={208} flow={r.brineFlow_per_module_m3h} unit="m³/h" dn={p.cell_inlet_DN} v={r.inlet_vel_ms} label="brine / stream" count={r.modules_running} />
        <line x1={745} y1={230} x2={965} y2={230} stroke="#22d3ee" strokeWidth={2.6} markerEnd="url(#arrow)" />
        <PipeBadge x={855} y={262} flow={r.brineFlow_per_module_m3h} unit="m³/h" dn={p.cell_outlet_DN} v={r.outlet_vel_ms} label="NaOCl + H₂ / stream" count={r.modules_running} />
        <line x1={1055} y1={230} x2={1290} y2={230} stroke="#10b981" strokeWidth={2.6} markerEnd="url(#arrow)" />
        <PipeBadge x={1172} y={208} flow={r.dosingPumpFlow_per_duty_Lh / 1000} unit="m³/h" dn={p.dosingPump_inlet_DN} v={r.dosing_in_vel_ms} label="NaOCl product / pump" count={p.dosingPumps_duty} />
        <line x1={1355} y1={230} x2={1500} y2={230} stroke="#10b981" strokeWidth={2.6} markerEnd="url(#arrow)" />
        <PipeBadge x={1428} y={264} flow={r.dosingPumpFlow_per_duty_Lh / 1000} unit="m³/h" dn={p.dosingPump_outlet_DN} v={r.dosing_out_vel_ms} label="to dosing point" count={p.dosingPumps_duty} />
        {/* air duct flow + velocity */}
        <PipeBadge x={905} y={172} flow={r.blowerFlow_actual_m3h_each} unit="m³/h" dn={p.blower_outlet_DN} v={r.blower_out_vel_ms} label="dilution air / blower" count={p.blower_duty} band={[3, 15]} />
        {/* acid CIP pipe */}
        <PipeBadge x={812} y={340} flow={r.acidPump_per_duty_Lh / 1000} unit="m³/h" dn={p.acidPump_outlet_DN} v={r.acid_out_vel_ms} label="acid CIP" count={p.acidPumps_duty} />

        {/* ===== live-data callouts (below each unit) ===== */}
        <Callout cx={130} top={405} w={165} title="Feed" stroke="#0ea5e9"
          rows={[`${p.electrolyserInlet_m3h.toFixed(1)} m3/h`, `${p.salinity_ppt.toFixed(1)} ppt . ${p.temperature_C.toFixed(0)} C`]} />
        <Callout cx={380} top={405} w={165} title="Strainer" stroke="#38bdf8"
          rows={[`${r.filter_flow_m3h.toFixed(1)} m3/h . ${p.filter_micron_um} um`, `in DN${p.filter_inlet_DN} / out DN${p.filter_outlet_DN}`]} />
        <Callout cx={680} top={420} w={205} title="Cells (Faraday)" stroke="#22d3ee"
          rows={[`${r.stackVoltage_V.toFixed(0)} V x ${r.I_cell_A.toFixed(0)} A DC`, `${r.power_total_DC_kW.toFixed(1)} kW . ${r.specificEnergy_kWh_per_kgCl2.toFixed(2)} kWh/kg`, `${p.cells_per_module} cells . in DN${p.cell_inlet_DN}/out DN${p.cell_outlet_DN}`]} />
        <Callout cx={1010} top={405} w={190} title="Degassing tank" stroke="#10b981"
          rows={[`${p.storage_tank_volume_m3.toFixed(2)} m3 . NaOCl ${r.naocl_mgL.toFixed(0)} mg/L`, `H2 ${r.H2_Nm3h.toFixed(2)} . air ${r.dilutionAir_Nm3h.toFixed(0)} Nm3/h`]} />
        <Callout cx={1320} top={405} w={200} title="Dosing pumps" stroke="#10b981"
          rows={[`${(r.dosingPumpFlow_total_demand_Lh / 1000).toFixed(2)} m3/h . ${r.dosingPump_kW_each.toFixed(2)} kW`, `in DN${p.dosingPump_inlet_DN} / out DN${p.dosingPump_outlet_DN}`]} />

        {/* legend strip */}
        <g>
          <text x={130} y={520} fill="#0ea5e9" fontSize={10}>seawater</text>
          <text x={250} y={520} fill="#22d3ee" fontSize={10}>NaOCl / brine</text>
          <text x={400} y={520} fill={ventTone} fontSize={10}>H2 / dilution air</text>
          <text x={560} y={520} fill="#f59e0b" fontSize={10}>acid CIP (intermittent)</text>
          <text x={760} y={520} fill="#a78bfa" fontSize={10}>DC power</text>
          <text x={900} y={520} fill="#34d399" fontSize={10}>● pill = live pipe velocity (green = 0.3-3 m/s)</text>
          <text x={1500} y={520} fill="#64748b" fontSize={9} textAnchor="end">amber tag = P&amp;ID equipment number . all values live</text>
        </g>
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
type SystemPreset = 'oneOne' | 'twoOne' | 'threeOne' | 'custom';
type TopTab       = 'process' | 'equipment' | 'degassing' | 'calcsheet' | 'chemistry' | 'config';

function applySystemPreset(p: ECParams, preset: SystemPreset): ECParams {
  switch (preset) {
    case 'oneOne':
      return { ...p, modules_installed: 1, modules_standby: 1,
        dosingPumps_duty: 1, dosingPumps_standby: 1,
        blower_duty: 1,      blower_standby: 1,
        acidPumps_duty: 1,   acidPumps_standby: 1 };
    case 'twoOne':
      return { ...p, modules_installed: 2, modules_standby: 1,
        dosingPumps_duty: 2, dosingPumps_standby: 1,
        blower_duty: 2,      blower_standby: 1,
        acidPumps_duty: 1,   acidPumps_standby: 1 };
    case 'threeOne':
      return { ...p, modules_installed: 3, modules_standby: 1,
        dosingPumps_duty: 3, dosingPumps_standby: 1,
        blower_duty: 2,      blower_standby: 1,
        acidPumps_duty: 1,   acidPumps_standby: 1 };
    default:
      return p;
  }
}

// ---------------------------------------------------------------------------
// Full-screen P&ID — large live diagram + per-equipment data cards (kiosk view)
// ---------------------------------------------------------------------------
function PidFullScreen({ p, r, checks, onClose }: { p: ECParams; r: ReturnType<typeof calcResults>; checks: Crit[]; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const pass = checks.filter(c => c.pass).length;
  const Kpi = ({ l, v, tone }: { l: string; v: string; tone?: string }) => (
    <div className="text-right leading-tight">
      <div className="text-[10px] uppercase text-slate-500">{l}</div>
      <div className="text-sm font-bold tabular-nums" style={{ color: tone ?? '#e2e8f0' }}>{v}</div>
    </div>
  );
  const Card = ({ title, tag, color, rows }: { title: string; tag?: string; color: string; rows: [string, string][] }) => (
    <div className="rounded-lg border bg-slate-900/60 p-3" style={{ borderColor: color + '66' }}>
      <div className="flex items-baseline justify-between mb-1.5">
        <p className="text-[13px] font-bold" style={{ color }}>{title}</p>
        {tag && <span className="text-[10px] font-bold text-amber-400">{tag}</span>}
      </div>
      <table className="w-full text-[12px]">
        <tbody>{rows.map(([k, v], i) => (
          <tr key={i} className={i % 2 ? 'bg-slate-800/30' : ''}>
            <td className="text-slate-400 py-[3px] pl-1 pr-2">{k}</td>
            <td className="text-right text-slate-100 font-semibold tabular-nums pr-1">{v}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
  return (
    <div id="pid-print" className="fixed inset-0 z-[100] bg-slate-950 overflow-auto">
      {/* print only this kiosk view (hide everything else); fit landscape */}
      <style>{`@media print {
        @page { size: A3 landscape; margin: 8mm; }
        body * { visibility: hidden !important; }
        #pid-print, #pid-print * { visibility: visible !important; }
        #pid-print { position: absolute !important; inset: 0 !important; overflow: visible !important; background: #fff !important; }
        #pid-print .no-print { display: none !important; }
      }`}</style>
      <div className="sticky top-0 z-10 flex items-center gap-5 px-5 py-2.5 bg-slate-900 border-b border-slate-700">
        <span className="text-cyan-200 font-bold text-[15px] tracking-wide">SEAWATER ELECTROCHLORINATION — P&amp;ID · Mesaieed (4737-M2UT-6-51-0001)</span>
        <div className="ml-auto flex items-center gap-5">
          <Kpi l="Cl₂ production" v={`${p.hypochloriteOutput_kgh.toFixed(1)} kg/h`} tone="#34d399" />
          <Kpi l="Seawater" v={`${p.electrolyserInlet_m3h.toFixed(1)} m³/h`} tone="#38bdf8" />
          <Kpi l="AC power" v={`${r.power_total_AC_kW.toFixed(0)} kW`} tone="#fbbf24" />
          <Kpi l="Spec. energy" v={`${r.specificEnergy_kWh_per_kgCl2.toFixed(2)} kWh/kg`} />
          <Kpi l="H₂ vent" v={`${r.vent_H2_pc_vv.toFixed(2)} %`} tone={r.vent_safe ? '#34d399' : '#f43f5e'} />
          <Kpi l="Checks" v={`${pass}/${checks.length}`} tone={pass === checks.length ? '#34d399' : '#fbbf24'} />
          <button onClick={() => window.print()} className="no-print px-3 py-1.5 rounded bg-cyan-700 text-white text-sm font-bold hover:bg-cyan-600">🖨 Export PDF</button>
          <button onClick={onClose} className="no-print px-3 py-1.5 rounded bg-slate-700 text-slate-100 text-sm font-bold hover:bg-slate-600">✕ Exit (Esc)</button>
        </div>
      </div>
      <div className="px-4 pt-3">
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-2">
          <ProcessDiagram p={p} r={r} />
        </div>
      </div>
      <div className="px-4 py-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card title="Seawater feed" color="#0ea5e9" rows={[
          ['Electrolyser inlet', `${p.electrolyserInlet_m3h.toFixed(1)} m³/h`],
          ['PD-off header', `${p.seawaterFlow_PDoff_m3h.toFixed(1)} m³/h`],
          ['Salinity', `${p.salinity_ppt.toFixed(1)} ppt`],
          ['Temperature', `${p.temperature_C.toFixed(0)} °C`],
          ['Conductivity', `${r.cond.toFixed(1)} mS/cm`],
          ['Density', `${r.densityCorr ? (swDensity(p.salinity_ppt, p.temperature_C)).toFixed(0) : ''} kg/m³`],
        ]} />
        <Card title="Seawater strainer" color="#38bdf8" rows={[
          ['Flow', `${r.filter_flow_m3h.toFixed(1)} m³/h`],
          ['Rating', `${p.filter_micron_um} µm`],
          ['Design ΔP', `${p.filter_designDP_bar.toFixed(2)} bar`],
          ['Inlet / outlet', `DN${p.filter_inlet_DN} / DN${p.filter_outlet_DN}`],
          ['Inlet velocity', `${r.filter_in_vel.toFixed(2)} m/s`],
        ]} />
        <Card title="Electrochlorination cells" tag={MESAIEED_TAGS.cells} color="#22d3ee" rows={[
          ['Modules (duty+standby)', `${p.modules_installed} + ${p.modules_standby}`],
          ['Cells in series', `${p.cells_per_module}`],
          ['Current density', `${p.currentDensity_kAm2.toFixed(2)} kA/m²`],
          ['Stack voltage', `${r.stackVoltage_V.toFixed(0)} VDC`],
          ['DC current (T/R)', `${r.I_cell_A.toFixed(0)} A`],
          ['DC power', `${r.power_total_DC_kW.toFixed(1)} kW`],
          ['Specific energy', `${r.specificEnergy_kWh_per_kgCl2.toFixed(2)} kWh/kg`],
          ['Operating pressure', `${p.cell_operatingPressure_barg.toFixed(1)} bar(g)`],
        ]} />
        <Card title="Transformer-rectifier" color="#a78bfa" rows={[
          ['AC supply', `${p.txr_AC_primary_V} VAC`],
          ['DC power', `${r.power_total_DC_kW.toFixed(1)} kW`],
          ['AC power', `${r.power_total_AC_kW.toFixed(1)} kW`],
          ['Efficiency', `${p.txr_efficiency_pc} %`],
          ['Redundancy', `${p.txr_redundancy}`],
        ]} />
        <Card title="Degassing tank" tag={MESAIEED_TAGS.degasTank} color="#10b981" rows={[
          ['Working volume', `${p.storage_tank_volume_m3.toFixed(2)} m³`],
          ['NaOCl product', `${r.naocl_mgL.toFixed(0)} mg/L`],
          ['Material', `${p.storage_material}`],
          ['Residence', `${p.storageTank_h.toFixed(1)} h`],
        ]} />
        <Card title="H₂ dilution blowers" tag={MESAIEED_TAGS.blowers} color={r.vent_safe ? '#34d399' : '#f43f5e'} rows={[
          ['H₂ generated', `${r.H2_Nm3h.toFixed(2)} Nm³/h`],
          ['Dilution air', `${r.dilutionAir_Nm3h.toFixed(0)} Nm³/h`],
          ['Vent H₂', `${r.vent_H2_pc_vv.toFixed(2)} % v/v`],
          ['Blowers', `${p.blower_duty} + ${p.blower_standby}`],
          ['Static / motor', `${p.blower_static_mbar} mbar · ${r.blower_motor_kW_each.toFixed(2)} kW`],
        ]} />
        <Card title="NaOCl dosing pumps" color="#10b981" rows={[
          ['Process dosed', `${p.dosing_process_flow_m3h.toFixed(0)} m³/h`],
          ['Target dose', `${p.dosing_target_ppm} ppm`],
          ['Demand flow', `${(r.dosingPumpFlow_total_demand_Lh / 1000).toFixed(2)} m³/h`],
          ['Pumps', `${p.dosingPumps_duty} + ${p.dosingPumps_standby}`],
          ['Head', `${(p.dosingPumpHead_bar + p.dosingPump_staticPressure_mbar / 1000).toFixed(2)} bar`],
          ['Motor each', `${r.dosingPump_kW_each.toFixed(2)} kW`],
          ['In / out velocity', `${r.dosing_in_vel_ms.toFixed(2)} / ${r.dosing_out_vel_ms.toFixed(2)} m/s`],
        ]} />
        <Card title="Acid wash / CIP" tag={MESAIEED_TAGS.acidPumps} color="#f59e0b" rows={[
          ['Acid', `${p.acidWashConc_target_pc.toFixed(0)} % HCl`],
          ['Interval', `every ${p.acidWashEvery_d} d`],
          ['Duration', `${p.acidWashDuration_min} min`],
          ['Pump flow', `${r.acidPump_per_duty_Lh.toFixed(0)} L/h`],
          ['Head', `${p.acidPumpHead_bar.toFixed(1)} bar`],
          ['Tank', `${p.acidWashTank_m3.toFixed(1)} m³`],
        ]} />
      </div>
      <p className="text-center text-[11px] text-slate-500 pb-4">All values are live — driven by the Cl₂ production target, seawater chemistry and equipment inputs. Press Esc or Exit to return.</p>
    </div>
  );
}

export default function ElectrochlorinationSpecPage({ bootFullScreen = false }: { bootFullScreen?: boolean } = {}) {
  const [p, setP] = useState<ECParams>(DEFAULTS);
  const [pidFull, setPidFull] = useState(bootFullScreen);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Local config save/load (works offline in the .exe — no backend needed).
  const saveConfigLocal = () => {
    const blob = new Blob([JSON.stringify(p, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `electrochlorination-config-${p.hypochloriteOutput_kgh.toFixed(0)}kgh.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const loadConfigLocal = (file: File) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const obj = JSON.parse(String(reader.result));
        setP(prev => ({ ...prev, ...obj }));
        (await import('sonner')).toast.success('Configuration loaded');
      } catch (e: any) {
        (await import('sonner')).toast.error(`Invalid config file: ${e?.message ?? e}`);
      }
    };
    reader.readAsText(file);
  };
  const [tab, setTab] = useState<TopTab>('process');
  const [eqRedundancy, setEqRedundancy] = useState<'N+1' | '2N'>('N+1');
  const [eqOverride, setEqOverride] = useState<Record<string, number>>({});
  const [preset, setPreset] = useState<SystemPreset>('oneOne');
  const r = useMemo(() => calcResults(p), [p]);
  const checks = useMemo(() => validate(p, r), [p, r]);
  const passCount = checks.filter(c => c.pass).length;
  const totalChecks = checks.length;

  // ─── Cloud config save/load ────────────────────────────
  const [configName, setConfigName] = useState('EC config');
  const [cloudConfigId, setCloudConfigId] = useState<number | null>(null);
  const [cloudSaving, setCloudSaving] = useState(false);
  const [cloudPickerOpen, setCloudPickerOpen] = useState(false);
  const [cloudList, setCloudList] = useState<any[]>([]);
  const [cloudLoading, setCloudLoading] = useState(false);

  const handleCloudSave = async () => {
    setCloudSaving(true);
    try {
      const { apiSaveECConfig } = await import('@/lib/phpApi');
      const res = await apiSaveECConfig({
        id: cloudConfigId ?? undefined,
        name: configName.trim() || 'EC config',
        cl2_kgh: p.hypochloriteOutput_kgh,
        seawater_m3h: p.seawaterFlow_PDoff_m3h,
        modules: p.modules_installed,
        spec: p,
      });
      setCloudConfigId(res.id);
      (await import('sonner')).toast.success(`Saved to cloud (id ${res.id})`);
    } catch (e: any) {
      (await import('sonner')).toast.error(`Save failed: ${e?.message ?? e}`);
    } finally { setCloudSaving(false); }
  };
  const handleCloudLoadOpen = async () => {
    setCloudPickerOpen(true);
    setCloudLoading(true);
    try {
      const { apiListECConfigs } = await import('@/lib/phpApi');
      setCloudList(await apiListECConfigs());
    } catch (e: any) {
      (await import('sonner')).toast.error(`List failed: ${e?.message ?? e}`);
    } finally { setCloudLoading(false); }
  };
  const handleCloudLoad = async (id: number) => {
    try {
      const { apiGetECConfig } = await import('@/lib/phpApi');
      const wb = await apiGetECConfig(id);
      if (wb.spec) setP(wb.spec as ECParams);
      setCloudConfigId(wb.id);
      setConfigName(wb.name);
      setCloudPickerOpen(false);
      (await import('sonner')).toast.success(`Loaded "${wb.name}"`);
    } catch (e: any) {
      (await import('sonner')).toast.error(`Load failed: ${e?.message ?? e}`);
    }
  };
  const handleCloudDelete = async (id: number, name: string) => {
    if (!confirm(`Delete "${name}"?`)) return;
    try {
      const { apiDeleteECConfig, apiListECConfigs } = await import('@/lib/phpApi');
      await apiDeleteECConfig(id);
      setCloudList(await apiListECConfigs());
      if (cloudConfigId === id) setCloudConfigId(null);
    } catch (e: any) {
      (await import('sonner')).toast.error(`Delete failed: ${e?.message ?? e}`);
    }
  };

  const choosePreset = (next: SystemPreset) => {
    setPreset(next);
    if (next !== 'custom') setP(prev => applySystemPreset(prev, next));
  };

  // PRODUCTION DRIVER — Cl2 output is the master variable: the seawater feed scales
  // with it so the product stays at the design ~1000 mg/L (electrolyser inlet ≈ kg/h,
  // since 1000 mg/L = 1 kg/m³), and the PD-off seawater header keeps the ~1.5× booster
  // ratio. The eductor motive split follows the PD-off flow.
  const setProduction = (kgh: number) => {
    const v = Math.max(0.1, Math.min(500, kgh || 0));
    setP(prev => {
      const inlet = v;                 // m³/h to hold 1000 mg/L product
      const pdOff = inlet * 1.5;        // booster / treated-water header
      // A single bipolar stream tops out near its nameplate cell current — cap each
      // module at ~15 kg/h (≈756 A across 20 cells) and add modules above that, so the
      // per-cell current stays physical instead of one module carrying thousands of amps.
      const modules = Math.max(1, Math.ceil(v / 15));
      return { ...prev, hypochloriteOutput_kgh: v, electrolyserInlet_m3h: inlet, seawaterFlow_PDoff_m3h: pdOff, eductor_motive_m3h: pdOff / 3, modules_installed: modules };
    });
  };

  // Detect manual divergence from the active preset and downgrade to 'custom'
  useEffect(() => {
    if (preset === 'custom') return;
    const ideal = applySystemPreset(p, preset);
    const drift = ideal.modules_installed !== p.modules_installed
      || ideal.modules_standby !== p.modules_standby
      || ideal.dosingPumps_duty !== p.dosingPumps_duty
      || ideal.dosingPumps_standby !== p.dosingPumps_standby
      || ideal.blower_duty !== p.blower_duty
      || ideal.blower_standby !== p.blower_standby
      || ideal.acidPumps_duty !== p.acidPumps_duty
      || ideal.acidPumps_standby !== p.acidPumps_standby;
    if (drift) setPreset('custom');
  }, [p, preset]);

  const sweepSal = useMemo(() => {
    const out: { salinity: number; specEnergy: number; cond: number }[] = [];
    for (let s = 25; s <= 40; s += 1) {
      const rr = calcResults({ ...p, salinity_ppt: s });
      out.push({ salinity: s, specEnergy: +rr.specificEnergy_kWh_per_kgCl2.toFixed(2), cond: +rr.cond.toFixed(1) });
    }
    return out;
  }, [p]);

  const sweepT = useMemo(() => {
    const out: { temp: number; specEnergy: number }[] = [];
    for (let t = 5; t <= 35; t += 2) {
      const rr = calcResults({ ...p, temperature_C: t });
      out.push({ temp: t, specEnergy: +rr.specificEnergy_kWh_per_kgCl2.toFixed(2) });
    }
    return out;
  }, [p]);

  return (
    <div className="p-4 md:p-6 space-y-6">
      {pidFull && <PidFullScreen p={p} r={r} checks={checks} onClose={() => { setPidFull(false); try { if (document.fullscreenElement) document.exitFullscreen?.(); } catch { /* ignore */ } }} />}
      <PageHeader
        title="Electrochlorination - sizing & specification"
        subtitle="On-site sodium hypochlorite generation from seawater. Standard seawater properties (salinity 30 - 40 ppt, 0 - 35 deg C) embedded; user-adjustable inputs for dosing pumps, acid washing / CIP, cell stack, and N+1 redundancy."
        icon={Zap}
        actions={
          <div className="flex gap-2">
            <button onClick={async () => {
                setP(prev => ({ ...prev, ...PRESET_VENDOR }));
                setPreset('oneOne');
                (await import('sonner')).toast.success('Loaded Vendor sizing (Perocon QKQ2831 Rev.2)');
              }}
              title="Load the Perocon vendor sizing calculation (QKQ2831) — the manufacturer's design basis"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-300 text-xs font-bold border border-emerald-500/30 hover:bg-emerald-500/20">
              🏭 Vendor sizing
            </button>
            <button onClick={async () => {
                setP(prev => ({ ...prev, ...PRESET_DATASHEET }));
                setPreset('oneOne');
                (await import('sonner')).toast.success('Loaded Issued datasheet (QatarEnergy 4737-M2UT)');
              }}
              title="Load the QatarEnergy issued process-datasheet nameplate (P&ID 4737-M2UT-6-51-0001)"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-sky-500/10 text-sky-300 text-xs font-bold border border-sky-500/30 hover:bg-sky-500/20">
              📄 Issued datasheet
            </button>
            <button onClick={handleCloudSave} disabled={cloudSaving}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-500/10 text-purple-300 text-xs font-bold border border-purple-500/30 hover:bg-purple-500/20 disabled:opacity-50">
              ☁ {cloudSaving ? 'Saving…' : cloudConfigId ? `Update cloud (#${cloudConfigId})` : 'Save to cloud'}
            </button>
            <button onClick={handleCloudLoadOpen}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-sky-500/10 text-sky-300 text-xs font-bold border border-sky-500/30 hover:bg-sky-500/20">
              📂 Open from cloud
            </button>
            <button onClick={saveConfigLocal}
              title="Save the full configuration to a local .json file (works offline)"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-500/10 text-slate-200 text-xs font-bold border border-slate-500/30 hover:bg-slate-500/20">
              💾 Save config
            </button>
            <button onClick={() => fileInputRef.current?.click()}
              title="Load a configuration from a local .json file"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-500/10 text-slate-200 text-xs font-bold border border-slate-500/30 hover:bg-slate-500/20">
              📁 Load config
            </button>
            <input ref={fileInputRef} type="file" accept="application/json,.json" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) loadConfigLocal(f); e.currentTarget.value = ''; }} />
          </div>
        }
      />

      {/* ─── PRODUCTION DRIVER PANEL — Cl₂ kg/h sets the cell + everything else ─── */}
      <div className="p-4 rb-card border-l-4 border-emerald-500/60">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
          <div className="md:col-span-3">
            <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">PRODUCTION DRIVER</p>
            <p className="text-[11px] text-slate-400">Set Cl₂ output here and the whole spec re-sizes</p>
          </div>
          <div className="md:col-span-5">
            <label className="text-[10px] uppercase text-slate-500">Liquid Cl₂ production (NaOCl as Cl₂)</label>
            <div className="flex items-center gap-3">
              <input type="range" min={0.1} max={500} step={0.5} value={p.hypochloriteOutput_kgh}
                onChange={e => setProduction(Number(e.target.value))}
                className="flex-1 cursor-pointer"
                style={{ accentColor: '#10b981', color: '#10b981', ['--p' as any]: `${Math.max(0, Math.min(100, (p.hypochloriteOutput_kgh / 500) * 100)).toFixed(2)}%` }} />
              <input type="number" min={0.1} max={500} step={0.1} value={p.hypochloriteOutput_kgh}
                onChange={e => setProduction(Number(e.target.value))}
                className="w-24 bg-rb-elevated border border-emerald-500/30 rounded-lg px-2 py-1.5 text-base font-bold text-emerald-300 tabular-nums text-right" />
              <span className="text-xs text-slate-500">kg/h</span>
            </div>
          </div>
          <div className="md:col-span-4 flex justify-end gap-2">
            <button onClick={() => setP(prev => ({ ...prev, ...recommendAll(prev) }))}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-300 text-xs font-bold border border-emerald-500/40 hover:bg-emerald-500/20">
              ⚙ Re-size everything from this Cl₂
            </button>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
          <div className="p-2 rb-inner-card">
            <span className="text-slate-500">Seawater flow </span>
            <span className="text-cyan-300 font-bold tabular-nums">{p.seawaterFlow_PDoff_m3h.toFixed(2)} m³/h</span>
          </div>
          <div className="p-2 rb-inner-card">
            <span className="text-slate-500">Modules </span>
            <span className="text-cyan-300 font-bold tabular-nums">{p.modules_installed} duty + {p.modules_standby} standby</span>
          </div>
          <div className="p-2 rb-inner-card">
            <span className="text-slate-500">Power AC </span>
            <span className="text-amber-300 font-bold tabular-nums">{r.power_total_AC_kW.toFixed(0)} kW</span>
          </div>
          <div className="p-2 rb-inner-card">
            <span className="text-slate-500">Spec. energy </span>
            <span className={`font-bold tabular-nums ${r.specificEnergy_kWh_per_kgCl2 < 5.5 ? 'text-emerald-300' : r.specificEnergy_kWh_per_kgCl2 < 7 ? 'text-amber-300' : 'text-rose-300'}`}>
              {r.specificEnergy_kWh_per_kgCl2.toFixed(2)} kWh/kg Cl₂
            </span>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <input type="text" value={configName} onChange={e => setConfigName(e.target.value)} placeholder="Configuration name"
            className="flex-1 bg-rb-elevated border border-slate-800 rounded px-2 py-1 text-xs text-slate-200" />
          {cloudConfigId ? <span className="text-[10px] text-purple-400">cloud id #{cloudConfigId}</span> : <span className="text-[10px] text-amber-400">local only</span>}
        </div>
      </div>

      {/* CLOUD PICKER POPUP */}
      {cloudPickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setCloudPickerOpen(false)}>
          <div className="rb-card w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <h3 className="text-sm font-bold text-white">☁ Saved EC configurations ({cloudList.length})</h3>
              <button onClick={() => setCloudPickerOpen(false)} className="p-1 text-slate-500 hover:text-white">✕</button>
            </div>
            <div className="overflow-y-auto p-3 space-y-2">
              {cloudLoading && <p className="text-xs text-slate-500 text-center py-6">Loading…</p>}
              {!cloudLoading && cloudList.length === 0 && <p className="text-xs text-slate-500 text-center py-6">No saved configs yet.</p>}
              {cloudList.map(cfg => (
                <div key={cfg.id} className="p-3 rb-inner-card flex items-center justify-between hover:bg-slate-800/30">
                  <button onClick={() => handleCloudLoad(cfg.id)} className="flex-1 text-left">
                    <p className="text-xs font-bold text-white">{cfg.name}</p>
                    <p className="text-[10px] text-slate-500">
                      id #{cfg.id} · {Number(cfg.cl2_kgh).toFixed(1)} kg/h Cl₂ · {cfg.modules} modules · {new Date(cfg.updated_at).toLocaleString()}
                    </p>
                  </button>
                  <button onClick={() => handleCloudDelete(cfg.id, cfg.name)}
                    className="p-1.5 rounded text-rose-400 hover:bg-rose-500/20">🗑</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TOP TABS */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-700 pb-2">
        {([
          { id: 'process',   label: 'Process & sizing' },
          { id: 'equipment', label: 'Equipment selection' },
          { id: 'degassing', label: 'Degassing tank & breathing' },
          { id: 'calcsheet', label: 'Calculation sheet' },
          { id: 'chemistry', label: 'Detailed seawater chemistry' },
          { id: 'config',    label: 'System configuration' },
        ] as { id: TopTab; label: string }[]).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
              tab === t.id
                ? 'bg-cyan-600/20 border-cyan-500 text-cyan-200'
                : 'bg-slate-900/40 border-slate-700 text-slate-300 hover:border-slate-500'
            }`}>{t.label}</button>
        ))}
        <button onClick={() => setP(prev => ({ ...prev, ...recommendAll(prev) }))}
          className="ml-auto px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-600/20 border border-emerald-500/60 text-emerald-200 hover:bg-emerald-600/30 inline-flex items-center gap-1.5">
          <Activity className="w-3.5 h-3.5" /> Apply all recommendations
        </button>
        <span className="text-[11px] text-slate-400">
          Preset:{' '}
          <span className="text-cyan-300 font-semibold">
            {preset === 'oneOne' ? '1 duty + 1 standby'
              : preset === 'twoOne' ? '2 duty + 1 standby'
              : preset === 'threeOne' ? '3 duty + 1 standby'
              : 'Custom'}
          </span>
        </span>
      </div>

      {tab === 'equipment' && (() => {
        const streamCount = Math.max(1, p.modules_installed);   // set live by the Cl₂ driver
        const eqRows = EQUIPMENT_CATALOGUE.map(eq => {
          let total: number, rating: number, duty: number, driven = false;
          if (eq.key === 'stream') {
            // Stream count is the module count the production driver already sized.
            total = p.hypochloriteOutput_kgh; duty = streamCount;
            rating = Math.round((total / duty) * 10) / 10; driven = true;
          } else if (eq.key === 'txr') {
            // One transformer-rectifier per stream; rating = the smallest catalogue size ≥ per-stream kW.
            total = r.power_total_DC_kW; duty = streamCount;
            const perStream = r.power_total_DC_kW / streamCount;
            rating = eq.sizes.find(s => s >= perStream) ?? eq.sizes[eq.sizes.length - 1]; driven = true;
          } else {
            total = eq.key === 'dosing' ? r.dosingPumpFlow_total_demand_Lh / 1000
                  : eq.key === 'blower' ? r.dilutionAir_Nm3h
                  : eq.key === 'acid'   ? p.acidWashTank_m3 / Math.max(0.1, p.acidWashDuration_min / 60)
                  : p.seawaterFlow_PDoff_m3h;
            const s = splitUnits(total, eq.sizes, eqOverride[eq.key]);
            rating = s.rating; duty = s.duty;
          }
          const standby = eqRedundancy === '2N' ? duty : 1;
          const installed = rating * duty;
          const margin = total > 0 ? (installed / total - 1) * 100 : 0;
          return { eq, total, rating, duty, standby, totalUnits: duty + standby, installed, margin, driven };
        });
        const totalUnits = eqRows.reduce((a, x) => a + x.totalUnits, 0);
        const doseDemandCl2 = p.dosing_process_flow_m3h * p.dosing_target_ppm / 1000;
        const prodOk = p.hypochloriteOutput_kgh >= doseDemandCl2;
        return (
          <Section title="Equipment selection — duty + standby split across realistic units" icon={<Layers className="w-4 h-4" />}
            action={
              <div className="flex items-center gap-1 text-[11px]">
                <span className="text-slate-500">Redundancy:</span>
                {(['N+1', '2N'] as const).map(rk => (
                  <button key={rk} onClick={() => setEqRedundancy(rk)}
                    className={`px-2 py-0.5 rounded font-bold border ${eqRedundancy === rk ? 'bg-cyan-600/30 border-cyan-500 text-cyan-200' : 'bg-slate-900/40 border-slate-700 text-slate-400'}`}>{rk}</button>
                ))}
              </div>
            }>
            <p className="text-[11px] text-slate-400 mb-2">
              Each unit count is the live duty ÷ a standard rating (smallest size that meets the duty in ≤ 4 units), plus standby.
              Pick a different rating to force a coarser/finer split. The Cl₂ driver and dosing inputs feed these totals directly.
            </p>
            <div className={`p-2 mb-3 rounded-lg border text-[11px] ${prodOk ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/40'}`}>
              <span className={prodOk ? 'text-emerald-300' : 'text-rose-300'}>{prodOk ? '✓' : '✕'}</span>{' '}
              Generation vs demand: producing <b>{p.hypochloriteOutput_kgh.toFixed(1)} kg/h</b> vs dosing demand{' '}
              <b>{doseDemandCl2.toFixed(2)} kg/h</b> ({p.dosing_process_flow_m3h.toFixed(0)} m³/h × {p.dosing_target_ppm} ppm).{' '}
              {prodOk ? `Margin +${((p.hypochloriteOutput_kgh / Math.max(0.01, doseDemandCl2) - 1) * 100).toFixed(0)}%.` : 'Increase Cl₂ production or reduce dose/flow.'}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {eqRows.map(row => (
                <div key={row.eq.key} className="rb-inner-card p-3 border border-slate-700/60 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-bold text-cyan-200">{row.eq.label}</p>
                      <p className="text-[10px] text-slate-500 leading-tight mt-0.5">{row.eq.note}</p>
                    </div>
                    <span className="text-[10px] text-slate-400 whitespace-nowrap">duty {row.total < 100 ? row.total.toFixed(2) : row.total.toFixed(0)} {row.eq.unit}</span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 items-end">
                    <div>
                      <label className="text-[9px] uppercase text-slate-500 block">Unit rating</label>
                      {row.driven ? (
                        <p className="mt-0.5 px-2 py-1 rounded bg-slate-900/40 border border-slate-800 text-slate-300 text-[11px]">{row.rating} {row.eq.unit} <span className="text-slate-600">· from Cl₂ driver</span></p>
                      ) : (
                        <select value={eqOverride[row.eq.key] ?? 0}
                          onChange={e => setEqOverride(o => ({ ...o, [row.eq.key]: Number(e.target.value) }))}
                          className="w-full mt-0.5 px-2 py-1 rounded bg-slate-900/60 border border-slate-700 text-slate-100 text-[11px]">
                          <option value={0}>Auto ({row.rating} {row.eq.unit})</option>
                          {row.eq.sizes.map(s => <option key={s} value={s}>{s} {row.eq.unit}</option>)}
                        </select>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-[9px] uppercase text-slate-500">Selected</p>
                      <p className="text-emerald-300 font-bold text-[12px] leading-tight">{row.duty} × {row.rating} {row.eq.unit}</p>
                      <p className="text-slate-400 text-[10px]">{row.duty} duty + {row.standby} standby = {row.totalUnits}</p>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-slate-800 flex items-center justify-between text-[10px]">
                    <span className="text-slate-400">Installed <b className="text-slate-200">{row.installed.toFixed(0)} {row.eq.unit}</b></span>
                    <span className="text-slate-400">margin <b className={row.margin >= 0 ? 'text-emerald-300' : 'text-rose-300'}>{row.margin > 0 ? '+' : ''}{row.margin.toFixed(0)}%</b></span>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-slate-400">
              Total installed units (incl. standby): <b className="text-cyan-200">{totalUnits}</b>. Mirrors the vendor's
              "Electrolyser Assembly / Hypo-Pump Selection" sheets — change the Cl₂ driver, dosing flow or redundancy and the split re-solves live.
            </p>
          </Section>
        );
      })()}

      {tab === 'degassing' && (() => {
        const dg = degassingDesign(p, r);
        const allOk = dg.ventOk && dg.h2Ok && dg.degassOk && dg.heightOk;
        const Row = ({ k, v, tone, sub }: { k: string; v: string; tone?: string; sub?: string }) => (
          <div className="py-[3px] border-b border-slate-800/40">
            <div className="flex justify-between text-[12px]">
              <span className="text-slate-400">{k}</span>
              <span className="font-semibold tabular-nums" style={{ color: tone ?? '#e2e8f0' }}>{v}</span>
            </div>
            {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
          </div>
        );
        return (
          <Section title="Degassing hypochlorite storage tank  -  design & API 2000 breathing" icon={<Droplets className="w-4 h-4" />}>
            <div className={`p-2 mb-3 rounded-lg border text-[12px] ${allOk ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200' : 'bg-amber-500/10 border-amber-500/40 text-amber-200'}`}>
              {allOk ? 'OK Vessel sizing, H2 dilution and vent breathing are all within spec.' : '! One or more checks are out of spec  -  see the coloured rows below.'}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* INPUTS */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-cyan-300 uppercase tracking-wide">Design inputs (amend live)</h4>
                <p className="text-[10px] text-slate-500 -mt-1">Vendor method (4494-M2UT-6-12-0001): the tank fills during PD-OFF and empties during PD-ON; working volume = worst liquid swing + 5-min degassing; height = nozzle/level stack x (1 + HHLL margin) + freeboard. Levels are <em>derived</em>, not assumed.</p>
                <div className="grid grid-cols-2 gap-3">
                  <NumField label="Tank diameter"         suffix="mm"   value={p.tank_diameter_mm}    min={500} max={6000} step={10}  onChange={v => setP({ ...p, tank_diameter_mm: v })} help="1900 fits a 3.4 m limit" />
                  <NumField label="Max tank height"       suffix="mm"   value={p.tank_max_height_mm}  min={1000} max={12000} step={50} onChange={v => setP({ ...p, tank_max_height_mm: v })} help="site head-room limit" />
                  <NumField label="Electrolyser inflow (A)" suffix="m3/h" value={p.electrolyserInlet_m3h} min={1} max={200} step={0.5} onChange={v => setP({ ...p, electrolyserInlet_m3h: v })} help="Into tank" />
                  <NumField label="Antifoam inflow (B)"   suffix="m3/h" value={p.antifoam_flow_m3h}   min={0}   max={10}   step={0.1} onChange={v => setP({ ...p, antifoam_flow_m3h: v })} />
                  <NumField label="Min. degassing (F)"    suffix="min"  value={p.degas_min_min}       min={1}   max={30}   step={1}   onChange={v => setP({ ...p, degas_min_min: v })} help="5-min H2 degassing" />
                  <NumField label="Case-1 PD ON / OFF"    suffix="min"  value={p.case1_on_min}        min={1}   max={60}   step={1}   onChange={v => setP({ ...p, case1_on_min: v })} help={`OFF ${p.case1_off_min} * dose ${p.case1_dose_m3h}`} />
                  <NumField label="Case-1 dosing out"     suffix="m3/h" value={p.case1_dose_m3h}      min={0}   max={200}  step={0.5} onChange={v => setP({ ...p, case1_dose_m3h: v })} />
                  <NumField label="Case-2 PD ON / OFF"    suffix="min"  value={p.case2_on_min}        min={1}   max={60}   step={1}   onChange={v => setP({ ...p, case2_on_min: v })} help={`OFF ${p.case2_off_min} * dose ${p.case2_dose_m3h}`} />
                  <NumField label="Case-2 dosing out"     suffix="m3/h" value={p.case2_dose_m3h}      min={0}   max={200}  step={0.5} onChange={v => setP({ ...p, case2_dose_m3h: v })} />
                  <NumField label="HHLL margin (O)"       suffix="%"    value={p.hhll_margin_pc}      min={0}   max={50}   step={1}   onChange={v => setP({ ...p, hhll_margin_pc: v })} help="20 % on stack height" />
                  <NumField label="Freeboard HHLL to top (P)" suffix="mm"  value={p.freeboard_top_mm}    min={100} max={1500} step={10}  onChange={v => setP({ ...p, freeboard_top_mm: v })} help="500 (0.5 m)" />
                  <NumField label="Bottom to outlet (I)"     suffix="mm"   value={p.nozzle_bottom_I_mm}  min={0}   max={1000} step={10}  onChange={v => setP({ ...p, nozzle_bottom_I_mm: v })} />
                  <NumField label="Outlet dia (J)"          suffix="mm"   value={p.nozzle_dia_J_mm}     min={0}   max={1000} step={5}   onChange={v => setP({ ...p, nozzle_dia_J_mm: v })} />
                  <NumField label="Brim to LLLL (K)"         suffix="mm"   value={p.nozzle_brim_K_mm}    min={0}   max={1000} step={10}  onChange={v => setP({ ...p, nozzle_brim_K_mm: v })} />
                  <NumField label="LLLL to LLL (L)"          suffix="mm"   value={p.deadband_L_mm}       min={0}   max={1000} step={10}  onChange={v => setP({ ...p, deadband_L_mm: v })} />
                  <SelField label="Material" value={p.storage_material}
                    options={[{ value: 'GRP', label: 'GRVE + ECTFE liner' }, { value: 'HDPE', label: 'HDPE' }, { value: 'PP', label: 'Polypropylene' }, { value: 'PVDF', label: 'PVDF' }]}
                    onChange={v => setP({ ...p, storage_material: v as ECParams['storage_material'] })} />
                  <NumField label="Air : H2 dilution"     suffix=":1"   value={p.vent_dilution_factor} min={30}  max={200}  step={5}   onChange={v => setP({ ...p, vent_dilution_factor: v })} help="100:1 to H2 ~1% (25% LEL)" />
                  <NumField label="Vent velocity target"  suffix="m/s"  value={p.vent_velocity_target_ms} min={2} max={30}  step={0.5} onChange={v => setP({ ...p, vent_velocity_target_ms: v })} help="~15 m/s gas line to DN125" />
                </div>
              </div>
              {/* RESULTS */}
              <div className="space-y-3">
                <div className="rb-inner-card p-3">
                  <p className="text-xs font-bold text-cyan-200 mb-1">Vessel design (governing: {dg.govern.id})</p>
                  <Row k="Diameter x height (T/L-T/L)" v={`${(dg.D * 1000).toFixed(0)} x ${(dg.H * 1000).toFixed(0)} mm`} />
                  <Row k="Height vs limit" v={`${dg.topMM.toFixed(0)} / ${p.tank_max_height_mm} mm`} tone={dg.heightOk ? '#34d399' : '#f43f5e'}
                    sub={dg.heightOk ? 'fits under the head-room limit' : `OVER LIMIT - widen tank to at least ${isFinite(dg.dForHeight_mm) ? dg.dForHeight_mm.toFixed(0) : '-'} mm dia`} />
                  <Row k="Total geometric volume" v={`${dg.Vtotal.toFixed(2)} m3`} />
                  <Row k="Working volume (worst swing + degas)" v={`${dg.Vwork.toFixed(2)} m3`} tone="#7dd3fc" />
                  <Row k="Liquid heel (below Low)" v={`${dg.Vheel.toFixed(2)} m3`} />
                  <Row k="Gas disengagement (above HHLL)" v={`${dg.Vvapor.toFixed(2)} m3`} />
                  <Row k="Resulting H/D ratio" v={dg.HD.toFixed(2)} />
                  <Row k="Min. degassing volume (5 min, G)" v={`${dg.Vdegas5.toFixed(2)} m3`} />
                  <Row k="Working residence (working / inflow)" v={`${dg.degassResidenceActual.toFixed(1)} min`} sub="follows from the working volume  -  not an input" />
                  <Row k="Liquid rise rate" v={`${dg.riseVel_mmps.toFixed(2)} mm/s`} />
                </div>
                {/* Derived level setpoints */}
                <div className="rb-inner-card p-3">
                  <p className="text-xs font-bold text-cyan-200 mb-1">Derived level setpoints (vs datasheet)</p>
                  {([
                    ['Low-low (LLLL)', dg.lvl_LLLL, 380],
                    ['Low / PD OFF (LLL)', dg.lvl_LLL, 580],
                    ['High / PD ON (HLL)', dg.lvl_HLL, 2710],
                    ['High-high (HHLL)', dg.lvl_HHLL, 3250],
                    ['Tank top (T/L)', dg.topMM, 3750],
                  ] as [string, number, number][]).map(([k, mm, ds]) => (
                    <Row key={k} k={k} v={`${mm.toFixed(0)} mm`} tone={Math.abs(mm - ds) <= 15 ? '#34d399' : '#fbbf24'} sub={`datasheet ${ds} mm`} />
                  ))}
                </div>
                {/* Vendor working-volume table A-H */}
                {(() => {
                  const Cell = ({ children, hd }: { children: ReactNode; hd?: boolean }) => (
                    <td className={`px-1.5 py-0.5 text-right tabular-nums ${hd ? 'text-cyan-300 font-semibold' : 'text-slate-200'}`}>{children}</td>
                  );
                  const fmt = (n: number) => n.toFixed(2);
                  return (
                    <div className="rb-inner-card p-3 overflow-x-auto">
                      <p className="text-xs font-bold text-cyan-200 mb-1">Vendor working-volume calc (A-H, m3)</p>
                      <table className="w-full text-[11px]">
                        <thead><tr className="text-slate-500 border-b border-slate-700">
                          <th className="text-left px-1.5">row</th><th className="px-1.5">C1 ON</th><th className="px-1.5">C1 OFF</th><th className="px-1.5">C2 ON</th><th className="px-1.5">C2 OFF</th>
                        </tr></thead>
                        <tbody>
                          <tr><td className="px-1.5 text-slate-400">A in (m3/h)</td><Cell>{dg.A}</Cell><Cell>{dg.A}</Cell><Cell>{dg.A}</Cell><Cell>{dg.A}</Cell></tr>
                          <tr><td className="px-1.5 text-slate-400">C accumulate</td><Cell>{fmt(dg.c1.Con)}</Cell><Cell>{fmt(dg.c1.Coff)}</Cell><Cell>{fmt(dg.c2.Con)}</Cell><Cell>{fmt(dg.c2.Coff)}</Cell></tr>
                          <tr><td className="px-1.5 text-slate-400">E out</td><Cell>{fmt(dg.c1.Eon)}</Cell><Cell>0</Cell><Cell>{fmt(dg.c2.Eon)}</Cell><Cell>0</Cell></tr>
                          <tr><td className="px-1.5 text-slate-400">G degas (5min)</td><Cell>{fmt(dg.G)}</Cell><Cell>{fmt(dg.G)}</Cell><Cell>{fmt(dg.G)}</Cell><Cell>{fmt(dg.G)}</Cell></tr>
                          <tr className="border-t border-slate-700"><td className="px-1.5 text-cyan-300 font-semibold">H working</td><Cell hd>{fmt(dg.c1.Hon)}</Cell><Cell hd>{fmt(dg.c1.Hoff)}</Cell><Cell hd>{fmt(dg.c2.Hon)}</Cell><Cell hd>{fmt(dg.c2.Hoff)}</Cell></tr>
                        </tbody>
                      </table>
                      <p className="text-[10px] text-slate-500 mt-1">Worst = <b className="text-cyan-300">{dg.govern.id} = {fmt(dg.Vwork)} m3</b> (datasheet 5.13). Then M = V/A = {dg.M.toFixed(2)} m; N = I+J+K+L+M = {dg.N.toFixed(2)} m; HHLL = Nx{(1 + p.hhll_margin_pc / 100).toFixed(2)} = {dg.O.toFixed(2)} m; top = +{dg.Ptop.toFixed(2)} m = {dg.H.toFixed(2)} m.</p>
                    </div>
                  );
                })()}
                {/* Worst-case basis across equipment */}
                <div className="rb-inner-card p-3 border border-amber-500/25">
                  <p className="text-xs font-bold text-amber-200 mb-1">Worst case = 15-min cycle (Case-2), not 20-min</p>
                  <p className="text-[11px] text-slate-300 leading-relaxed">
                    Both cases dose the same <b>average {dg.A} m3/h</b> (= electrolyser production), so they only differ in pulse shape. The 15/15 cycle
                    has the longest fill (15-min OFF) and the deepest draw (peak {Math.max(p.case1_dose_m3h, p.case2_dose_m3h)} m3/h), so it sets the biggest swing.
                  </p>
                  <div className="mt-1.5 space-y-0.5 text-[11px]">
                    <Row k="Degassing tank (PD-driven)" v={`Case-2: ${dg.Vwork.toFixed(2)} m3 / ${dg.Vtotal.toFixed(1)} m3`} tone="#fbbf24" sub="vs Case-1 3.83 m3 — 15-min governs" />
                    <Row k="Dosing pumps (PD peak)" v={`${Math.max(p.case1_dose_m3h, p.case2_dose_m3h).toFixed(1)} m3/h`} tone="#fbbf24" sub="peak pulse draw — size pumps for Case-2, not the 22.5 of Case-1" />
                    <Row k="Cells / rectifier (continuous)" v={`${p.hypochloriteOutput_kgh.toFixed(1)} kg/h`} sub="max production — not PD-dependent" />
                    <Row k="H2 blowers + vent (continuous)" v={`${dg.air.toFixed(0)} / ${dg.forcedVent.toFixed(0)} Nm3/h`} sub="tied to H2 = production — not PD-dependent" />
                    <Row k="Seawater filters (continuous)" v={`${dg.A} m3/h`} sub="electrolyser inflow — not PD-dependent" />
                  </div>
                </div>
                <div className="rb-inner-card p-3">
                  <p className="text-xs font-bold text-rose-200 mb-1">Hydrogen management</p>
                  <Row k="H2 generated" v={`${dg.H2.toFixed(2)} Nm3/h`} />
                  <Row k="Dilution air (forced by blower)" v={`${dg.air.toFixed(0)} Nm3/h`} />
                  <Row k="Vent H2 concentration" v={`${dg.ventH2pct.toFixed(2)} % v/v`} tone={dg.h2Ok ? '#34d399' : '#f43f5e'} />
                  <Row k="...as % of the 4% LEL" v={`${dg.lelPct.toFixed(0)} %`} />
                  <Row k="Headspace to LEL if blower trips" v={isFinite(dg.h2ToLEL_min) ? `${dg.h2ToLEL_min.toFixed(1)} min` : ' - '} tone="#fbbf24" sub="Time for stagnant gas space to reach 4%  -  drives the blower-fail interlock / vacuum-breaker." />
                </div>
                <div className="rb-inner-card p-3">
                  <p className="text-xs font-bold text-amber-200 mb-1">Vent duty &amp; breathing (phase-dependent)</p>
                  <Row k="Forced sweep - continuous (ON = OFF)" v={`${dg.forcedVent.toFixed(0)} Nm3/h`}
                    sub={`air ${dg.air.toFixed(0)} + H2 ${dg.H2.toFixed(1)} - cells run nonstop, so same in PD ON and OFF`} />
                  <Row k="PD OFF (tank filling) - VENT DUTY" v={`${dg.ventFill.toFixed(0)} Nm3/h`} tone="#fbbf24"
                    sub={`+ rising liquid ${dg.fillRate.toFixed(1)} + thermal ${dg.Vtherm_out.toFixed(1)} = PEAK, sizes the vent`} />
                  <Row k="PD ON (tank emptying)" v={`${dg.ventDose.toFixed(0)} Nm3/h`}
                    sub="falling liquid draws some air in, so the vent is lower than during filling" />
                  <Row k="IN-breathing (vacuum, blower trip)" v={`${dg.inBreath.toFixed(1)} Nm3/h`}
                    sub={`liquid pump-out ${dg.flowOut.toFixed(1)} + thermal ${dg.Vtherm_in.toFixed(1)}`} />
                </div>
                <div className="rb-inner-card p-3">
                  <p className="text-xs font-bold text-sky-200 mb-1">Vent &amp; vacuum-relief sizing</p>
                  <Row k="Required vent bore" v={`${dg.ventD_req_mm.toFixed(0)} mm`} />
                  <Row k="Selected vent line" v={`DN${dg.ventDN}`} />
                  <Row k="Vent gas velocity" v={`${dg.ventVel.toFixed(1)} m/s`} />
                  <Row k="Vent dP (gooseneck + bends)" v={`${dg.dP_vent_mbar.toFixed(2)} mbar`} tone={dg.ventOk ? '#34d399' : '#f43f5e'} sub={`must stay below the ${p.tank_design_pressure_mbar} mbar design pressure`} />
                  <Row k="Vacuum breaker (in-breathing)" v={`DN${dg.vacDN}`} sub="admits air on blower trip / pump-out to protect against collapse" />
                </div>
                {/* Pressure & vacuum events */}
                <div className="rb-inner-card p-3 border border-rose-500/25">
                  <p className="text-xs font-bold text-rose-200 mb-1">Pressure &amp; vacuum events (when / how)</p>
                  <Row k="NORMAL (blower on, vent open)" v={`+${dg.opPressure_mbar.toFixed(2)} mbar`} tone="#34d399"
                    sub="slight positive = the vent gas pressure drop; the tank runs just above atmospheric" />
                  <Row k="PRESSURE: vent blocks, blower runs" v={`up to +${dg.blowerDeadhead_mbar} mbar`} tone={dg.pressureReliefNeeded ? '#f43f5e' : '#34d399'}
                    sub={`blower deadheads (0.5 barg). ${dg.pressureReliefNeeded ? `exceeds the ${p.tank_design_pressure_mbar} mbar design - needs a pressure relief / blower relief` : 'within design pressure'}`} />
                  <Row k="VACUUM: blower trips, dosing pulls out (vent open)" v={`-${dg.vacOpenVent_mbar.toFixed(2)} mbar`} tone="#34d399"
                    sub="negligible - the open vent admits air. Real hazard is H2 (no dilution) -> trip the cells in seconds" />
                  <Row k="VACUUM: blower trip + vent ALSO blocked" v={`down to ${dg.collapseVac_mbar.toFixed(0)} mbar`} tone="#f43f5e"
                    sub="pressure falls toward seawater vapour pressure -> tank collapse. The vacuum breaker is essential" />
                  <Row k="Design pressure / vacuum" v={`+${p.tank_design_pressure_mbar} / -${p.tank_design_vacuum_mbar} mbar`}
                    sub={`vacuum breaker DN${dg.vacDN} (bore ${dg.vacBrkD_mm.toFixed(0)} mm) admits the ${dg.inBreath.toFixed(0)} m3/h in-breathing at -${p.tank_design_vacuum_mbar} mbar`} />
                </div>
                {/* Datasheet T30X2 verification */}
                {(() => {
                  const DSRow = ({ k, ours, ds, ok }: { k: string; ours: string; ds: string; ok: boolean }) => (
                    <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center text-[12px] py-[3px] border-b border-slate-800/40">
                      <span className="text-slate-400">{k}</span>
                      <span className="font-semibold tabular-nums text-slate-200 text-right w-20">{ours}</span>
                      <span className="tabular-nums text-cyan-300 text-right w-20">{ds}</span>
                      <span className={ok ? 'text-emerald-400' : 'text-amber-400'}>{ok ? 'OK' : '~'}</span>
                    </div>
                  );
                  const near = (a: number, b: number, tol: number) => Math.abs(a - b) <= tol;
                  return (
                    <div className="rb-inner-card p-3 border border-cyan-500/30">
                      <p className="text-xs font-bold text-cyan-200 mb-1">Verification vs issued datasheet T30X2 (4494-M2UT-6-51-0001-002)</p>
                      <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 text-[10px] text-slate-500 pb-1 border-b border-slate-700">
                        <span></span><span className="text-right w-20">our calc</span><span className="text-right w-20">datasheet</span><span></span>
                      </div>
                      <DSRow k="Tank diameter (height-limited)" ours={`${(dg.D * 1000).toFixed(0)} mm`} ds="1750 base" ok={dg.heightOk} />
                      <DSRow k="Tank height vs limit" ours={`${dg.topMM.toFixed(0)} mm`} ds={`<= ${p.tank_max_height_mm}`} ok={dg.heightOk} />
                      <DSRow k="Total volume" ours={`${dg.Vtotal.toFixed(2)} m3`} ds="9.0 base" ok={dg.Vtotal >= 8.9} />
                      <DSRow k="Working volume (physics)" ours={`${dg.Vwork.toFixed(2)} m3`} ds="5.13 m3" ok={near(dg.Vwork, 5.13, 0.25)} />
                      <DSRow k="Vent (N1)  -  physics" ours={`DN${dg.ventDN} - ${dg.dP_vent_mbar.toFixed(1)} mbar`} ds={'10" oversized'} ok={dg.ventOk} />
                      <DSRow k="Dilution air (physics min)" ours={`${dg.air.toFixed(0)} Nm3/h`} ds="650 (margin)" ok={dg.ventH2pct < 1} />
                      <DSRow k="Vent H2" ours={`${dg.ventH2pct.toFixed(2)} %`} ds="< 1 %" ok={dg.ventH2pct < 1} />
                      <DSRow k="Contents / conc." ours="1000 mg/L" ds="1000 mg/L" ok={true} />
                      <p className="text-[10px] text-slate-500 mt-1.5">Vent sized by <strong>physics</strong>: at ~15 m/s the bore is {dg.ventD_req_mm.toFixed(0)} mm to DN{dg.ventDN}, dP {dg.dP_vent_mbar.toFixed(1)} mbar (well under the tank limit). The datasheet's 10" (DN250) is oversized  -  it matches the blower inlet nozzle, not a flow requirement. Dilution air is the physics minimum to hold H2 &lt; 1 % (25 % LEL): air = 99 x H2 ~ {dg.air.toFixed(0)} Nm3/h; the datasheet's 650 carries extra margin. Geometry uses the vendor A-P method (Case-2 worst). The datasheet base design was 1750 mm x 3750 mm (9.0 m3); the diameter is <strong>widened to {(dg.D * 1000).toFixed(0)} mm</strong> so the tank fits under the {p.tank_max_height_mm} mm head-room limit ({dg.topMM.toFixed(0)} mm tall, {dg.Vtotal.toFixed(1)} m3) - the working volume (physics) is unchanged. Flame arrestor removed per HAZOP; tank ATM (blower 0.5 barg).</p>
                    </div>
                  );
                })()}
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-3">
              Vessel sized by the issued <strong>vendor A-P method</strong> (tank top = HHLL + 0.5 m; working volume = liquid between Low and High levels;
              at least 5-min H2 degassing). <strong>Breathing per API 2000</strong>: the vent passes the OUT-breathing (forced dilution air + H2 + liquid pump-in
              + thermal) without exceeding design pressure, and a vacuum breaker admits the IN-breathing (pump-out + thermal) on a blower trip. All values are
              live  -  change a level, the diameter, the Cl2 driver or the dilution ratio and the design (and the datasheet check) re-solve.
            </p>
          </Section>
        );
      })()}

      {tab === 'calcsheet' && (() => {
        const dg = degassingDesign(p, r);
        const acid = ACID_PROPS[p.acidType];
        const H2R = 0.362;                       // Nm3 H2 per kg Cl2 (design-temp basis)
        // -- acid intermediates (mirror calcResults) --
        const Vwash = Math.max(0.5, p.acidWashTank_m3);
        const acidActive_kg = Vwash * (p.acidWashConc_target_pc / 100) * 1000;
        const acidConc_m3   = acidActive_kg / (acid.activeFrac * acid.sg);
        const events_yr     = 365 / Math.max(1, p.acidWashEvery_d);
        const acidConc_m3_yr = acidConc_m3 * events_yr;
        const acidConc_kg_yr = acidConc_m3_yr * acid.sg;
        const acidFlow_Lh    = (Vwash * 1000) / (Math.max(1, p.acidWashDuration_min) / 60);
        const acidFlow_duty  = p.acidPumps_duty > 0 ? acidFlow_Lh / p.acidPumps_duty : acidFlow_Lh;
        const acid_dP_Pa     = p.acidPumpHead_bar * 1e5 + p.acidPump_staticPressure_mbar * 100;
        const acidkW         = (acidFlow_duty / 1000 / 3600) * acid_dP_Pa / 0.55 / 1000;

        const Eqn = ({ n, title, formula, sub, result, note }:
          { n: string; title: string; formula: string; sub?: string; result: string; note?: string }) => (
          <div className="rb-inner-card p-3 mb-2">
            <div className="flex gap-2.5">
              <span className="text-cyan-400 font-bold text-[11px] mt-0.5 shrink-0">{n}</span>
              <div className="flex-1 min-w-0">
                <p className="text-[12.5px] font-semibold text-slate-200">{title}</p>
                <p className="font-mono text-[11px] text-sky-300 mt-1 break-words">{formula}</p>
                {sub && <p className="font-mono text-[11px] text-slate-400 break-words">= {sub}</p>}
                <p className="font-mono text-[12.5px] text-emerald-300 font-bold break-words">= {result}</p>
                {note && <p className="text-[10px] text-slate-500 mt-1">{note}</p>}
              </div>
            </div>
          </div>
        );
        const H = ({ children }: { children: ReactNode }) => (
          <h4 className="text-[13px] font-bold text-cyan-300 uppercase tracking-wide mt-4 mb-2">{children}</h4>
        );
        return (
          <Section title="Calculation sheet  -  degassing tank, acid tank & acid pumps (step-by-step)" icon={<FileText className="w-4 h-4" />}>
            <p className="text-[11px] text-slate-400 mb-3">
              Every equation, with the current live inputs substituted. Change any input on the other tabs and these re-compute. Constants:
              H2 yield 0.362 Nm3/kg Cl2, air density 1.2 kg/m3, pump efficiency 0.67 (dosing) / 0.55 (acid).
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-5">
              <div>
                <H>A - Degassing tank</H>
                <Eqn n="A1" title="Chlorine production (design input)"
                  formula="Cl2 = hypochlorite output"
                  result={`${p.hypochloriteOutput_kgh.toFixed(2)} kg/h`} />
                <Eqn n="A2" title="Hydrogen co-generated (~1 mol H2 per mol Cl2)"
                  formula="H2 = Cl2 x 0.362 Nm3/kg"
                  sub={`${p.hypochloriteOutput_kgh.toFixed(2)} x ${H2R}`}
                  result={`${dg.H2.toFixed(2)} Nm3/h`} />
                <Eqn n="A3" title="Forced dilution air for safe vent (< 1 % H2)"
                  formula="Q_air = H2 x (air:H2 ratio)"
                  sub={`${dg.H2.toFixed(2)} x ${p.vent_dilution_factor}`}
                  result={`${dg.air.toFixed(0)} Nm3/h`} />
                <Eqn n="A4" title="Vent hydrogen concentration"
                  formula="H2% = H2 / (H2 + Q_air) x 100"
                  sub={`${dg.H2.toFixed(2)} / (${dg.H2.toFixed(2)} + ${dg.air.toFixed(0)}) x 100`}
                  result={`${dg.ventH2pct.toFixed(2)} % v/v  (${dg.lelPct.toFixed(0)} % of LEL)`} />
                <Eqn n="A5" title="Degassing minimum volume (G) & worst working volume (H)"
                  formula="G = A*F/60 ;  H_on = E-C+G ;  H_off = C-E+G"
                  sub={`G = ${dg.A}x${p.degas_min_min}/60 = ${dg.G.toFixed(2)} ;  C2 OFF: C ${dg.c2.Coff.toFixed(2)} - E 0 + G ${dg.G.toFixed(2)}`}
                  result={`worst = ${dg.govern.id} = ${dg.Vwork.toFixed(2)} m3  (datasheet 5.13)`}
                  note="Vendor A-H: tank fills during PD-OFF, empties during PD-ON; worst swing + 5-min degassing governs." />
                <Eqn n="A6" title="Cross-section & swing height (M)"
                  formula="A = pi/4*D2 ;  M = V_w / A"
                  sub={`pi/4 x ${(p.tank_diameter_mm / 1000).toFixed(3)}2 = ${dg.area.toFixed(3)} ;  ${dg.Vwork.toFixed(2)} / ${dg.area.toFixed(3)}`}
                  result={`M = ${dg.M.toFixed(2)} m  (Low to High swing)`} />
                <Eqn n="A7" title="Tank height (I-P stack) & total volume"
                  formula="N = I+J+K+L+M ;  HHLL = Nx(1+margin) ;  H = HHLL + P"
                  sub={`N = ${dg.I}+${dg.J}+${dg.K}+${dg.L}+${dg.M.toFixed(2)} = ${dg.N.toFixed(2)} ;  x${(1 + p.hhll_margin_pc / 100).toFixed(2)} = ${dg.O.toFixed(2)} ;  +${dg.Ptop.toFixed(2)}`}
                  result={`H = ${dg.H.toFixed(2)} m,  V_tot = A*H = ${dg.Vtotal.toFixed(2)} m3  (datasheet 3.75 m / 9.0 m3)`}
                  note={`Derived levels: LLLL ${dg.lvl_LLLL.toFixed(0)} - LLL ${dg.lvl_LLL.toFixed(0)} - HLL ${dg.lvl_HLL.toFixed(0)} - HHLL ${dg.lvl_HHLL.toFixed(0)} mm.`} />
                <Eqn n="A8" title="Continuous forced sweep (same in PD ON and OFF)"
                  formula="Q_sweep = Q_air + H2"
                  sub={`${dg.air.toFixed(0)} + ${dg.H2.toFixed(1)}`}
                  result={`${dg.forcedVent.toFixed(0)} Nm3/h`}
                  note="Cells run nonstop, so H2 and blower air are constant - not tied to the dosing pulse." />
                <Eqn n="A9" title="Vent duty = PD-OFF peak (tank filling)"
                  formula="Q_vent = Q_sweep + rising liquid (A+B) + thermal"
                  sub={`${dg.forcedVent.toFixed(0)} + ${dg.fillRate.toFixed(1)} + ${dg.Vtherm_out.toFixed(1)}`}
                  result={`${dg.outBreath.toFixed(0)} Nm3/h  (PD ON is lower: ${dg.ventDose.toFixed(0)})`}
                  note="During PD OFF the tank fills, the rising liquid pushes extra gas out -> vent PEAKS here, not during ON." />
                <Eqn n="A10" title="IN-breathing  -  vacuum duty (blower trip)"
                  formula="Q_in.vac = pump-out + 0.25*V_tot^0.7"
                  sub={`${dg.flowOut.toFixed(1)} + ${dg.Vtherm_in.toFixed(1)}`}
                  result={`${dg.inBreath.toFixed(1)} Nm3/h`} />
                <Eqn n="A11" title="Vent line bore (from out-breathing & target velocity)"
                  formula="A = (Q_out/3600)/v ;  d = sqrt(4A/pi)"
                  sub={`(${dg.outBreath.toFixed(0)}/3600) / ${p.vent_velocity_target_ms} to d = ${dg.ventD_req_mm.toFixed(0)} mm`}
                  result={`DN${dg.ventDN}  (actual velocity ${dg.ventVel.toFixed(1)} m/s)`} />
                <Eqn n="A12" title="Vent pressure drop vs design pressure"
                  formula="dP = K * 0.5 * rho_air * v2   (K ~ 4, rho = 1.2)"
                  sub={`4 x 0.5 x 1.2 x ${dg.ventVel.toFixed(1)}2`}
                  result={`${dg.dP_vent_mbar.toFixed(2)} mbar  ${dg.ventOk ? '<=' : '>'} ${p.tank_design_pressure_mbar} mbar design ${dg.ventOk ? 'OK' : 'X'}`} />
              </div>

              <div>
                <H>B - Acid wash tank &amp; pumps</H>
                <Eqn n="B0" title="Acid selected"
                  formula="acid = type, strength, SG"
                  result={`${acid.name}  (active ${(acid.activeFrac * 100).toFixed(0)} %, SG ${acid.sg})`} />
                <Eqn n="B1" title="Wash solution volume per clean"
                  formula="V_wash = acid wash tank volume"
                  result={`${Vwash.toFixed(2)} m3`}
                  note="One batch fills the CIP/wash tank; recirculated through the cells." />
                <Eqn n="B2" title="Active acid (HCl) mass per clean"
                  formula="m_acid = V_wash x conc% x 1000"
                  sub={`${Vwash.toFixed(2)} x ${(p.acidWashConc_target_pc / 100).toFixed(3)} x 1000`}
                  result={`${acidActive_kg.toFixed(1)} kg`} />
                <Eqn n="B3" title="Concentrate acid volume per clean"
                  formula="V_conc = m_acid / (active fraction x SG)"
                  sub={`${acidActive_kg.toFixed(1)} / (${acid.activeFrac} x ${acid.sg})`}
                  result={`${(acidConc_m3 * 1000).toFixed(1)} L  (${acidConc_m3.toFixed(3)} m3)`} />
                <Eqn n="B4" title="Cleans per year"
                  formula="N = 365 / interval"
                  sub={`365 / ${p.acidWashEvery_d}`}
                  result={`${events_yr.toFixed(1)} cleans/yr`} />
                <Eqn n="B5" title="Concentrate acid consumption per year"
                  formula="V_yr = V_conc x N ;  m_yr = V_yr x SG"
                  sub={`${acidConc_m3.toFixed(3)} x ${events_yr.toFixed(1)}`}
                  result={`${(acidConc_m3_yr * 1000).toFixed(0)} L/yr  (${acidConc_kg_yr.toFixed(0)} kg/yr)`} />
                <Eqn n="B6" title="Acid tank working volume"
                  formula="V_tank >= V_wash (one batch) + storage margin"
                  result={`${p.acidWashTank_m3.toFixed(2)} m3  (specced)`}
                  note="Sized for one full wash batch; add days of concentrate storage if delivered in bulk." />
                <Eqn n="B7" title="Acid recirculation / dosing pump flow"
                  formula="Q = V_wash x 1000 / (duration / 60)"
                  sub={`${Vwash.toFixed(2)} x 1000 / (${p.acidWashDuration_min}/60)`}
                  result={`${acidFlow_Lh.toFixed(0)} L/h total  to  ${acidFlow_duty.toFixed(0)} L/h per duty pump (${p.acidPumps_duty}x)`} />
                <Eqn n="B8" title="Acid pump differential pressure"
                  formula="dP = head x 1e5 + static x 100"
                  sub={`${p.acidPumpHead_bar} bar x 105 + ${p.acidPump_staticPressure_mbar} mbar x 100`}
                  result={`${(acid_dP_Pa / 1e5).toFixed(2)} bar  (${(acid_dP_Pa / 1000).toFixed(0)} kPa)`} />
                <Eqn n="B9" title="Acid pump shaft power (per duty pump)"
                  formula="P = (Q/1000/3600) x dP / eta   (eta = 0.55)"
                  sub={`(${acidFlow_duty.toFixed(0)}/1000/3600) x ${(acid_dP_Pa / 1000).toFixed(0)}kPa / 0.55`}
                  result={`${acidkW.toFixed(3)} kW  (${(acidkW * 1000).toFixed(0)} W) per pump`} />
                <Eqn n="B10" title="Acid pump line sizing"
                  formula="DN from Q & target velocity (0.5 / 1.0 m/s)"
                  result={`inlet DN${p.acidPump_inlet_DN} - outlet DN${p.acidPump_outlet_DN}  (out ${r.acid_out_vel_ms.toFixed(2)} m/s)`} />

                <H>C - NaOCl dosing (for reference)</H>
                <Eqn n="C1" title="Chlorine dose mass to process"
                  formula="m_dose = process flow x target ppm / 1000"
                  sub={`${p.dosing_process_flow_m3h} x ${p.dosing_target_ppm} / 1000`}
                  result={`${(p.dosing_process_flow_m3h * p.dosing_target_ppm / 1000).toFixed(2)} kg/h Cl2`} />
                <Eqn n="C2" title="Dosing pump volumetric flow"
                  formula="Q_dose = m_dose x 1e6 / delivered conc (mg/L)"
                  sub={`/ ${p.dosing_delivered_conc_mgL} mg/L available Cl`}
                  result={`${(r.dosingPumpFlow_total_demand_Lh).toFixed(0)} L/h total  to  ${(dg.flowOut * 1000).toFixed(0)} L/h drawn from tank`} />
              </div>
            </div>
          </Section>
        );
      })()}

      {tab === 'config' && (
        <Section title="System configuration - select preset" icon={<Settings className="w-4 h-4" />}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {([
              { id: 'oneOne',   title: '1 duty + 1 standby', desc: 'Single duty module, full N+1 redundancy across electrolyser, dosing, blower and acid pumps. Smallest footprint, typical for offshore platforms and small intakes.' },
              { id: 'twoOne',   title: '2 duty + 1 standby', desc: 'Split duty across two modules with one common standby; matched 2-duty dosing pumps and blowers. Mid-size plants (1 000 - 3 000 m3/h).' },
              { id: 'threeOne', title: '3 duty + 1 standby', desc: 'Large intake configuration; three duty modules with one common standby. Used for >= 3 000 m3/h coastal plants.' },
              { id: 'custom',   title: 'Custom',             desc: 'No preset applied - configure every duty / standby count manually in the Process tab.' },
            ] as { id: SystemPreset; title: string; desc: string }[]).map(opt => (
              <button key={opt.id} onClick={() => choosePreset(opt.id)}
                className={`text-left p-3 rounded-lg border transition-colors ${
                  preset === opt.id
                    ? 'bg-cyan-600/15 border-cyan-500'
                    : 'bg-slate-900/40 border-slate-700 hover:border-slate-500'
                }`}>
                <div className="text-sm font-semibold text-cyan-200 mb-1">{opt.title}</div>
                <div className="text-[11px] text-slate-400 leading-snug">{opt.desc}</div>
              </button>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
            <div className="rounded-md bg-slate-900/40 border border-slate-700 px-3 py-2">
              <div className="text-slate-400">Electrolyser modules</div>
              <div className="text-slate-100 font-semibold">{p.modules_installed} duty + {p.modules_standby} standby</div>
            </div>
            <div className="rounded-md bg-slate-900/40 border border-slate-700 px-3 py-2">
              <div className="text-slate-400">NaOCl dosing pumps</div>
              <div className="text-slate-100 font-semibold">{p.dosingPumps_duty} duty + {p.dosingPumps_standby} standby</div>
            </div>
            <div className="rounded-md bg-slate-900/40 border border-slate-700 px-3 py-2">
              <div className="text-slate-400">H2 dilution blowers</div>
              <div className="text-slate-100 font-semibold">{p.blower_duty} duty + {p.blower_standby} standby</div>
            </div>
            <div className="rounded-md bg-slate-900/40 border border-slate-700 px-3 py-2">
              <div className="text-slate-400">Acid wash pumps</div>
              <div className="text-slate-100 font-semibold">{p.acidPumps_duty} duty + {p.acidPumps_standby} standby</div>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-slate-400 italic">
            Selecting a preset overwrites the duty / standby counts across all four equipment groups in one click. After applying
            a preset you may still tune individual counts in the Process tab - the preset will switch to "Custom" if you do.
          </p>
        </Section>
      )}

      {tab === 'chemistry' && (
        <Section title="Detailed seawater chemistry (used in process calculations)" icon={<Beaker className="w-4 h-4" />}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <NumField label="TDS"                  suffix="mg/L"             value={p.TDS_mgL}                min={0}    step={100}  onChange={v => setP({ ...p, TDS_mgL: v })} help="Reference 35 000 at S=35 ppt" />
            <NumField label="Chloride (Cl-)"       suffix="mg/L"             value={p.chloride_mgL}           min={0}    step={100}  onChange={v => setP({ ...p, chloride_mgL: v })} help="Drives Cl2 yield; ref 19 000" />
            <NumField label="Bromide (Br-)"        suffix="mg/L"             value={p.bromide_mgL}            min={0}    step={1}    onChange={v => setP({ ...p, bromide_mgL: v })} help="Bromate formation precursor" />
            <NumField label="Sulphate (SO4 2-)"    suffix="mg/L"             value={p.sulphate_mgL}           min={0}    step={50}   onChange={v => setP({ ...p, sulphate_mgL: v })} />
            <NumField label="Calcium hardness"     suffix="mg/L as CaCO3"    value={p.calciumHardness_mgL}    min={0}    step={10}   onChange={v => setP({ ...p, calciumHardness_mgL: v })} help="Cathodic CaCO3 scale" />
            <NumField label="Magnesium hardness"   suffix="mg/L as CaCO3"    value={p.magnesiumHardness_mgL}  min={0}    step={50}   onChange={v => setP({ ...p, magnesiumHardness_mgL: v })} help="Cathodic Mg(OH)2 scale" />
            <NumField label="Total alkalinity"     suffix="mg/L as CaCO3"    value={p.totalAlkalinity_mgL}    min={0}    step={5}    onChange={v => setP({ ...p, totalAlkalinity_mgL: v })} help="Neutralisation buffer" />
            <NumField label="Free CO2"             suffix="mg/L"             value={p.freeCO2_mgL}            min={0}    step={0.1}  onChange={v => setP({ ...p, freeCO2_mgL: v })} />
            <NumField label="Silica (SiO2)"        suffix="mg/L"             value={p.silica_mgL}             min={0}    step={0.5}  onChange={v => setP({ ...p, silica_mgL: v })} />
            <NumField label="Iron + Manganese"     suffix="mg/L"             value={p.ironMn_mgL}             min={0}    step={0.01} onChange={v => setP({ ...p, ironMn_mgL: v })} help="Electrode-fouling risk if > 0.3" />
            <NumField label="Total organic carbon" suffix="mg/L"             value={p.organic_TOC_mgL}        min={0}    step={0.1}  onChange={v => setP({ ...p, organic_TOC_mgL: v })} help="THM / HAA precursor pool" />
            <NumField label="pH (feed)"                                       value={p.pH_feed}                min={5}    max={10}    step={0.1}  onChange={v => setP({ ...p, pH_feed: v })} />
          </div>

          <h4 className="mt-4 mb-2 text-xs font-semibold text-cyan-300">Live effects on process calculations</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="Cl- correction factor"       value={r.Cl_factor.toFixed(3)}                  unit="x nominal CE" tone={r.Cl_factor >= 0.9 ? 'good' : 'warn'} />
            <KPI label="Adjusted current eff."       value={r.adjusted_CE_pc.toFixed(1)}             unit="%"            tone={r.adjusted_CE_pc >= 85 ? 'good' : 'warn'} />
            <KPI label="Total hardness"              value={r.totalHardness_mgL.toFixed(0)}          unit="mg/L as CaCO3" />
            <KPI label="Recommended wash interval"   value={r.recommendedWashInterval_d.toFixed(0)}  unit="days" tone={p.acidWashEvery_d <= r.recommendedWashInterval_d * 1.1 ? 'good' : 'warn'} />
            <KPI label="Bromate formation risk"      value={r.bromate_risk}                          unit=""             tone={r.bromate_risk === 'High' || r.bromate_risk === 'Moderate' ? 'warn' : 'good'} />
            <KPI label="Neutralisation alkalinity"   value={r.neutralisation_NaOH_kg_per_event_basis.toFixed(0)} unit="mg/L (basis)" />
            <KPI label="Iron + Mn"                   value={p.ironMn_mgL.toFixed(2)}                 unit="mg/L"         tone={p.ironMn_mgL < 0.3 ? 'good' : 'warn'} />
            <KPI label="TOC"                         value={p.organic_TOC_mgL.toFixed(2)}            unit="mg/L"         tone={p.organic_TOC_mgL < 5 ? 'good' : 'warn'} />
          </div>
          <p className="mt-3 text-[11px] text-slate-400 italic">
            Chloride drives the effective current efficiency, the cell-stack current and the AC power - therefore the entire
            sizing chain re-balances live when this value is changed. Hardness drives the recommended acid-wash interval
            (CaCO3 + Mg(OH)2 scaling rate). Bromide flags the bromate formation pathway downstream. Iron + Mn is the
            electrode-fouling indicator (target {'<'} 0.3 mg/L).
          </p>
        </Section>
      )}

      {tab !== 'process' ? null : (
      <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <KPI label="Seawater PD-off" value={p.seawaterFlow_PDoff_m3h.toFixed(1)}   unit="m3/h" />
        <KPI label="Electrolyser in" value={p.electrolyserInlet_m3h.toFixed(1)}    unit="m3/h" />
        <KPI label="NaOCl out (Cl2)" value={p.hypochloriteOutput_kgh.toFixed(1)}   unit="kg/h" tone="good" />
        <KPI label="Free Cl2 dose"   value={r.freeCl2_dose_mgL.toFixed(1)}         unit="mg/L"
          tone={r.freeCl2_dose_mgL >= 0.5 && r.freeCl2_dose_mgL <= 10 ? 'good' : 'warn'} />
        <KPI label="AC power (stack)" value={r.power_total_AC_kW.toFixed(2)} unit="kW" />
        <KPI label="Dosing pumps"     value={(r.dosingPump_kW_each * p.dosingPumps_duty).toFixed(2)} unit="kW total" />
        <KPI label="Blowers"          value={(r.blower_motor_kW_each * p.blower_duty).toFixed(2)} unit="kW total" />
        <KPI label="H2 vent"          value={r.H2_Nm3h.toFixed(2)} unit="Nm3/h" tone={r.vent_safe ? 'good' : 'bad'} />
      </div>

      {/* PROCESS FLOW DIAGRAM */}
      <Section title="Process flow diagram - live values" icon={<Activity className="w-4 h-4" />}
        action={
          <button onClick={() => { setPidFull(true); try { document.documentElement.requestFullscreen?.(); } catch { /* ignore */ } }}
            className="px-3 py-1.5 rounded-lg bg-cyan-600/20 text-cyan-200 text-xs font-bold border border-cyan-500/50 hover:bg-cyan-600/30">
            ⛶ Full-screen P&amp;ID
          </button>
        }>
        <ProcessDiagram p={p} r={r} />
        <p className="mt-2 text-[10px] text-slate-500 italic">
          Streams and equipment update live with the inputs on the left. Green tones = passing safety / engineering envelope;
          amber = warning; red = critical. Amber tags (e.g. {MESAIEED_TAGS.cells}) are the P&ID equipment numbers from the
          QatarEnergy Mesaieed drawings (4737-M2UT-6-51-0001).
        </p>
      </Section>

      {/* DATASHEET VERIFICATION — live calc vs QatarEnergy Mesaieed datasheets */}
      <Section title="Sizing verification — live calc vs QKQ2831 / Mesaieed (Perocon Rev.2)" icon={<FileText className="w-4 h-4" />}
        action={
          <button onClick={async () => {
              setP(prev => ({ ...prev, ...PRESET_VENDOR }));
              setPreset('oneOne');
              (await import('sonner')).toast.success('Loaded Vendor sizing values');
            }}
            className="px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-300 text-[11px] font-bold border border-emerald-500/30 hover:bg-emerald-500/20">
            🏭 Load vendor sizing
          </button>
        }>
        {(() => {
          const groups = Array.from(new Set(MESAIEED_REF.map(x => x.group)));
          const okCount = MESAIEED_REF.filter(x => !x.check || x.check(p, r)).length;
          return (
            <div className="space-y-3">
              <p className="text-[11px] text-slate-400">
                Source: the vendor "System Design Calculations" workbook (QKQ2831, Perocon, Rev.2) and the issued P&ID datasheets
                (tank {MESAIEED_TAGS.degasTank}). These values are now the page <strong>defaults</strong>, so on load the live
                calc reproduces each design figure. <span className="text-emerald-300 font-bold">{okCount}/{MESAIEED_REF.length} within tolerance.</span>
              </p>
              {groups.map(g => (
                <div key={g}>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">{g}</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead className="text-slate-500 border-b border-slate-800">
                        <tr><th className="text-left py-1 pr-3 font-medium">Parameter</th><th className="text-right py-1 px-3 font-medium">Datasheet</th><th className="text-right py-1 px-3 font-medium">Live calc</th><th className="text-center py-1 pl-3 font-medium">Match</th></tr>
                      </thead>
                      <tbody>
                        {MESAIEED_REF.filter(x => x.group === g).map((row, i) => {
                          const ok = !row.check || row.check(p, r);
                          return (
                            <tr key={i} className="border-b border-slate-800/40">
                              <td className="py-1 pr-3 text-slate-300">{row.label}</td>
                              <td className="py-1 px-3 text-right text-slate-400 tabular-nums">{row.datasheet}</td>
                              <td className={`py-1 px-3 text-right tabular-nums font-bold ${ok ? 'text-slate-200' : 'text-amber-300'}`}>{row.live(p, r)}</td>
                              <td className="py-1 pl-3 text-center">{ok ? <span className="text-emerald-400">✓</span> : <span className="text-amber-400">≈</span>}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              <p className="text-[10px] text-slate-600 italic">
                VTA / HOLD items remain subject to vendor confirmation. "≈" flags a live value outside the datasheet tolerance band
                (e.g. after you change the production target) — load the datasheet preset to realign.
              </p>
            </div>
          );
        })()}
      </Section>

      {/* TWO SOURCE DOCUMENTS — vendor sizing vs issued datasheet */}
      <Section title="Two source documents — Vendor sizing (Perocon) vs Issued datasheet (QatarEnergy)" icon={<FileText className="w-4 h-4" />}
        action={
          <div className="flex gap-2">
            <button onClick={async () => { setP(prev => ({ ...prev, ...PRESET_VENDOR })); setPreset('oneOne'); (await import('sonner')).toast.success('Loaded Vendor sizing'); }}
              className="px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-300 text-[11px] font-bold border border-emerald-500/30 hover:bg-emerald-500/20">🏭 Load vendor</button>
            <button onClick={async () => { setP(prev => ({ ...prev, ...PRESET_DATASHEET })); setPreset('oneOne'); (await import('sonner')).toast.success('Loaded Issued datasheet'); }}
              className="px-2.5 py-1 rounded-lg bg-sky-500/10 text-sky-300 text-[11px] font-bold border border-sky-500/30 hover:bg-sky-500/20">📄 Load datasheet</button>
          </div>
        }>
        <p className="text-[11px] text-slate-400 mb-2">
          Both describe the <strong>same Mesaieed plant</strong> but at different stages: the Perocon "System Design Calculations"
          (QKQ2831 — the manufacturer's sizing) and the QatarEnergy issued process datasheets (P&ID 4737-M2UT). Neither is "wrong" —
          they round/derate differently. <strong>Load either configuration</strong> (buttons above, or the column headers) to use it
          as the working basis, then adjust on site. Rows shaded amber are genuine engineering differences.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="text-slate-500 border-b border-slate-700">
              <tr>
                <th className="text-left py-1 pr-3 font-medium">Parameter</th>
                <th className="text-left py-1 px-3 font-medium">
                  <button onClick={async () => { setP(prev => ({ ...prev, ...PRESET_VENDOR })); setPreset('oneOne'); (await import('sonner')).toast.success('Loaded Vendor sizing'); }}
                    className="text-emerald-300 hover:underline font-bold" title="Load this configuration">Vendor sizing ⤓</button>
                </th>
                <th className="text-left py-1 px-3 font-medium">
                  <button onClick={async () => { setP(prev => ({ ...prev, ...PRESET_DATASHEET })); setPreset('oneOne'); (await import('sonner')).toast.success('Loaded Issued datasheet'); }}
                    className="text-sky-300 hover:underline font-bold" title="Load this configuration">Issued datasheet ⤓</button>
                </th>
                <th className="text-left py-1 pl-3 font-medium">Note</th>
              </tr>
            </thead>
            <tbody>
              {VENDOR_VS_DATASHEET.map((row, i) => (
                <tr key={i} className={`border-b border-slate-800/40 ${row.diff ? 'bg-amber-500/10' : ''}`}>
                  <td className="py-1 pr-3 text-slate-300 font-medium">{row.param}</td>
                  <td className="py-1 px-3 text-emerald-300 tabular-nums">{row.vendor}</td>
                  <td className="py-1 px-3 text-sky-300 tabular-nums">{row.datasheet}</td>
                  <td className="py-1 pl-3 text-slate-400">{row.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-500 italic mt-2">
          On the dosing point specifically: the page now follows the vendor's <strong>23.25 m³/h</strong> dosing duty. The earlier
          3717 L/h figure was the page's own generic concentrated-product model — not a Mesaieed assumption — and has been corrected.
        </p>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* INPUTS COLUMN */}
        <div className="space-y-5">
          <Section title="1. Plant flow architecture + seawater feed" icon={<Droplets className="w-4 h-4" />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <NumField label="Seawater flow - PD OFF (max)"  suffix="m3/h" value={p.seawaterFlow_PDoff_m3h}  min={1}  max={500}  step={0.5} onChange={v => setP({ ...p, seawaterFlow_PDoff_m3h: v })} help="Max treated-water flow when pressure-drop restriction is off" />
              <NumField label="Electrolyser inlet flow"       suffix="m3/h" value={p.electrolyserInlet_m3h}   min={0.5} max={300}  step={0.5} onChange={v => setP({ ...p, electrolyserInlet_m3h: v })} help="Brine feed to electrolyser cells (split off the seawater intake)" />
              <NumField label="Hypochlorite output (Cl2)"     suffix="kg/h" value={p.hypochloriteOutput_kgh}  min={0.1} max={500}  step={0.1} onChange={v => setP({ ...p, hypochloriteOutput_kgh: v })} help="Product Cl2 mass produced - drives the cell + dosing sizing" />
              <NumField label="Salinity"                      suffix="ppt"   value={p.salinity_ppt}           min={0}   max={45}   step={0.5} onChange={v => setP({ ...p, salinity_ppt: v })} help="Open ocean ~ 35; brackish 5 - 25" />
              <NumField label="Temperature"                   suffix="deg C" value={p.temperature_C}          min={-2}  max={45}   step={0.5} onChange={v => setP({ ...p, temperature_C: v })} help="Sets conductivity and Cl2 solubility" />
              <NumField label="pH (feed)"                                    value={p.pH_feed}                min={5}   max={10}   step={0.1} onChange={v => setP({ ...p, pH_feed: v })} />
              <NumField label="TSS"                           suffix="mg/L"  value={p.TSS_mgL}                min={0}   max={500}  step={1}   onChange={v => setP({ ...p, TSS_mgL: v })} />
              <NumField label="Turbidity"                     suffix="NTU"   value={p.turbidity_NTU}          min={0}   max={50}   step={0.5} onChange={v => setP({ ...p, turbidity_NTU: v })} />
              <SelField label="Duty mode" value={p.dutyMode} options={[
                { value: 'continuous', label: 'Continuous (24/7)' },
                { value: 'shock',      label: 'Intermittent / shock (set hours/day)' },
                { value: 'cyclic',     label: 'Cyclic ON / OFF (e.g. 30 / 30, 20 / 20)' },
              ]} onChange={v => setP({ ...p, dutyMode: v })} />
              {p.dutyMode === 'shock' && (
                <NumField label="Shock hours / day" suffix="h" value={p.shockHoursPerDay} min={0.5} max={24} step={0.5} onChange={v => setP({ ...p, shockHoursPerDay: v })} />
              )}
              {p.dutyMode === 'cyclic' && (
                <>
                  <NumField label="Cycle ON time"  suffix="min" value={p.cycleOnMin}  min={1} max={240} step={1} onChange={v => setP({ ...p, cycleOnMin: v })}  help="Rectifier energised; Cl₂ produced" />
                  <NumField label="Cycle OFF time" suffix="min" value={p.cycleOffMin} min={1} max={240} step={1} onChange={v => setP({ ...p, cycleOffMin: v })} help="Rectifier idle; storage tank covers dosing demand" />
                </>
              )}
            </div>

            {/* Cyclic-mode derived metrics + presets */}
            {p.dutyMode === 'cyclic' && (() => {
              const cyclesPerDay = 1440 / cycleTotalMin;
              const dailyCl2_kg = p.hypochloriteOutput_kgh * dailyDuty_h;
              const dailyCl2_continuous_kg = p.hypochloriteOutput_kgh * 24;
              const minStorageH = p.cycleOffMin / 60; // storage tank must cover one OFF segment
              const storageOK = (p.storageTank_h ?? 2) >= minStorageH;
              return (
                <div className="mt-3 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] text-slate-400 mr-1">Presets:</span>
                    {[
                      { on: 30, off: 30, label: '30/30' },
                      { on: 20, off: 20, label: '20/20' },
                      { on: 15, off: 45, label: '15/45' },
                      { on: 60, off: 60, label: '60/60' },
                    ].map(pr => (
                      <button key={pr.label} onClick={() => setP({ ...p, cycleOnMin: pr.on, cycleOffMin: pr.off })}
                        className={`px-2 py-1 rounded-md text-[10px] font-bold border ${
                          p.cycleOnMin === pr.on && p.cycleOffMin === pr.off
                            ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200'
                            : 'bg-slate-800/40 border-slate-700 text-slate-300 hover:border-cyan-500/40 hover:text-cyan-300'
                        }`}>
                        {pr.label} min
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                    <div className="rounded-md bg-emerald-900/20 border border-emerald-700 px-2 py-1.5">
                      <span className="text-slate-400">ON hours/day </span>
                      <span className="text-emerald-200 font-semibold">{dailyDuty_h.toFixed(1)} h</span>
                      <div className="text-[10px] text-slate-500">{(cycleDutyFrac * 100).toFixed(0)}% duty</div>
                    </div>
                    <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                      <span className="text-slate-400">Cycles/day </span>
                      <span className="text-cyan-200 font-semibold">{cyclesPerDay.toFixed(0)}</span>
                      <div className="text-[10px] text-slate-500">@ {cycleTotalMin.toFixed(0)} min each</div>
                    </div>
                    <div className="rounded-md bg-amber-900/20 border border-amber-700 px-2 py-1.5">
                      <span className="text-slate-400">Cl₂/day (this duty) </span>
                      <span className="text-amber-200 font-semibold">{dailyCl2_kg.toFixed(1)} kg</span>
                      <div className="text-[10px] text-slate-500">vs continuous {dailyCl2_continuous_kg.toFixed(0)} kg</div>
                    </div>
                    <div className={`rounded-md border px-2 py-1.5 ${storageOK ? 'bg-emerald-900/20 border-emerald-700' : 'bg-rose-900/20 border-rose-700'}`}>
                      <span className="text-slate-400">Storage need </span>
                      <span className={`font-semibold ${storageOK ? 'text-emerald-200' : 'text-rose-200'}`}>{minStorageH.toFixed(2)} h</span>
                      <div className="text-[10px] text-slate-500">{storageOK ? '✓ tank covers OFF' : `✗ raise to ≥ ${minStorageH.toFixed(2)} h`}</div>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500 italic leading-relaxed">
                    Cyclic mode: rectifier energises for {p.cycleOnMin} min, idles for {p.cycleOffMin} min, then repeats — {cyclesPerDay.toFixed(0)} cycles per 24 h.
                    Average daily Cl₂ scales by the duty fraction ({(cycleDutyFrac * 100).toFixed(0)} % of the instantaneous rate).
                    The hypochlorite storage tank must hold at least <strong className="text-slate-300">{minStorageH.toFixed(2)} h</strong> of dosing demand to cover one OFF segment without interrupting downstream injection.
                    The blower runs through ON segments plus pre-/post-purge ({p.blower_purge_min} min each side, every cycle).
                  </p>
                </div>
              );
            })()}
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Free Cl2 dose (derived)</span>{' '}
                <span className="text-cyan-200 font-semibold">{r.freeCl2_dose_mgL.toFixed(2)} mg/L</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Brine / module (derived)</span>{' '}
                <span className="text-cyan-200 font-semibold">{r.brineFlow_per_module_m3h.toFixed(2)} m3/h</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Density</span>{' '}
                <span className="text-slate-100 font-medium">{r.rho.toFixed(1)} kg/m3</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Conductivity</span>{' '}
                <span className="text-slate-100 font-medium">{r.cond.toFixed(1)} mS/cm</span>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-slate-400 italic">
              The three plant flows are interdependent: the hypochlorite output divided by the PD-off seawater flow defines the
              free Cl2 dose; the electrolyser inlet divided by the module count defines the per-module brine flow used in the
              cell-stack and pipe-velocity calculations.
            </p>
          </Section>

          <Section title="1b. Seawater pre-filter (sized from inflow)" icon={<Droplets className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Micron rating"        suffix="um"          value={p.filter_micron_um}        min={20}  max={500}  step={5}  onChange={v => setP({ ...p, filter_micron_um: v })} help="Electrochlorination pre-filter typ. 80 - 100 um" />
              <NumField label="Design DP trigger"    suffix="bar"         value={p.filter_designDP_bar}     min={0.1} max={1.5}  step={0.05} onChange={v => setP({ ...p, filter_designDP_bar: v })} help="Backwash starts at this clean-to-dirty DP" />
              <NumField label="Filtration rate"      suffix="m3/m2/h"     value={p.filter_rate_m3_m2_h}     min={20}  max={250}  step={5}  onChange={v => setP({ ...p, filter_rate_m3_m2_h: v })} help="Self-cleaning strainer 80 - 150 envelope" />
              <SelField label="Inlet (standard)"                                value={String(p.filter_inlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, filter_inlet_DN: parseInt(v, 10) || 65 })} />
              <SelField label="Outlet (standard)"                               value={String(p.filter_outlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, filter_outlet_DN: parseInt(v, 10) || 65 })} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Filter flow (= PD-off)</span>{' '}
                <span className="text-cyan-200 font-semibold">{r.filter_flow_m3h.toFixed(2)} m3/h</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Filter area (derived)</span>{' '}
                <span className="text-cyan-200 font-semibold">{r.filter_area_m2.toFixed(3)} m2</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Units</span>{' '}
                <span className="text-slate-100 font-semibold">{r.filter_units_duty} duty + {r.filter_units_standby} standby</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Backwash flow</span>{' '}
                <span className="text-slate-100 font-semibold">{r.filter_backwash_m3h.toFixed(2)} m3/h</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Inlet v</span>{' '}
                <span className={`font-semibold ${r.filter_in_vel >= 1 && r.filter_in_vel <= 3 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.filter_in_vel.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Outlet v</span>{' '}
                <span className={`font-semibold ${r.filter_out_vel >= 1 && r.filter_out_vel <= 3 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.filter_out_vel.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Rec. inlet DN</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_filter_in_DN}</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Rec. outlet DN</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_filter_out_DN}</span>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-slate-400 italic">
              The filter is sized to the PD-off seawater flow (max plant flow). Filter area = inflow / filtration rate; inlet
              and outlet pipe sizes follow the same 2 m/s seawater design velocity used elsewhere. 1 duty + 1 standby matches
              the system redundancy preset; backwash flow envelope is set at 4 % of forward flow.
            </p>
          </Section>

          <Section title="2. Electrolysis cell stack" icon={<Layers className="w-4 h-4" />}
            action={<RecommendBtn onClick={() => setP(prev => ({ ...prev, ...recommendStack(prev) }))} />}>
            <div className="grid grid-cols-2 gap-3">
              <SelField label="Cell topology" value={p.cellTech} options={[
                { value: 'bipolar',   label: 'Bipolar (cells in series)' },
                { value: 'monopolar', label: 'Monopolar (parallel)' },
              ]} onChange={v => setP({ ...p, cellTech: v })} />
              <NumField label="Current density"   suffix="kA/m^2" value={p.currentDensity_kAm2}    min={1.0} max={6.0} step={0.1} onChange={v => setP({ ...p, currentDensity_kAm2: v })} help="2 - 4 kA/m^2 envelope" />
              <NumField label="Cell voltage"      suffix="V"      value={p.cellVoltage_V}          min={2.5} max={5.5} step={0.05} onChange={v => setP({ ...p, cellVoltage_V: v })} help="MMO Pt/Ir typ. 3.5 - 4.5 V" />
              <NumField label="Current efficiency" suffix="%"     value={p.currentEfficiency_pc}   min={70}  max={100} step={1}  onChange={v => setP({ ...p, currentEfficiency_pc: v })} help="Single-pass seawater 85 - 95 %" />
              <NumField label="Cells / module"                    value={p.cells_per_module}       min={1}   max={200} step={1}  onChange={v => setP({ ...p, cells_per_module: v })} />
              <NumField label="Modules (duty)"                    value={p.modules_installed}      min={1}   max={20}  step={1}  onChange={v => setP({ ...p, modules_installed: v })} />
              <NumField label="Modules (standby)"                 value={p.modules_standby}        min={0}   max={10}  step={1}  onChange={v => setP({ ...p, modules_standby: v })} />
              <NumField label="Brine velocity"      suffix="m/s"  value={p.brineVelocity_ms}       min={0.1} max={1.5} step={0.05} onChange={v => setP({ ...p, brineVelocity_ms: v })} />
              <div className="rounded-md bg-cyan-900/15 border border-cyan-800/60 px-2 py-2 text-[11px] flex flex-col justify-center">
                <span className="text-slate-400">Brine flow / module (derived)</span>
                <span className="text-cyan-200 font-semibold">{r.brineFlow_per_module_m3h.toFixed(2)} m3/h</span>
                <span className="text-[9.5px] text-slate-500">= electrolyser inlet / modules</span>
              </div>
            </div>
          </Section>

          <Section title="2b. Cell operating conditions (nameplate)" icon={<Settings className="w-4 h-4" />}
            action={<RecommendBtn onClick={() => setP(prev => ({ ...prev, ...recommendStack(prev) }))} />}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <NumField label="Rated DC current (nameplate)" suffix="A" value={p.cell_ratedDC_current_A} min={50} max={20000} step={50} onChange={v => setP({ ...p, cell_ratedDC_current_A: v })} help="Calculated by default from Faraday + demand; editable." />
                <div className="mt-1 flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Calculated (Faraday + 15 % margin):{' '}
                    <span className="text-cyan-300 font-semibold">{r.rated_current_calc_A.toFixed(0)} A</span>
                  </span>
                  <button onClick={() => setP({ ...p, cell_ratedDC_current_A: r.rated_current_calc_A })}
                    className="px-1.5 py-0.5 rounded bg-cyan-700/30 hover:bg-cyan-700/50 border border-cyan-700 text-cyan-200 text-[10px]">
                    use calc
                  </button>
                </div>
              </div>
              <NumField label="Rated DC voltage" suffix="V" value={p.cell_ratedDC_voltage_V} min={20} max={500} step={1} onChange={v => setP({ ...p, cell_ratedDC_voltage_V: v })} help="Module nameplate stack voltage (default 61 V)" />
              <div>
                <NumField label="Rated capacity" suffix="kg Cl2/h" value={p.cell_ratedCapacity_kgCl2h} min={0.1} max={200} step={0.1} onChange={v => setP({ ...p, cell_ratedCapacity_kgCl2h: v })} help="Per-module nameplate Cl2 yield" />
                <div className="mt-1 flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Calculated at I_rated:{' '}
                    <span className="text-cyan-300 font-semibold">{r.rated_capacity_calc_kgCl2h.toFixed(2)} kg/h</span>
                  </span>
                  <button onClick={() => setP({ ...p, cell_ratedCapacity_kgCl2h: parseFloat(r.rated_capacity_calc_kgCl2h.toFixed(2)) })}
                    className="px-1.5 py-0.5 rounded bg-cyan-700/30 hover:bg-cyan-700/50 border border-cyan-700 text-cyan-200 text-[10px]">
                    use calc
                  </button>
                </div>
              </div>
              <NumField label="Operating pressure"   suffix="barg"        value={p.cell_operatingPressure_barg} min={0.1} max={6}    step={0.1} onChange={v => setP({ ...p, cell_operatingPressure_barg: v })} help="0.5 - 3 barg envelope" />
              <SelField label="Inlet connection (standard)"               value={String(p.cell_inlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label}  (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={(v) => setP({ ...p, cell_inlet_DN: parseInt(v, 10) || 80 })} />
              <SelField label="Outlet connection (standard)"              value={String(p.cell_outlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label}  (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={(v) => setP({ ...p, cell_outlet_DN: parseInt(v, 10) || 80 })} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Inlet v</span>{' '}
                <span className={`font-semibold ${r.inlet_vel_ms >= 0.4 && r.inlet_vel_ms <= 1.5 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.inlet_vel_ms.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Outlet v</span>{' '}
                <span className={`font-semibold ${r.outlet_vel_ms >= 0.3 && r.outlet_vel_ms <= 1.2 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.outlet_vel_ms.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Recommended inlet</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_inlet_DN}</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Recommended outlet</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_outlet_DN}</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Current util.</span>{' '}
                <span className={`font-semibold ${r.cell_current_util_pc >= 60 && r.cell_current_util_pc <= 105 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.cell_current_util_pc.toFixed(0)} % of rated</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Voltage util.</span>{' '}
                <span className={`font-semibold ${r.cell_voltage_util_pc >= 70 && r.cell_voltage_util_pc <= 105 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.cell_voltage_util_pc.toFixed(0)} % of rated</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Capacity util.</span>{' '}
                <span className={`font-semibold ${r.cell_capacity_util_pc >= 50 && r.cell_capacity_util_pc <= 105 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.cell_capacity_util_pc.toFixed(0)} % of rated</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">P_op band</span>{' '}
                <span className={`font-semibold ${r.pressure_in_band ? 'text-emerald-300' : 'text-rose-300'}`}>{p.cell_operatingPressure_barg.toFixed(2)} barg</span>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-slate-400 italic">
              Inlet / outlet selections are restricted to standard DN sizes used by electrochlorination cell manufacturers
              (De Nora, Severn Trent, Evoqua, Hidrostal); inside diameters follow BS EN ISO 15493 Sch 80 PVDF (BS EN 1092-1
              / ASME B16.5 flange faces). Velocities are computed from the per-module brine flow on the actual pipe ID.
            </p>
          </Section>

          <Section title="3. Hypochlorite dosing pumps" icon={<Gauge className="w-4 h-4" />}
            action={<RecommendBtn onClick={() => setP(prev => ({ ...prev, ...recommendDosing(prev) }))} />}>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Process flow to dose" suffix="m³/h"   value={p.dosing_process_flow_m3h} min={0} max={20000} step={1} onChange={v => setP({ ...p, dosing_process_flow_m3h: v })} help="Cooling/seawater main protected by the chlorine" />
              <NumField label="Target dose"          suffix="ppm"    value={p.dosing_target_ppm}       min={0} max={20} step={0.1} onChange={v => setP({ ...p, dosing_target_ppm: v })} help="Residual at the dosing point (shock)" />
              <NumField label="Delivered concentration" suffix="mg/L" value={p.dosing_delivered_conc_mgL} min={50} max={2000} step={10} onChange={v => setP({ ...p, dosing_delivered_conc_mgL: v })} help="Available-Cl the pump delivers (product max 1000)" />
              <NumField label="Dosing pumps (duty)"                  value={p.dosingPumps_duty}        min={1}  max={6} step={1}  onChange={v => setP({ ...p, dosingPumps_duty: v })} />
              <NumField label="Dosing pumps (standby)"               value={p.dosingPumps_standby}     min={0}  max={6} step={1}  onChange={v => setP({ ...p, dosingPumps_standby: v })} />
              <NumField label="Pump capacity each"  suffix="L/h"     value={p.dosingPump_capacity_Lh_each} min={0} max={5000} step={1} onChange={v => setP({ ...p, dosingPump_capacity_Lh_each: v })} help="0 = auto-derive from demand" />
              <NumField label="Pump discharge head" suffix="bar"     value={p.dosingPumpHead_bar}     min={0.5} max={20} step={0.1} onChange={v => setP({ ...p, dosingPumpHead_bar: v })} help="To overcome injection-quill back-pressure" />
              <NumField label="System static pressure" suffix="mbar" value={p.dosingPump_staticPressure_mbar} min={0} max={5000} step={10} onChange={v => setP({ ...p, dosingPump_staticPressure_mbar: v })} help="Static elevation + line losses" />
              <NumField label="Storage residence"   suffix="h"       value={p.storageTank_h}          min={0.5} max={8}  step={0.5} onChange={v => setP({ ...p, storageTank_h: v })} help="NaOCl decay limits to <= 4 h" />
              <SelField label="Inlet (standard)"                     value={String(p.dosingPump_inlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, dosingPump_inlet_DN: parseInt(v, 10) || 25 })} />
              <SelField label="Outlet (standard)"                    value={String(p.dosingPump_outlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, dosingPump_outlet_DN: parseInt(v, 10) || 25 })} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Demand</span>{' '}
                <span className="text-cyan-200 font-semibold">{r.dosingPumpFlow_total_demand_Lh.toFixed(0)} L/h</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Installed</span>{' '}
                <span className="text-slate-100 font-semibold">{r.dosingPumpFlow_total_Lh.toFixed(0)} L/h</span>
              </div>
              <div className={`rounded-md border px-2 py-1.5 ${r.dosing_margin_pc >= 0 ? 'bg-emerald-900/20 border-emerald-700' : 'bg-rose-900/20 border-rose-700'}`}>
                <span className="text-slate-400">Margin</span>{' '}
                <span className="text-slate-100 font-semibold">{r.dosing_margin_pc > 0 ? '+' : ''}{r.dosing_margin_pc.toFixed(1)} %</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Rec. inlet / outlet</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_dosing_in_DN} / DN{r.recommended_dosing_out_DN}</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Inlet v</span>{' '}
                <span className={`font-semibold ${r.dosing_in_vel_ms >= 0.3 && r.dosing_in_vel_ms <= 1.0 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.dosing_in_vel_ms.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Outlet v</span>{' '}
                <span className={`font-semibold ${r.dosing_out_vel_ms >= 0.5 && r.dosing_out_vel_ms <= 2.0 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.dosing_out_vel_ms.toFixed(2)} m/s</span>
              </div>
            </div>
          </Section>

          <Section title="4. Hydrogen dilution blowers (DSEAR / IEC 60079)" icon={<Wind className="w-4 h-4" />}
            action={<RecommendBtn onClick={() => setP(prev => ({ ...prev, ...recommendBlower(prev) }))} />}>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Dilution air ratio"     suffix=":1 (air:H2)" value={p.vent_dilution_factor}  min={10} max={200} step={1}    onChange={v => setP({ ...p, vent_dilution_factor: v })} help="Target >= 30 : 1 to stay below 25 % LEL" />
              <NumField label="Blower safety factor"   suffix="x"           value={p.blower_safety_factor}  min={1.0} max={2.0} step={0.05} onChange={v => setP({ ...p, blower_safety_factor: v })} help="Margin over computed (1.25 typical)" />
              <NumField label="Blowers (duty)"                              value={p.blower_duty}           min={1}  max={4}   step={1}    onChange={v => setP({ ...p, blower_duty: v })} />
              <NumField label="Blowers (standby)"                           value={p.blower_standby}        min={0}  max={4}   step={1}    onChange={v => setP({ ...p, blower_standby: v })} />
              <NumField label="Total static pressure"  suffix="mbar"        value={p.blower_static_mbar}    min={10} max={300} step={5}    onChange={v => setP({ ...p, blower_static_mbar: v })} help="Vent-stack + grille + line losses" />
              <NumField label="Purge time"             suffix="min"         value={p.blower_purge_min}      min={2}  max={60}  step={1}    onChange={v => setP({ ...p, blower_purge_min: v })} help="Pre-energise and post-shutdown each" />
              <SelField label="Air intake (standard)"                       value={String(p.blower_inlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, blower_inlet_DN: parseInt(v, 10) || 80 })} />
              <SelField label="Vent discharge (standard)"                   value={String(p.blower_outlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, blower_outlet_DN: parseInt(v, 10) || 100 })} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Intake v</span>{' '}
                <span className={`font-semibold ${r.blower_in_vel_ms <= 15 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.blower_in_vel_ms.toFixed(1)} m/s</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Discharge v</span>{' '}
                <span className={`font-semibold ${r.blower_out_vel_ms <= 12 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.blower_out_vel_ms.toFixed(1)} m/s</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Rec. intake</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_blower_in_DN}</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Rec. discharge</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_blower_out_DN}</span>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-[11px]">
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Recommended flow / blower</span>{' '}
                <span className="text-cyan-200 font-semibold">{r.blowerFlow_Nm3h_each_duty.toFixed(0)} Nm3/h</span>
                <span className="text-slate-500"> = {r.blowerFlow_actual_m3h_each.toFixed(0)} m3/h @ {p.workingTemp_C.toFixed(0)} C</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Run time / day</span>{' '}
                <span className="text-slate-100 font-medium">{r.blower_run_hours_per_day.toFixed(1)} h</span>
                <span className="text-slate-500"> (+ {r.blower_purge_total_min} min purge)</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">H2 generated</span>{' '}
                <span className="text-slate-100 font-medium">{r.H2_Nm3h.toFixed(2)} Nm3/h</span>
              </div>
              <div className={`rounded-md border px-2 py-1.5 ${r.vent_safe ? 'bg-emerald-900/20 border-emerald-700' : 'bg-rose-900/20 border-rose-700'}`}>
                <span className="text-slate-400">Vent [H2]</span>{' '}
                <span className="text-slate-100 font-medium">{r.vent_H2_pc_vv.toFixed(2)} % v/v</span>
              </div>
            </div>
          </Section>

          {/* ─── H2 TANK HEADSPACE TARGET + AUTO-SIZED BLOWER ───────────── */}
          {(() => {
            const H2_gen = r.H2_Nm3h;
            const target = Math.max(0.1, Math.min(3.9, p.h2_target_pct_vv));
            const V = Math.max(0.5, p.h2_tank_volume_m3);
            // Equilibrium dilution: %v/v = H2 / (H2 + Air) × 100
            // Solve for Air: Air = H2 × (100/target − 1)
            const requiredAir_Nm3h = H2_gen * (100 / target - 1);
            const requiredAir_actual = requiredAir_Nm3h * ((273.15 + p.workingTemp_C) / 273.15) * (1013 / p.workingPressure_mbar_atm);
            const ach = requiredAir_actual / V;
            const dilutionRatio = H2_gen > 0 ? requiredAir_Nm3h / H2_gen : 0;
            const recommendedFactor = Math.ceil(dilutionRatio);
            // Clearance time from 4% (LEL) down to target — well-mixed CSTR model: τ = V/Q × ln(C0/Ct)
            const clearanceMin = V / Math.max(0.0001, requiredAir_actual) * Math.log(4 / target) * 60;
            const lelPct = (target / 4) * 100;
            const isUnsafe = target > 1.0 || lelPct > 25;
            const blowerFlowWithSF = requiredAir_Nm3h * p.blower_safety_factor;
            const perBlower = blowerFlowWithSF / Math.max(1, p.blower_duty);
            const applyToBlower = () => setP(prev => ({ ...prev, vent_dilution_factor: Math.max(10, Math.min(200, recommendedFactor)) }));
            return (
          <Section title="4a. H₂ tank headspace target → auto-sized blower" icon={<Wind className="w-4 h-4" />}
            action={
              <button onClick={applyToBlower}
                className="px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/40 text-[11px] font-bold text-cyan-300 hover:bg-cyan-500/25 transition-colors">
                ⇣ Apply to dilution ratio
              </button>
            }>
            <div className="grid grid-cols-2 gap-3">
              <NumField
                label="Max H₂ in tank headspace"
                suffix="% v/v"
                value={p.h2_target_pct_vv}
                min={0.1} max={3.9} step={0.05}
                onChange={v => setP({ ...p, h2_target_pct_vv: v })}
                help="LEL of H₂ in air = 4 % v/v. Industry standard: keep ≤ 1 % (25 % LEL) per IEC 60079 / DSEAR / NFPA 820."
              />
              <NumField
                label="Tank / enclosure headspace volume"
                suffix="m³"
                value={p.h2_tank_volume_m3}
                min={0.5} max={100} step={0.5}
                onChange={v => setP({ ...p, h2_tank_volume_m3: v })}
                help="Free vapor space above hypochlorite / brine in the storage / degassing tank that must be ventilated."
              />
            </div>

            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Required dilution air</span>{' '}
                <span className="text-cyan-200 font-semibold">{requiredAir_Nm3h.toFixed(1)} Nm³/h</span>
                <div className="text-[10px] text-slate-500">{requiredAir_actual.toFixed(0)} m³/h @ {p.workingTemp_C.toFixed(0)} °C</div>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Dilution ratio</span>{' '}
                <span className="text-slate-100 font-semibold">{dilutionRatio.toFixed(0)} : 1</span>
                <div className="text-[10px] text-slate-500">air : H₂ for target</div>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">ACH</span>{' '}
                <span className={`font-semibold ${ach >= 12 ? 'text-emerald-300' : ach >= 6 ? 'text-amber-300' : 'text-rose-300'}`}>{ach.toFixed(0)} /h</span>
                <div className="text-[10px] text-slate-500">≥12 recommended for H₂ spaces</div>
              </div>
              <div className={`rounded-md border px-2 py-1.5 ${isUnsafe ? 'bg-rose-900/20 border-rose-700' : 'bg-emerald-900/20 border-emerald-700'}`}>
                <span className="text-slate-400">LEL position</span>{' '}
                <span className={`font-semibold ${isUnsafe ? 'text-rose-300' : 'text-emerald-300'}`}>{lelPct.toFixed(0)} % LEL</span>
                <div className="text-[10px] text-slate-500">{isUnsafe ? '⚠ above 25 % LEL' : '✓ within DSEAR / IEC 60079'}</div>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Clearance time</span>{' '}
                <span className="text-slate-100 font-medium">{clearanceMin.toFixed(1)} min</span>
                <div className="text-[10px] text-slate-500">4 % → target after upset</div>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Air flow × safety factor</span>{' '}
                <span className="text-slate-100 font-medium">{blowerFlowWithSF.toFixed(0)} Nm³/h</span>
                <div className="text-[10px] text-slate-500">× {p.blower_safety_factor.toFixed(2)} margin</div>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Per blower</span>{' '}
                <span className="text-cyan-200 font-semibold">{perBlower.toFixed(0)} Nm³/h</span>
                <div className="text-[10px] text-slate-500">{p.blower_duty} duty + {p.blower_standby} standby</div>
              </div>
            </div>

            {/* ─── Live SVG: tank + headspace H₂ + dilution blower ──────────── */}
            <div className="mt-4 rb-inner-card p-3">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2">Live diagram — H₂ headspace ventilation</p>
              <svg viewBox="0 0 880 360" className="w-full h-auto">
                <defs>
                  <linearGradient id="hypoLiq" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.45" />
                    <stop offset="100%" stopColor="#0e7490" stopOpacity="0.95" />
                  </linearGradient>
                  <linearGradient id="h2Gas" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fbbf24" stopOpacity={Math.min(0.85, 0.15 + target * 0.18)} />
                    <stop offset="100%" stopColor="#fbbf24" stopOpacity={Math.min(0.95, 0.30 + target * 0.18)} />
                  </linearGradient>
                </defs>

                <text x="440" y="22" fill="#cbd5e1" fontSize="14" textAnchor="middle" fontWeight="bold" letterSpacing="2">
                  HYDROGEN VENTILATION — TANK + BLOWER · LIVE
                </text>

                {/* Tank — half cross-section */}
                <g>
                  {/* Tank outline */}
                  <rect x="80" y="60" width="260" height="240" rx="14" fill="none" stroke="#64748b" strokeWidth="2.5" />
                  {/* Hypochlorite liquid fill (bottom 55% of tank) */}
                  <rect x="82" y="190" width="256" height="108" rx="10" fill="url(#hypoLiq)" />
                  {/* Wave on liquid surface */}
                  <path d="M 82 192 Q 145 186 210 192 T 338 192 L 338 198 L 82 198 Z" fill="#22d3ee" opacity="0.55" />
                  {/* Headspace fill — colour scales with target % */}
                  <rect x="82" y="62" width="256" height="128" rx="10" fill="url(#h2Gas)" />
                  {/* Bubbles in headspace representing accumulating H₂ */}
                  {Array.from({ length: 14 }).map((_, i) => {
                    const cx = 95 + (i * 18) % 240;
                    const cy = 110 + ((i * 41) % 60);
                    const r = 2.2 + (i % 4) * 0.7;
                    return (
                      <circle key={i} cx={cx} cy={cy} r={r} fill="#fbbf24" opacity={0.5 + (i % 3) * 0.15}>
                        <animate attributeName="cy" values={`${cy};${cy - 8};${cy}`} dur={`${2.2 + (i % 5) * 0.4}s`} repeatCount="indefinite" />
                        <animate attributeName="opacity" values={`${0.4 + (i % 3) * 0.15};${0.8};${0.4 + (i % 3) * 0.15}`} dur={`${2.2 + (i % 5) * 0.4}s`} repeatCount="indefinite" />
                      </circle>
                    );
                  })}

                  {/* Tank labels */}
                  <text x="210" y="48" fill="#94a3b8" fontSize="13" textAnchor="middle" fontWeight="bold">DEGASSING TANK</text>
                  <text x="210" y="58" fill="#64748b" fontSize="11" textAnchor="middle">V = {V.toFixed(1)} m³ headspace</text>
                  {/* Liquid label */}
                  <text x="210" y="260" fill="#cbd5e1" fontSize="12" textAnchor="middle" fontWeight="bold">Hypochlorite product</text>
                  <text x="210" y="276" fill="#94a3b8" fontSize="11" textAnchor="middle">(NaOCl + brine)</text>
                  {/* H2 % label */}
                  <text x="210" y="125" fill="#0f172a" fontSize="22" textAnchor="middle" fontWeight="bold">{target.toFixed(2)} % v/v</text>
                  <text x="210" y="143" fill="#0f172a" fontSize="12" textAnchor="middle" fontWeight="bold">H₂ equilibrium target</text>
                  {/* Liquid line markers */}
                  <line x1="80" y1="190" x2="76" y2="190" stroke="#64748b" strokeWidth="2" />
                  <text x="73" y="194" fill="#94a3b8" fontSize="10" textAnchor="end">liq.</text>
                </g>

                {/* H₂ generation flow into tank */}
                <g>
                  <path d="M 80 230 L 40 230 L 40 280" fill="none" stroke="#fbbf24" strokeWidth="2.5" strokeDasharray="6 4" />
                  <polygon points="36,278 40,288 44,278" fill="#fbbf24" />
                  <text x="38" y="305" fill="#fbbf24" fontSize="12" textAnchor="end" fontWeight="bold">H₂ gen</text>
                  <text x="38" y="318" fill="#94a3b8" fontSize="11" textAnchor="end">{H2_gen.toFixed(2)} Nm³/h</text>
                </g>

                {/* Vent pipe up + over to blower */}
                <g>
                  <line x1="340" y1="80" x2="430" y2="80" stroke="#94a3b8" strokeWidth="3" />
                  <line x1="430" y1="80" x2="430" y2="160" stroke="#94a3b8" strokeWidth="3" />
                  <text x="385" y="72" fill="#94a3b8" fontSize="11" textAnchor="middle">vent to blower</text>
                </g>

                {/* Blower */}
                <g transform="translate(470, 150)">
                  <rect x="-8" y="-8" width="120" height="80" rx="8" fill="#0f172a" stroke="#06b6d4" strokeWidth="2.5" />
                  {/* Fan blades */}
                  <g transform="translate(45, 32)">
                    <circle r="22" fill="none" stroke="#06b6d4" strokeWidth="1.5" />
                    <g>
                      <line x1="0" y1="-18" x2="0" y2="18" stroke="#06b6d4" strokeWidth="3" strokeLinecap="round" />
                      <line x1="-18" y1="0" x2="18" y2="0" stroke="#06b6d4" strokeWidth="3" strokeLinecap="round" />
                      <line x1="-13" y1="-13" x2="13" y2="13" stroke="#06b6d4" strokeWidth="3" strokeLinecap="round" />
                      <line x1="-13" y1="13" x2="13" y2="-13" stroke="#06b6d4" strokeWidth="3" strokeLinecap="round" />
                      <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur={`${Math.max(0.4, 2 - target).toFixed(2)}s`} repeatCount="indefinite" />
                    </g>
                    <circle r="4" fill="#06b6d4" />
                  </g>
                  <text x="52" y="89" fill="#06b6d4" fontSize="12" textAnchor="middle" fontWeight="bold">DILUTION BLOWER</text>
                  <text x="52" y="102" fill="#94a3b8" fontSize="10" textAnchor="middle">{perBlower.toFixed(0)} Nm³/h ea · {p.blower_duty}D+{p.blower_standby}S</text>
                </g>

                {/* Air intake into blower */}
                <g>
                  <line x1="600" y1="190" x2="700" y2="190" stroke="#22d3ee" strokeWidth="3" strokeDasharray="8 5" />
                  <polygon points="600,186 600,194 588,190" fill="#22d3ee" />
                  <text x="650" y="180" fill="#22d3ee" fontSize="12" textAnchor="middle" fontWeight="bold">Air intake</text>
                  <text x="650" y="208" fill="#94a3b8" fontSize="11" textAnchor="middle">{requiredAir_Nm3h.toFixed(0)} Nm³/h dilution</text>
                  {/* Intake grille */}
                  <g transform="translate(700, 175)">
                    <rect x="0" y="0" width="14" height="30" fill="#0f172a" stroke="#22d3ee" strokeWidth="1.5" />
                    {Array.from({ length: 5 }).map((_, i) => (
                      <line key={i} x1="0" y1={4 + i * 6} x2="14" y2={4 + i * 6} stroke="#22d3ee" strokeWidth="0.8" />
                    ))}
                  </g>
                </g>

                {/* Vent stack discharge */}
                <g>
                  <line x1="535" y1="142" x2="535" y2="80" stroke="#10b981" strokeWidth="3" />
                  <line x1="535" y1="80" x2="600" y2="80" stroke="#10b981" strokeWidth="3" />
                  <text x="568" y="72" fill="#10b981" fontSize="11" textAnchor="middle">diluted vent</text>
                  {/* Stack tip */}
                  <polygon points="595,75 605,80 595,85" fill="#10b981" />
                  <text x="630" y="84" fill="#10b981" fontSize="11" fontWeight="bold">to roof</text>
                </g>

                {/* LEL gauge bar */}
                <g transform="translate(770, 60)">
                  <text x="40" y="14" fill="#cbd5e1" fontSize="12" textAnchor="middle" fontWeight="bold">LEL gauge</text>
                  <text x="40" y="28" fill="#94a3b8" fontSize="10" textAnchor="middle">0 → 100 % LEL</text>
                  {/* Vertical bar */}
                  <rect x="20" y="40" width="40" height="200" rx="4" fill="#0f172a" stroke="#1e293b" />
                  {/* Green zone 0-25% LEL */}
                  <rect x="22" y="190" width="36" height="48" fill="#10b981" opacity="0.7" />
                  {/* Amber 25-50 */}
                  <rect x="22" y="140" width="36" height="50" fill="#f59e0b" opacity="0.75" />
                  {/* Red 50-100 */}
                  <rect x="22" y="42" width="36" height="98" fill="#ef4444" opacity="0.85" />
                  {/* Tick marks */}
                  <text x="65" y="244" fill="#94a3b8" fontSize="10">0</text>
                  <text x="65" y="194" fill="#94a3b8" fontSize="10">25 ← safe</text>
                  <text x="65" y="144" fill="#94a3b8" fontSize="10">50</text>
                  <text x="65" y="44"  fill="#94a3b8" fontSize="10">100</text>
                  {/* Pointer at current LEL */}
                  {(() => {
                    const lelClamped = Math.min(100, Math.max(0, lelPct));
                    const py = 240 - (lelClamped / 100) * 200;
                    return (
                      <g>
                        <line x1="15" y1={py} x2="65" y2={py} stroke="#f8fafc" strokeWidth="2" />
                        <polygon points={`12,${py - 4} 12,${py + 4} 20,${py}`} fill="#f8fafc" />
                        <text x="40" y={py - 6} fill="#f8fafc" fontSize="11" textAnchor="middle" fontWeight="bold">{lelPct.toFixed(0)}%</text>
                      </g>
                    );
                  })()}
                </g>

                {/* Bottom data strip */}
                <g transform="translate(80, 320)">
                  <rect x="0" y="0" width="720" height="32" rx="6" fill="#0f172a" stroke="#1e293b" />
                  <text x="12" y="13" fill="#64748b" fontSize="10">H₂ in</text>
                  <text x="12" y="26" fill="#fbbf24" fontSize="12" fontWeight="bold">{H2_gen.toFixed(2)} Nm³/h</text>
                  <text x="120" y="13" fill="#64748b" fontSize="10">Dilution air</text>
                  <text x="120" y="26" fill="#22d3ee" fontSize="12" fontWeight="bold">{requiredAir_Nm3h.toFixed(0)} Nm³/h</text>
                  <text x="240" y="13" fill="#64748b" fontSize="10">Ratio</text>
                  <text x="240" y="26" fill="#cbd5e1" fontSize="12" fontWeight="bold">{dilutionRatio.toFixed(0)} : 1</text>
                  <text x="320" y="13" fill="#64748b" fontSize="10">ACH</text>
                  <text x="320" y="26" fill={ach >= 12 ? '#10b981' : '#f59e0b'} fontSize="12" fontWeight="bold">{ach.toFixed(0)} /h</text>
                  <text x="380" y="13" fill="#64748b" fontSize="10">Equilibrium [H₂]</text>
                  <text x="380" y="26" fill={lelPct > 25 ? '#ef4444' : '#10b981'} fontSize="12" fontWeight="bold">{target.toFixed(2)} % v/v</text>
                  <text x="490" y="13" fill="#64748b" fontSize="10">LEL position</text>
                  <text x="490" y="26" fill={lelPct > 25 ? '#ef4444' : '#10b981'} fontSize="12" fontWeight="bold">{lelPct.toFixed(0)} %</text>
                  <text x="580" y="13" fill="#64748b" fontSize="10">Clearance τ</text>
                  <text x="580" y="26" fill="#cbd5e1" fontSize="12" fontWeight="bold">{clearanceMin.toFixed(1)} min</text>
                </g>
              </svg>
            </div>

            <p className="mt-3 text-[11px] text-slate-400 italic leading-relaxed">
              Mass balance: at well-mixed equilibrium <code className="text-slate-300">[H₂]% = Q_H₂ / (Q_H₂ + Q_air) × 100</code> → required air <code className="text-slate-300">Q_air = Q_H₂ × (100/target − 1)</code>.
              Clearance time uses the CSTR exponential model <code className="text-slate-300">τ = V/Q · ln(C₀/Cₜ)</code>.
              Hydrogen-air LEL = 4 % v/v (IEC 60079-20-1, NFPA 69). Keep headspace ≤ 1 % v/v (25 % LEL) under all upset scenarios; alarm at 10 % LEL (0.4 %), trip rectifier and ramp blower at 25 % LEL.
              The button above syncs the dilution ratio used by the main blower sizing block below. Fan speed in the diagram scales with the target — the lower the target, the faster the blower spins.
            </p>
          </Section>
            );
          })()}

          <Section title="4b. Working conditions (applied to all devices)" icon={<Thermometer className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Working temperature"   suffix="deg C" value={p.workingTemp_C}            min={-10} max={55}  step={0.5} onChange={v => setP({ ...p, workingTemp_C: v })} help="Ambient / electrical-room T - sets gas density correction for blowers and motor de-rate envelope" />
              <NumField label="Atmospheric pressure"  suffix="mbar"  value={p.workingPressure_mbar_atm} min={700} max={1100} step={1}  onChange={v => setP({ ...p, workingPressure_mbar_atm: v })} help="Site barometric pressure - 1013 mbar at sea level" />
            </div>
            <p className="mt-2 text-[11px] text-slate-400 italic">
              These conditions feed gas-volume corrections (Nm3 -&gt; m3) for blower sizing and apply uniformly to all pumps and panels for motor de-rate checks.
            </p>
          </Section>

          <Section title="5. Acid wash (electrode CIP)" icon={<FlaskConical className="w-4 h-4" />}
            action={<RecommendBtn onClick={() => setP(prev => ({ ...prev, ...recommendAcid(prev) }))} />}>
            <div className="grid grid-cols-2 gap-3">
              <SelField label="Acid type" value={p.acidType} options={[
                { value: 'HCl_15pc',      label: 'HCl 15 % (industry standard)' },
                { value: 'HCl_30pc',      label: 'HCl 30 % (heavy fouling)' },
                { value: 'sulphamic_5pc', label: 'Sulphamic acid 5 % (low corrosivity)' },
              ]} onChange={v => setP({ ...p, acidType: v })} />
              <NumField label="Wash interval"           suffix="days"   value={p.acidWashEvery_d}        min={7}  max={120} step={1}  onChange={v => setP({ ...p, acidWashEvery_d: v })} help="Tightens with hardness > 250 mg/L" />
              <NumField label="Wash duration"           suffix="min"    value={p.acidWashDuration_min}   min={15} max={240} step={5}  onChange={v => setP({ ...p, acidWashDuration_min: v })} />
              <NumField label="Wash strength (working)" suffix="% w/w"  value={p.acidWashConc_target_pc} min={1}  max={15}  step={0.5}onChange={v => setP({ ...p, acidWashConc_target_pc: v })} help="Working solution after dilution" />
              <NumField label="CIP tank volume"         suffix="m3"     value={p.acidWashTank_m3}        min={0.2} max={20} step={0.1} onChange={v => setP({ ...p, acidWashTank_m3: v })} />
              <NumField label="Acid pump head"          suffix="bar"    value={p.acidPumpHead_bar}       min={0.5} max={10} step={0.1} onChange={v => setP({ ...p, acidPumpHead_bar: v })} />
              <NumField label="Acid static pressure"    suffix="mbar"   value={p.acidPump_staticPressure_mbar} min={0} max={5000} step={10} onChange={v => setP({ ...p, acidPump_staticPressure_mbar: v })} help="CIP loop static + line losses" />
              <NumField label="Acid pumps (duty)"                       value={p.acidPumps_duty}        min={1}  max={4}  step={1}  onChange={v => setP({ ...p, acidPumps_duty: v })} />
              <NumField label="Acid pumps (standby)"                    value={p.acidPumps_standby}     min={0}  max={4}  step={1}  onChange={v => setP({ ...p, acidPumps_standby: v })} />
              <SelField label="Acid pump inlet (standard)"              value={String(p.acidPump_inlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, acidPump_inlet_DN: parseInt(v, 10) || 25 })} />
              <SelField label="Acid pump outlet (standard)"             value={String(p.acidPump_outlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, acidPump_outlet_DN: parseInt(v, 10) || 25 })} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Inlet v</span>{' '}
                <span className={`font-semibold ${r.acid_in_vel_ms >= 0.3 && r.acid_in_vel_ms <= 1.0 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.acid_in_vel_ms.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Outlet v</span>{' '}
                <span className={`font-semibold ${r.acid_out_vel_ms >= 0.5 && r.acid_out_vel_ms <= 2.0 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.acid_out_vel_ms.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Rec. inlet</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_acid_in_DN}</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Rec. outlet</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_acid_out_DN}</span>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-slate-400 italic">
              Selected: <span className="text-cyan-300">{ACID_PROPS[p.acidType].name}</span> - {ACID_PROPS[p.acidType].useCase}.
            </p>
          </Section>

          <Section title="6. Transformer / rectifier (datasheet)" icon={<Zap className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="AC primary voltage" suffix="V"   value={p.txr_AC_primary_V}  min={200} max={1200} step={5} onChange={v => setP({ ...p, txr_AC_primary_V: v })} help="Common: 400 / 415 / 480 / 690 V" />
              <NumField label="Overall efficiency" suffix="%"   value={p.txr_efficiency_pc} min={70}  max={99}   step={0.5} onChange={v => setP({ ...p, txr_efficiency_pc: v })} help="Transformer x rectifier product" />
              <SelField label="Redundancy"                       value={p.txr_redundancy} options={[
                { value: 'N',   label: 'N (no standby rectifier)' },
                { value: 'N+1', label: 'N+1 (one standby rectifier)' },
              ]} onChange={v => setP({ ...p, txr_redundancy: v })} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">DC power total</span>{' '}
                <span className="text-slate-100 font-semibold">{r.power_total_DC_kW.toFixed(2)} kW</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">AC power total</span>{' '}
                <span className="text-cyan-200 font-semibold">{r.power_total_AC_kW.toFixed(2)} kW</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">AC primary current</span>{' '}
                <span className="text-slate-100 font-semibold">{(r.power_total_AC_kW * 1000 / (p.txr_AC_primary_V * Math.sqrt(3))).toFixed(0)} A (3-ph)</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Eff. (overall)</span>{' '}
                <span className="text-slate-100 font-semibold">{(r.txr_eff * 100).toFixed(1)} %</span>
              </div>
            </div>
          </Section>

          <Section title="7. Hypochlorite storage + H2 degassing tank" icon={<Beaker className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Tank volume"                  suffix="m3"     value={p.storage_tank_volume_m3}          min={0.5} max={50}  step={0.1} onChange={v => setP({ ...p, storage_tank_volume_m3: v })} />
              <NumField label="Degassing residence required" suffix="min"    value={p.storage_degassing_residence_min} min={5}   max={120} step={1}   onChange={v => setP({ ...p, storage_degassing_residence_min: v })} help="H2 disengagement target" />
              <SelField label="Material"                                      value={p.storage_material} options={[
                { value: 'GRP',  label: 'GRP (datasheet std.)' },
                { value: 'HDPE', label: 'HDPE' },
                { value: 'PP',   label: 'Polypropylene' },
                { value: 'PVDF', label: 'PVDF' },
              ]} onChange={v => setP({ ...p, storage_material: v })} />
              <NumField label="Freeboard"                    suffix="%"      value={p.storage_freeboard_pc}            min={5}   max={40}  step={1}   onChange={v => setP({ ...p, storage_freeboard_pc: v })} help="Gas space above design level" />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className={`rounded-md border px-2 py-1.5 ${r.storage_degassing_ok ? 'bg-emerald-900/20 border-emerald-700' : 'bg-rose-900/20 border-rose-700'}`}>
                <span className="text-slate-400">Actual residence</span>{' '}
                <span className="text-slate-100 font-semibold">{r.storage_actual_residence_min.toFixed(1)} min</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Gas space</span>{' '}
                <span className="text-slate-100 font-semibold">{r.storage_gas_volume_m3.toFixed(2)} m3</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Material</span>{' '}
                <span className="text-slate-100 font-semibold">{p.storage_material}</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Working volume</span>{' '}
                <span className="text-slate-100 font-semibold">{(p.storage_tank_volume_m3 * (1 - p.storage_freeboard_pc / 100)).toFixed(2)} m3</span>
              </div>
            </div>
          </Section>

          <Section title="8. Eductor (NaOCl injection)" icon={<Activity className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Motive seawater flow"  suffix="m3/h" value={p.eductor_motive_m3h}    min={0.5} max={200} step={0.1} onChange={v => setP({ ...p, eductor_motive_m3h: v })} />
              <NumField label="Suction (NaOCl) flow"  suffix="L/h"  value={p.eductor_suction_Lh}    min={10}  max={20000} step={10} onChange={v => setP({ ...p, eductor_suction_Lh: v })} />
              <NumField label="Motive pressure"       suffix="bar"  value={p.eductor_motive_pressure_bar} min={1} max={10} step={0.1} onChange={v => setP({ ...p, eductor_motive_pressure_bar: v })} help="Drives the venturi suction" />
              <SelField label="Motive (standard)"                                  value={String(p.eductor_motive_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, eductor_motive_DN: parseInt(v, 10) || 50 })} />
              <SelField label="Suction (standard)"                                 value={String(p.eductor_suction_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, eductor_suction_DN: parseInt(v, 10) || 25 })} />
              <SelField label="Discharge (standard)"                               value={String(p.eductor_discharge_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, eductor_discharge_DN: parseInt(v, 10) || 65 })} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Motive v</span>{' '}
                <span className={`font-semibold ${r.eductor_motive_vel >= 1 && r.eductor_motive_vel <= 4 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.eductor_motive_vel.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Suction v</span>{' '}
                <span className={`font-semibold ${r.eductor_suction_vel >= 0.3 && r.eductor_suction_vel <= 1.5 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.eductor_suction_vel.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Discharge v</span>{' '}
                <span className={`font-semibold ${r.eductor_discharge_vel >= 1 && r.eductor_discharge_vel <= 3 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.eductor_discharge_vel.toFixed(2)} m/s</span>
              </div>
              <div className={`rounded-md border px-2 py-1.5 ${r.eductor_ratio_ok ? 'bg-emerald-900/20 border-emerald-700' : 'bg-amber-900/20 border-amber-700'}`}>
                <span className="text-slate-400">Motive / suction</span>{' '}
                <span className="text-slate-100 font-semibold">{r.eductor_ratio.toFixed(1)} : 1</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Discharge flow</span>{' '}
                <span className="text-slate-100 font-semibold">{r.eductor_discharge_m3h.toFixed(2)} m3/h</span>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-slate-400 italic">
              Eductor draws hypochlorite into the motive seawater stream by venturi action. Envelope: motive 1 - 4 m/s, suction 0.3 - 1.5 m/s,
              discharge 1 - 3 m/s, motive/suction ratio 3 : 1 - 10 : 1. Wetted materials: PVDF / GRP / Hastelloy C-276.
            </p>
          </Section>

          <button
            onClick={() => setP(DEFAULTS)}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-200 text-sm py-2"
          >
            <RotateCcw className="w-4 h-4" /> Reset to datasheet defaults
          </button>
        </div>

        {/* OUTPUTS COLUMN */}
        <div className="space-y-5">
          <Section title="A. Sizing summary - cells, current, power" icon={<Settings className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <KPI label="Modules running"            value={p.modules_installed}                            unit="(+ standby)" />
              <KPI label="Stack voltage"              value={r.stackVoltage_V.toFixed(0)}                    unit="V" />
              <KPI label="DC current (T/R, per series)" value={r.I_cell_A.toFixed(0)}                        unit="A DC" />
              <KPI label="Total active area"          value={r.cellArea_total_m2.toFixed(2)}                 unit="m^2" />
              <KPI label="DC power total"             value={r.power_total_DC_kW.toFixed(1)}                 unit="kW" />
              <KPI label="AC power total"             value={r.power_total_AC_kW.toFixed(1)}                 unit="kW"
                tone={r.specificEnergy_kWh_per_kgCl2 < 5.5 ? 'good' : 'warn'} />
              <KPI label="Spec. energy"               value={r.specificEnergy_kWh_per_kgCl2.toFixed(2)}      unit="kWh / kg Cl2" />
              <KPI label="NaOCl product"              value={r.naocl_mgL.toFixed(0)}                         unit="mg/L" />
            </div>
          </Section>

          <Section title="B. Hypochlorite dosing pumps - flow rates & power" icon={<Activity className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <KPI label="Demand (Cl2 -> NaOCl)"       value={r.dosingPumpFlow_total_demand_Lh.toFixed(1)}  unit="L/h" tone="neutral" />
              <KPI label="Installed total"             value={r.dosingPumpFlow_total_Lh.toFixed(1)}         unit="L/h" />
              <KPI label="Per duty pump"               value={r.dosingPumpFlow_per_duty_Lh.toFixed(1)}      unit="L/h each" />
              <KPI label="Pump motor (each)"           value={r.dosingPump_kW_each.toFixed(3)}              unit="kW" tone="good" />
              <KPI label="Total dosing power"          value={(r.dosingPump_kW_each * p.dosingPumps_duty).toFixed(3)} unit="kW" tone="good" />
              <KPI label="Storage tank"                value={r.storageTank_m3.toFixed(2)}                  unit="m^3" />
              <KPI label="Pumps installed"             value={`${p.dosingPumps_duty} + ${p.dosingPumps_standby}`} unit="duty + standby" />
              <KPI label="Discharge head + static"     value={`${p.dosingPumpHead_bar.toFixed(1)} bar + ${p.dosingPump_staticPressure_mbar} mbar`} unit="" />
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              Sized per duty pump with N+1 redundancy. Diaphragm metering pumps (Grundfos DDA / ProMinent gamma / Watson-Marlow Qdos)
              are typical; PVDF or PTFE wetted parts for NaOCl service.
            </p>
          </Section>

          <Section title="C. Acid wash chemistry & dosing pumps" icon={<Beaker className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <KPI label="Wash volume / event"        value={r.wash_volume_per_event_m3.toFixed(2)}          unit="m^3" />
              <KPI label="Active acid / event"        value={r.active_acid_kg_per_event.toFixed(1)}          unit="kg" />
              <KPI label="Concentrate / event"        value={(r.concentrate_m3_per_event * 1000).toFixed(0)} unit="L" />
              <KPI label="Events / year"              value={r.events_per_year.toFixed(1)}                   unit="/ yr" />
              <KPI label="Concentrate / year"         value={r.concentrate_m3_per_year.toFixed(2)}           unit="m^3" />
              <KPI label="Acid mass / year"           value={r.concentrate_kg_per_year.toFixed(0)}           unit="kg" />
              <KPI label="Acid pump flow"             value={r.acidPump_per_duty_Lh.toFixed(0)}              unit="L/h per duty" />
              <KPI label="Acid pump motor (each)"     value={r.acidPump_kW_each.toFixed(3)}                  unit="kW" tone="good" />
              <KPI label="Acid pump total power"      value={(r.acidPump_kW_each * p.acidPumps_duty).toFixed(3)} unit="kW" tone="good" />
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              CIP loop: drain seawater, fill from CIP tank, circulate {p.acidWashDuration_min} min, drain to neutralisation tank,
              rinse with permeate. Materials of construction: GRP / PVDF / PTFE / Hastelloy C-276 for wetted parts.
            </p>
          </Section>

          <Section title="D. Hydrogen safety + blower sizing" icon={<Flame className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <KPI label="H2 produced"                value={r.H2_Nm3h.toFixed(2)}                           unit="Nm3/h" />
              <KPI label="Dilution air required"      value={r.dilutionAir_Nm3h.toFixed(0)}                  unit="Nm3/h" />
              <KPI label="Vent [H2]"                  value={r.vent_H2_pc_vv.toFixed(2)}                     unit="% v/v" tone={r.vent_safe ? 'good' : 'bad'} />
              <KPI label="LEL margin"                 value={(100 - (r.vent_H2_pc_vv / 4) * 100).toFixed(0)} unit="% below LEL" tone={r.vent_safe ? 'good' : 'bad'} />
              <KPI label="Recommended blower flow"    value={r.blowerFlow_Nm3h_each_duty.toFixed(0)}         unit="Nm3/h per duty" tone="good" />
              <KPI label="Actual flow @ working T"    value={r.blowerFlow_actual_m3h_each.toFixed(0)}        unit="m3/h per duty" />
              <KPI label="Blower motor (each)"        value={r.blower_motor_kW_each.toFixed(3)}              unit="kW" tone="good" />
              <KPI label="Blower total power"         value={(r.blower_motor_kW_each * p.blower_duty).toFixed(3)} unit="kW" tone="good" />
              <KPI label="Total static pressure"      value={p.blower_static_mbar.toFixed(0)}                unit="mbar" />
              <KPI label="Blowers installed"          value={`${p.blower_duty} + ${p.blower_standby}`}       unit="duty + standby" />
              <KPI label="Run time / day"             value={r.blower_run_hours_per_day.toFixed(1)}          unit="h/d" />
              <KPI label="Purge before / after"       value={p.blower_purge_min}                             unit="min each" />
              <KPI label="Density correction"         value={r.densityCorr.toFixed(3)}                       unit="Nm3 -> m3" />
            </div>
            <p className="mt-2 text-[11px] text-amber-300">
              H2 LEL = 4 % v/v. Vent stack design must keep concentration &lt;= 25 % LEL (1 % v/v) per IEC 60079 / DSEAR.
              Zone-2 classification within 1.5 m radius of vent termination. Blower interlock with rectifier:
              blower starts {p.blower_purge_min} min BEFORE rectifier energise and runs {p.blower_purge_min} min AFTER de-energise.
            </p>
          </Section>

          <Section title={`E. Engineering validation (${passCount} / ${totalChecks} pass)`} icon={<ShieldCheck className="w-4 h-4" />}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-400 border-b border-slate-700">
                  <tr>
                    <th className="text-left py-1.5">Criterion</th>
                    <th className="text-right py-1.5">Value</th>
                    <th className="text-right py-1.5">Limit</th>
                    <th className="text-center py-1.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map((c, i) => (
                    <tr key={i} className="border-b border-slate-800/50">
                      <td className="py-1 text-slate-200">{c.label}{c.critical && <span className="ml-1 text-rose-400">*</span>}</td>
                      <td className="py-1 text-right text-slate-100 font-medium">{c.value}</td>
                      <td className="py-1 text-right text-slate-400">{c.limit}</td>
                      <td className="py-1 text-center">
                        {c.pass
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" />
                          : <AlertCircle  className="w-4 h-4 text-rose-400 inline" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[10px] text-slate-500">* critical limit (failure indicates redesign required)</p>
          </Section>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Sensitivity - salinity vs specific energy" icon={<Thermometer className="w-4 h-4" />}>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={sweepSal}>
                <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                <XAxis dataKey="salinity" stroke="#94a3b8" tick={{ fontSize: 11 }} label={{ value: 'Salinity (ppt)', position: 'insideBottom', offset: -2, fill: '#94a3b8', fontSize: 11 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} label={{ value: 'kWh / kg Cl2', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155' }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine x={p.salinity_ppt} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: 'design', fill: '#f59e0b', fontSize: 10 }} />
                <Line type="monotone" dataKey="specEnergy" stroke="#22d3ee" strokeWidth={2} dot={false} name="Specific energy" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section title="Sensitivity - temperature vs specific energy" icon={<Thermometer className="w-4 h-4" />}>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={sweepT}>
                <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                <XAxis dataKey="temp" stroke="#94a3b8" tick={{ fontSize: 11 }} label={{ value: 'Temperature (deg C)', position: 'insideBottom', offset: -2, fill: '#94a3b8', fontSize: 11 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} label={{ value: 'kWh / kg Cl2', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155' }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine x={p.temperature_C} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: 'design', fill: '#f59e0b', fontSize: 10 }} />
                <Line type="monotone" dataKey="specEnergy" stroke="#a78bfa" strokeWidth={2} dot={false} name="Specific energy" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>

      <Section title="Engineering basis & references" icon={<Info className="w-4 h-4" />}>
        <div className="space-y-2 text-[12px] text-slate-300 leading-relaxed">
          <p>
            <span className="text-cyan-300 font-semibold">Faraday yield:</span> 1.3245 g Cl2 / Ah at 100 % current efficiency
            (M_Cl2 = 71 g/mol; n = 2 electrons; F = 96 485 C/mol). Practical yield = theoretical x current efficiency
            (typically 0.85 - 0.95 for single-pass seawater on MMO Pt/Ir anodes).
          </p>
          <p>
            <span className="text-cyan-300 font-semibold">Standard seawater:</span> 35 ppt salinity (open ocean), ~ 19 g/L Cl-,
            density ~ 1025 kg/m3 at 20 deg C, conductivity ~ 43 mS/cm at 15 deg C, pH 8.0 - 8.2, alkalinity ~ 120 mg/L as CaCO3.
            Inputs may be reduced for brackish or recirculated brine streams.
          </p>
          <p>
            <span className="text-cyan-300 font-semibold">Cell envelope:</span> 2 - 4 kA/m2 current density, 3.5 - 4.5 V per cell,
            0.4 - 0.8 m/s brine velocity. Product hypochlorite typically 0.4 - 0.8 % w/w available Cl2 from single-pass seawater electrolysis.
          </p>
          <p>
            <span className="text-cyan-300 font-semibold">Hydrogen safety:</span> 1:1 molar H2 : Cl2 yield. Vent design (IEC 60079 / DSEAR)
            requires dilution to &lt;= 25 % of LEL = 1 % v/v in air (LEL_H2 = 4 % v/v). Zone-2 hazardous area within 1.5 m of vent termination.
          </p>
          <p>
            <span className="text-cyan-300 font-semibold">Acid wash:</span> HCl 15 % is the industry-standard CIP for cathodic
            CaCO3 / Mg(OH)2 scale (target wash strength 3 - 10 % w/w; interval 14 - 60 d depending on hardness). Sulphamic acid 5 %
            is a lower-corrosivity alternative for stainless-component compatibility.
          </p>
          <p className="text-slate-500 italic text-[11px]">
            References: Bommaraju &amp; Orosco, "Brine Electrolysis" (ECS); Bennett (1980) "Electrodes for the generation of Cl2 / O2 from seawater";
            De Nora technical bulletins; IEC 60079; DSEAR; HSE INDG370.
          </p>
        </div>
      </Section>
      </>
      )}

      <div className="text-[10px] text-slate-500 text-center pt-2 border-t border-slate-800">
        Reynolds &amp; Bauhm Smart Water - Reservoir Suite V 2.0 - Electrochlorination Sizing Tool
      </div>
    </div>
  );
}
